/*
 * Your Hall  ·  assets/hoh-your-hall.js
 *
 * Soft sign-in plus a personal dashboard inside the Hall of Heroes:
 *   - Sign in via email + 4-digit PIN (set on submission)
 *   - "Your Characters" view: submitter's heroes, sorted by community hearts
 *   - "Heroes You've Witnessed" view: characters they've hearted, sorted
 *     by personal heart count
 *
 * Lives outside sections/hall-of-heroes.liquid so the section file
 * stays under Shopify's 256 KB limit.
 *
 * Coupling to the host page:
 *   window.hohSupabaseClient        Supabase client
 *   window.hohGetAllCharacters()    pool source
 *   window.hohOpenDetail(id)        detail modal opener
 *   window.hohOpenEditForm(id)      edit form opener
 *   window.hohGetRarity(hearts)     rarity tier helper
 *
 * Public API (mounted on window):
 *   hohOpenYourHall()               open the dashboard
 *   hohCloseYourHall()              close it
 *   hohYourHallSignIn()             handle the sign-in submit
 *   (no sign-out by design — sessions persist 30 days)
 *   hohYourHallSwitchView(name)     switch between sub-views
 *   hohYourHallIsSignedIn()         boolean check (used by host)
 *
 * Storage keys:
 *   hoh-your-hall-email             persisted email
 *   hoh-your-hall-token             opaque session token (pin echo + ts)
 */
(function(){
  'use strict';

  var EMAIL_KEY = 'hoh-your-hall-email';
  var TOKEN_KEY = 'hoh-your-hall-token';
  /* Cached display name (player_name from the user's most-recent
     submission). Falls back to the email prefix when the server hasn't
     returned characters yet. Persisted so the first paint after a
     reload still shows the personalized label without a flicker. */
  var NAME_KEY = 'hoh-your-hall-name';
  /* Session lasts 30 days, then re-sign-in. */
  var SESSION_MS = 365 * 24 * 60 * 60 * 1000;   /* R384: a year, sliding (see getStoredToken) */

  function esc(s){
    if(s==null)return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  function getStoredEmail(){ try { return (localStorage.getItem(EMAIL_KEY) || '').trim().toLowerCase(); } catch(e){ return ''; } }
  function setStoredEmail(e){ try { localStorage.setItem(EMAIL_KEY, (e||'').trim().toLowerCase()); } catch(err){} }
  function clearStoredEmail(){ try { localStorage.removeItem(EMAIL_KEY); } catch(e){} }

  function getStoredToken(){
    try {
      var raw = localStorage.getItem(TOKEN_KEY);
      if(!raw) return null;
      var t = JSON.parse(raw);
      if(!t || !t.email || !t.pin || !t.savedAt) return null;
      if(Date.now() - t.savedAt > SESSION_MS){ clearStoredToken(); return null; }
      /* R384 (Jan): "once they log in keep them logged in." The session slides —
         any visit older than a day re-stamps savedAt, so the window only ever
         counts from the LAST visit. Only a player gone the full window is
         signed out. */
      if(Date.now() - t.savedAt > 86400000){
        try { t.savedAt = Date.now(); localStorage.setItem(TOKEN_KEY, JSON.stringify(t)); } catch(_){}
      }
      return t;
    } catch(e){ return null; }
  }
  function setStoredToken(email, pin, extras){
    var hadEmailBefore = false;
    try {
      var existing = {};
      try { existing = JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}') || {}; } catch(_){}
      hadEmailBefore = !!(existing && existing.email && String(existing.email).trim().length);
      var payload = {
        email: (email||'').trim().toLowerCase(),
        pin: pin,
        savedAt: Date.now(),
        /* phone: true when we know the user has an SMS-opted phone on
           file from a prior submission. Lets the new-submission form skip
           re-asking for SMS opt-in. */
        phone: (extras && typeof extras.phone === 'boolean') ? extras.phone : !!existing.phone
      };
      localStorage.setItem(TOKEN_KEY, JSON.stringify(payload));
    } catch(e){}
    /* R148-Z41: Central transition point — every successful sign-in
       (hero submission OR Your Hall PIN form) ends up here. Fire the
       gate-flip the moment the token lands so the wallet pill, the
       Explore the Hall section, and the Three Doors wake up without
       a page reload. The 'hoh:signed-in' event is the public signal;
       hohDispatchSignedIn() also toggles the html/body classes so CSS
       rules un-hide their gated regions immediately. The hadEmail-
       Before guard avoids dispatching on no-op writes (token refresh
       with the same email) — the event is a one-time transition. */
    try {
      if(!hadEmailBefore && email && String(email).trim().length){
        if(typeof window.hohDispatchSignedIn === 'function'){
          window.hohDispatchSignedIn('setStoredToken');
        }
      }
    } catch(_){}
  }
  function clearStoredToken(){ try { localStorage.removeItem(TOKEN_KEY); } catch(e){} }

  /* Display name helpers. Pulls the user's preferred name (player_name)
     from the most-recent submission once characters load. Falls back to
     the email prefix capitalized for the first paint. */
  function getStoredName(){
    try { return (localStorage.getItem(NAME_KEY) || '').trim(); } catch(e){ return ''; }
  }
  function setStoredName(n){
    try { if(n) localStorage.setItem(NAME_KEY, String(n).trim()); } catch(e){}
  }
  function emailPrefix(em){
    if(!em) return '';
    var raw = String(em).split('@')[0] || '';
    /* Strip dots/underscores, capitalize first word so "jane.doe" becomes
       "Jane". Single short token only — long emails would clutter the
       toggle button. */
    var first = raw.split(/[._\-+]/)[0] || '';
    if(!first) return '';
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  /* Public: returns the best available display name for the signed-in
     user. Cached name first, then email prefix, then empty string. */
  window.hohYourHallDisplayName = function(){
    var n = getStoredName();
    if(n) return n;
    var em = window.hohYourHallEmail();
    return em ? emailPrefix(em) : '';
  };
  /* Submission ownership comes from the account email, never a public name.
     auth_email is authoritative when present; email is the legacy fallback. */
  var accountSubmittedRows = [];
  function submittedByAccount(c, em) {
    if (!c || !em) return false;
    var owner = String(c.auth_email || '').trim() || String(c.email || '').trim();
    return owner.toLowerCase() === String(em).trim().toLowerCase();
  }
  function hallAccountCurrent(token) {
    var current = getStoredToken();
    return !!current && String(current.email).trim().toLowerCase() === String(token.email).trim().toLowerCase() &&
      String(current.pin) === String(token.pin);
  }
  window.hohYourHallSubmittedRows = function(){
    try {
      var token = getStoredToken();
      if(!token) return [];
      var all = (typeof window.hohGetAllCharacters === 'function')
        ? (window.hohGetAllCharacters() || []) : [];
      var seen = {};
      return accountSubmittedRows.concat(all).filter(function(c){
        if(!submittedByAccount(c, token.email) || !c.id || seen[c.id]) return false;
        seen[c.id] = true; return true;
      });
    } catch(_){ return []; }
  };
  /* God mode: signed-in display name === Jangela. Used by the
     section's heart cap, edit permissions, and other gates so the
     test account can hammer the system without affecting real
     players. Exposed on window so the section's canHeartToday
     and other gates can read it without re-implementing the check. */
  /* God mode requires BOTH the display name "jangela" AND a sign-in
     under the developer email. Either alone is not enough — a curious
     user can spoof the display name by submitting a hero with
     player_name="Jangela", but they can't fake the signed-in email
     because the PIN flow is what unlocks hohYourHallEmail(). The
     email check is the actual security boundary; the name check is
     kept so the dev can see the same UI affordances under either
     account. (The deeper fix lives server-side: the RPCs that grant
     elevated rights need to verify against an admin flag in the
     users table, not trust client-side identity.) */
  /* R50: god-mode email is the actual dev email used at sign-in. Was
     mistakenly set to Jan@framedfantasy.com before — that never matched
     the real account or the debug auto-sign-in, so god mode never
     activated. Use the real account email here. */
  var GOD_MODE_EMAIL = 'fondlyframed@gmail.com';
  /* Persistent debug flag — set by hohDebugSuccessView when the
     "Continue" link in the form is clicked. Survives page reloads
     and resists the character-data-load race that overwrites the
     display name from "Jangela" back to whatever player_name the
     most-recent submitted hero has. Email check still gates this:
     even if someone manually sets the flag, they need to be signed
     in as the dev account for god mode to engage. */
  var GOD_MODE_FLAG_KEY = 'hoh-god-mode-debug';
  window.hohIsGodMode = function(){
    try {
      var em = (typeof window.hohYourHallEmail === 'function')
        ? String(window.hohYourHallEmail() || '').toLowerCase()
        : '';
      /* Email check is the actual security boundary. Without it
         signed in, god mode is OFF regardless of any other state. */
      if(em !== GOD_MODE_EMAIL) return false;
      /* Either: display name says jangela (typed it), OR the debug
         flag is set (clicked Continue in the form). Either path is
         enough — the email check above is the real gate. */
      var n = window.hohYourHallDisplayName();
      var nameOk = !!n && String(n).toLowerCase() === 'jangela';
      var flagOk = false;
      try { flagOk = localStorage.getItem(GOD_MODE_FLAG_KEY) === '1'; } catch(_){}
      return nameOk || flagOk;
    } catch(e){ return false; }
  };
  /* Set/clear the debug flag. The Continue link calls
     hohYourHallSetGodModeFlag(true); the visible badge\'s exit-tap
     calls it with false to drop back into normal user mode. */
  window.hohYourHallSetGodModeFlag = function(on){
    try {
      if(on) localStorage.setItem(GOD_MODE_FLAG_KEY, '1');
      else localStorage.removeItem(GOD_MODE_FLAG_KEY);
    } catch(_){}
    /* Re-render any surface that depends on god mode state. */
    try { document.dispatchEvent(new CustomEvent('hoh:god-mode-changed', { detail: { on: !!on } })); } catch(_){}
    renderGodModeBadge();
  };
  /* R166: Bottom-left "DEBUG · GOD MODE" badge consolidated into the
     top-right gear panel (hoh-god-debug-panel). The badge function is
     kept as a no-op so the existing call sites (DOMContentLoaded,
     hoh:characters-loaded, hoh:god-mode-changed) don't error. Any
     stale DOM is also cleaned up on every call so a previously-
     mounted badge from a cached page disappears the moment the new
     JS loads. */
  function renderGodModeBadge(){
    try {
      var existing = document.getElementById('hoh-god-mode-badge');
      if(existing && existing.parentNode) existing.parentNode.removeChild(existing);
    } catch(_){}
  }
  /* Mount on initial load + whenever god-mode changes (sign-in,
     sign-out, debug toggle, character-data load that flips the
     display name). */
  function tryMountBadge(){ try { renderGodModeBadge(); } catch(_){} }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', tryMountBadge);
  } else {
    setTimeout(tryMountBadge, 100);
  }
  document.addEventListener('hoh:characters-loaded', tryMountBadge);
  document.addEventListener('hoh:god-mode-changed', tryMountBadge);
  /* Cache a name learned from the server (e.g., player_name on a
     returned character row). Re-renders the toggle so it picks up the
     fresh label immediately. */
  window.hohYourHallSetName = function(n){
    if(!n) return;
    var prev = getStoredName();
    if(prev === String(n).trim()) return;
    setStoredName(n);
    if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
  };

  window.hohYourHallIsSignedIn = function(){ return !!getStoredToken(); };
  /* Programmatic session set. Used by the submit flow to auto-sign-in
     a brand new user the moment their PIN registers, so they don't
     have to manually re-enter credentials in this same session.
     The previous behavior also bumped the wallet to 100 hearts as a
     "welcome" grant — that's what the user was seeing as "100 hearts
     after submit". Removed: the +7 form-submit bonus + the +3 daily
     visit grant for signed-in users are sufficient on-ramp. */
  window.hohYourHallSetSession = function(email, pin){
    if(!email || !pin) return;
    setStoredEmail(email);
    setStoredToken(email, pin);
    if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
  };
  /* R148-Z41: Email-only auto-sign-in. The hero submission form makes
     the PIN field optional — a brand-new visitor can submit a hero
     with just email + character details. Under the first-timer gate,
     owning a row with an email is enough to flip the visitor into the
     signed-in experience. This helper writes the email-bearing token
     without clobbering any existing PIN. Fires 'hoh:signed-in' via
     setStoredToken so the wallet pill, Explore the Hall, and Three
     Doors wake up in the same render frame. */
  window.hohYourHallSetEmailOnly = function(email){
    if(!email) return;
    var clean = String(email).trim().toLowerCase();
    if(!clean) return;
    var existing = getStoredToken();
    var pin = (existing && existing.pin) ? existing.pin : '';
    setStoredEmail(clean);
    setStoredToken(clean, pin);
    if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
  };
  /* Returns the signed-in email or empty string. Used by the section to
     decide whether to render the Edit button on a character's detail. */
  window.hohYourHallEmail = function(){
    var t = getStoredToken();
    return (t && t.email) ? t.email : '';
  };
  /* Returns true when the signed-in user already has a phone on file
     (from a previous submission or claim flow). Used by the new-submit
     form to skip the SMS opt-in field — they already opted in once. */
  window.hohYourHallHasPhone = function(){
    var t = getStoredToken();
    return !!(t && t.phone);
  };
  /* Set/unset the phone-on-file flag without touching email/pin. Called
     by the section's submit flow when a user submits with a phone, and
     by loadCharacters once it sees any of the user's rows has phone_number. */
  window.hohYourHallSetPhoneFlag = function(has){
    var t = getStoredToken();
    if(!t) return;
    setStoredToken(t.email, t.pin, { phone: !!has });
  };
  /* Called by the section's showFormView when opening the new-submission
     form (NOT in edit mode). For signed-in users we lock the email
     field (so they can't submit under another address), but everything
     else stays visible and editable: the SMS opt-in is submittable on
     its own (toggle on/off, update phone), and the PIN field remains
     present so users who didn't set one yet can do it now. */
  window.hohApplySignedInForm = function(){
    var t = getStoredToken();
    if(!t) return;
    var emEl = document.getElementById('hoh-f-email');
    if(emEl){
      emEl.value = t.email;
      emEl.readOnly = true;
      emEl.classList.add('hoh-input-locked');
      var note = emEl.parentNode && emEl.parentNode.querySelector('.hoh-field-note');
      if(note) note.textContent = 'Signed in as ' + t.email + '.';
    }
  };
  /* Returns true when the signed-in user owns the given character.
     Compares the active session email to the character's auth_email
     (added in phase 3 migration). Replaces the 30-minute edit window
     since authenticated owners can edit their characters indefinitely. */
  window.hohYourHallCanEditCharacter = function(c){
    if(!c) return false;
    /* God mode: Jangela can edit any hero. */
    if(typeof window.hohIsGodMode === 'function' && window.hohIsGodMode()) return true;
    if(!c.auth_email) return false;
    var em = window.hohYourHallEmail();
    if(!em) return false;
    return String(c.auth_email).toLowerCase() === String(em).toLowerCase();
  };
  /* Bottom-of-detail Edit button HTML, ALWAYS rendered. Click routes to
     edit (when signed-in owner) or claim (when not). The unified path
     keeps the section's render logic simple and lets every character
     surface a path to ownership for the rightful submitter. */
  /* ⛔ R568: `leadHtml` is prepended INSIDE this row. The Hall's detail view
     used to stack its own bordered "Show card view" panel above this row, so
     one screen spoke two visual languages for the same kind of action. Any
     caller that passes nothing behaves exactly as before. */
  window.hohYourHallEditButtonHtml = function(c, leadHtml){
    leadHtml = leadHtml || '';
    if(!c || !c.id) return '';
    var id = String(c.id).replace(/'/g,"\\'");
    /* R198: "See public profile" preview lets the owner view their own card exactly
       as the public sees it (no badge editor, no Edit/Hide/Delete). In preview, drop
       all owner affordances and show only the toggle back. */
    var _realOwn = (typeof window.hohIsCharacterOwner === 'function') && window.hohIsCharacterOwner(c);
    if((typeof window.hohIsViewingAsPublic === 'function') && window.hohIsViewingAsPublic(c)){
      /* ⛔ the lead links ride along here too. Without this an owner who taps
         "See public profile" loses the way back to the card view, because the
         only control that offered it was the panel this row replaced. A
         non-owner still gets the lead links and nothing else. */
      return _realOwn
        ? '<div class="hoh-detail-edit-row">'+leadHtml+'<button type="button" class="hoh-edit-link-bottom hoh-edit-link-bottom-preview" onclick="hohToggleViewAsPublic(\''+id+'\')">&#8249; Back to my view</button></div>'
        : (leadHtml ? '<div class="hoh-detail-edit-row">'+leadHtml+'</div>' : '');
    }
    /* Plain centered gold text instead of a golden button — sits
       quietly under "Image uploaded by submitter" / date. Functions
       identically to the previous button (same onclick handler). */
    /* R46: god-mode Hide/Unhide button. Renders only when hohIsGodMode()
       is true (Jangela). Click confirms then flips characters.is_hidden
       server-side. The character then disappears from gallery / Fresh
       Faces / events / search for every other visitor (including new
       ones) on their next page load. Jangela still sees it (with a
       hidden marker) so she can unhide it. */
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    var debugBtn = '';
    if(god){
      var isHidden = !!c.is_hidden;
      var label = isHidden ? 'Unhide' : 'Hide from Hall';
      var cls = isHidden ? 'hoh-edit-link-bottom-unhide' : 'hoh-edit-link-bottom-hide';
      debugBtn = '<button type="button" class="hoh-edit-link-bottom ' + cls + '" onclick="hohJangelaToggleHidden(\''+id+'\')">' + label + '</button>'
        /* R170: god-mode hard delete, sits next to Edit Hero. Removes the row
           from Supabase for good. */
        + '<button type="button" class="hoh-edit-link-bottom hoh-edit-link-bottom-delete" onclick="hohJangelaDeleteCharacter(\''+id+'\')">Delete</button>';
    }
    /* R65: god-mode label swap. When Jangela is viewing someone
       else\'s hero, the Edit button should make it clear this is the
       moderator action, not a claim. For her own heroes (or normal
       users) the label stays plain "Edit Hero Info". */
    /* R571 (Jan): "instead of the bottom saying Edit Hero, change it Edit Hero
       Info". Held in one constant because the god-mode branch below compares
       against it to decide whether to wear the moderator styling, and a
       renamed label with a stale comparison would have styled every owner's
       own button as a god action. */
    var EDIT_LABEL = 'Edit Hero Info';
    var editLabel = EDIT_LABEL;
    if(god){
      var owns = window.hohYourHallCanEditCharacter && window.hohYourHallCanEditCharacter(c);
      if(!owns) editLabel = 'Edit (God Mode)';
    }
    /* R571 (Jan): "remove the see public profile button". The preview MODE is
       left standing, along with its "Back to my view" way out above, so an
       owner who is somehow already inside it is never trapped. Only the door
       into it is gone. */
    var previewBtn = '';
    /* R318: tell the OWNER their hero is still hidden. A hidden hero is not in
       the public Hall and its art is not browsable, so without this note an owner
       who just claimed it would think their submission vanished. Shown only to a
       real owner (not to god, who has the Hide/Unhide control instead, and not in
       the public-preview mode). The copy explains the state and the way out. */
    var hiddenNote = '';
    if(_realOwn && !god && !window.hohIsViewingAsPublic(c) && !!c.is_hidden){
      /* R401 (Jan): the owner reads WHY, in the kind words Jan picked at hide
         time, and the note points at the road back instead of a dead end.
         Without a stored reason the note keeps its original single line. */
      var _hr = '';
      try { _hr = (c.quest_meta && c.quest_meta.hidden_reason) ? String(c.quest_meta.hidden_reason) : ''; } catch(_){}
      /* OWNHIDDEN (2026-10-29): a hidden hero's link no longer opens for anyone else, so the note says only the
         owner sees it, and a new picture already on its way to Jan says so instead of repeating the old note. */
      var _qm = c.quest_meta || {};
      var _resent = _qm.review_state === 'pending' && !!_qm.resubmitted_at;
      var _hasArt = !!(c.image_url || c.image_thumb_url);
      var _hrHtml = _resent
        ? ' <span style="display:block;margin-top:5px">Your new picture is with Jan. Only you can see this hero until then.</span>'
        : _hr
        ? ' <em style="display:block;margin-top:5px;color:#e8d4b0">Jan&rsquo;s note: ' + _hr.replace(/&/g,'&amp;').replace(/</g,'&lt;') + '</em>' +
          '<span style="display:block;margin-top:5px">Touch it up with Edit Hero Info below and it can hang on the wall again. Only you can see it in the meantime.</span>'
        : _hasArt
        ? ' Only you can see it while Jan takes a look.'
        : ' Add a picture with Edit Hero Info below and Jan will take a look. Only you can see it until then.';
      hiddenNote = '<div class="hoh-hidden-note" role="note">' +
        '<span class="hoh-hidden-note-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M2 12s3.6-6.5 10-6.5c1.6 0 3 .4 4.2 1"/><path d="M22 12s-3.6 6.5-10 6.5c-1.6 0-3-.4-4.2-1"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M3 3l18 18"/></svg>' +
        '</span>' +
        '<span class="hoh-hidden-note-text"><strong>This hero is resting off the Hall wall for now.</strong>' + _hrHtml + '</span>' +
      '</div>';
    }
    return hiddenNote + '<div class="hoh-detail-edit-row">' + leadHtml +
      '<button type="button" class="hoh-edit-link-bottom' + (god && editLabel !== EDIT_LABEL ? ' hoh-edit-link-bottom-god' : '') + '" onclick="hohOpenEditOrClaim(\''+id+'\')">' + editLabel + '</button>' +
      debugBtn +
      previewBtn +
    '</div>';
  };
  /* R198: owner public-profile preview. _hohViewAsPublic (scoped to one hero via
     _hohViewAsPublicHero) makes the detail render as a non-owner would see it. The
     badge section and the edit row both check hohIsViewingAsPublic. */
  window.hohIsViewingAsPublic = function(c){ return !!window._hohViewAsPublic && c && String(window._hohViewAsPublicHero) === String(c.id); };
  window.hohToggleViewAsPublic = function(id){
    if(!id) return;
    if(window._hohViewAsPublic && String(window._hohViewAsPublicHero) === String(id)){ window._hohViewAsPublic = false; }
    else { window._hohViewAsPublic = true; window._hohViewAsPublicHero = String(id); window._hohBadgeEdit = false; }
    try { if(typeof window.hohOpenDetail === 'function') window.hohOpenDetail(id); } catch(_){}
  };
  /* R170: god-mode HARD delete. Removes the character row from Supabase
     entirely. God gated client-side. If the database blocks the delete (no
     RLS delete policy), we detect the zero-row result and say so instead of
     pretending it worked. Cleans caches and closes the modal on success. */
  window.hohJangelaDeleteCharacter = async function(id){
    if(!id) return;
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    if(!god) return;
    var all = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull() : [];
    var c = all.find(function(x){ return x && String(x.id) === String(id); });
    if(!c && Array.isArray(window.__hohHidden)) c = window.__hohHidden.find(function(x){ return x && String(x.id) === String(id); });
    var name = (c && c.name) ? c.name : 'this hero';
    var ok = false;
    try { ok = window.confirm('Delete ' + name + ' permanently? This removes them from the Hall and the database for good, and cannot be undone.'); } catch(_){ ok = false; }
    if(!ok) return;
    var sb = window.hohSupabaseClient;
    if(!sb){ try { alert('Database client unavailable.'); } catch(_){} return; }
    try {
      /* R170: secure delete via a PIN-gated function. We never open a public
         delete policy (the anon key is in the page source). The function checks
         the admin email + your PIN server-side, then deletes with definer
         rights. PIN is asked once per session and cached. */
      var email = (typeof window.hohYourHallEmail === 'function') ? (window.hohYourHallEmail() || '') : '';
      var pin = '';
      try { pin = sessionStorage.getItem('hoh-admin-pin') || ''; } catch(_){}
      if(!pin){
        try { pin = window.prompt('Enter your admin PIN to delete (the same PIN you signed into the Hall with):') || ''; } catch(_){}
        if(pin){ try { sessionStorage.setItem('hoh-admin-pin', pin); } catch(_){} }
      }
      if(!pin) return;
      var res = await sb.rpc('admin_delete_character', { p_email: email, p_pin: pin, p_id: id });
      if(res.error){
        try { alert('Delete failed: ' + (res.error.message || 'unknown error') + '\n\nIf the function is missing, run the one-time SQL first.'); } catch(_){}
        return;
      }
      var dres = res.data || {};
      if(dres.ok !== true){
        if(dres.error === 'bad_pin'){ try { sessionStorage.removeItem('hoh-admin-pin'); } catch(_){} try { alert('Wrong admin PIN. Press Delete again to retry.'); } catch(_){} }
        else if(dres.error === 'not_admin'){ try { alert('Only the admin account can delete.'); } catch(_){} }
        else { try { alert('Delete blocked: ' + (dres.error || 'unknown') + '.\n\nIf the function is missing, run the one-time SQL first.'); } catch(_){} }
        return;
      }
      try { if(Array.isArray(window.__hohHidden)){ var j = window.__hohHidden.findIndex(function(x){ return x && String(x.id) === String(id); }); if(j>=0) window.__hohHidden.splice(j,1); } } catch(_){}
      try { if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('Deleted', name + ' was permanently removed.'); } catch(_){}
      try { var modal = document.getElementById('hoh-modal'); if(modal) modal.style.display = 'none'; document.body.style.overflow = ''; } catch(_){}
      try {
        if(typeof window.hohReloadCharacters === 'function'){
          await window.hohReloadCharacters();
          /* R198: the reload resets __hohHidden to null; re-fetch it so the NEXT
             hidden hero in the moderation view still opens right after a delete. */
          if(typeof window.hohEnsureHiddenChars === 'function') window.hohEnsureHiddenChars();
        }
      } catch(_){}
    } catch(e){
      try { alert('Delete failed: ' + (e && e.message ? e.message : 'unknown error')); } catch(_){}
    }
  };
  /* R176: shared lookup for a character by id across the visible list and the
     god-mode hidden cache. */
  function _hohFindCharById(id){
    var all = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull() : [];
    var c = all.find(function(x){ return x && String(x.id) === String(id); });
    if(!c && Array.isArray(window.__hohHidden)) c = window.__hohHidden.find(function(x){ return x && String(x.id) === String(id); });
    return c || null;
  }
  /* R176: repaint the live card strip in place (keeps the card's .hoh-visible
     reveal state, unlike a full node swap) and reopen the detail so its medals
     and controls reflect the new state immediately. */
  function _hohRepaintBadges(c, id){
    try {
      var _sel = '.hoh-card[data-hero-id="' + String(id).replace(/"/g, '\\"') + '"]';
      var _cards = document.querySelectorAll(_sel);
      /* R437: the grid no longer carries a badge row, so this only has to
         sweep away any row left in a tile the browser is still holding from
         before the change. It never re-inserts one. The detail reopen at the
         end of this function is what actually shows the new badge state. */
      var _rowHtml = '';
      for(var _k = 0; _k < _cards.length; _k++){
        var card = _cards[_k];
        /* R178: the trial row now lives in the card body, just above the footer.
           Drop any existing row, then (if there are badges) re-insert a fresh one
           before the footer to match the builder's placement. */
        var _old = card.querySelector('.hoh-quest-row');
        if(_old && _old.parentNode) _old.parentNode.removeChild(_old);
        if(!_rowHtml) continue;
        var body = card.querySelector('.hoh-card-body');
        if(!body) continue;
        var footer = body.querySelector('.hoh-card-footer');
        if(footer) footer.insertAdjacentHTML('beforebegin', _rowHtml);
        else body.insertAdjacentHTML('beforeend', _rowHtml);
      }
    } catch(_){}
    try { if(typeof window.hohOpenDetail === 'function') window.hohOpenDetail(id); } catch(_){}
  }

  /* R196: god grant/revoke is a single per-hero toggle on quest_meta.earned, so
     revoke always works (it simply removes the key). Only quest trials and Raffle
     Winner are grantable. Trials read account-wide because hohBuildAccountBadgeMaps
     unions earned across the account; raffle-winner is excluded from that union so it
     stays on this one hero. Hall Contributor is derived from submission count, and
     FF / Merchant's Mark / Completionist are per-hero status or derived, so none of
     those are grantable here. The account maps rebuild after each toggle so the
     change shows immediately, account-wide and per-hero. */
  window.hohJangelaToggleBadge = async function(id, key){
    if(!id || !key) return;
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    if(!god) return;
    if(['iron-matron','iron-court','raffle-winner'].indexOf(key) === -1) return; /* only these are grantable */
    var c = _hohFindCharById(id);
    if(!c) return;
    var sb = window.hohSupabaseClient;
    if(!sb){ try { alert('Database client unavailable.'); } catch(_){} return; }
    var qm = {}; try { qm = Object.assign({}, c.quest_meta || {}); } catch(_){}
    var earned = Array.isArray(qm.earned) ? qm.earned.slice()
               : (Array.isArray(qm.badges) ? qm.badges.slice()
               : (qm.source ? [qm.source] : []));
    var i = earned.indexOf(key);
    if(i === -1) earned.push(key); else earned.splice(i, 1);
    qm.earned = earned;
    if(i !== -1 && Array.isArray(qm.hidden)) qm.hidden = qm.hidden.filter(function(k){ return k !== key; });
    try {
      var res = await sb.from('characters').update({ quest_meta: qm }).eq('id', id).select('id');
      if(res.error){ try { alert('Could not save badge: ' + (res.error.message || 'error')); } catch(_){} return; }
      if(!res.data || !res.data.length){ try { alert('The server did not accept the change (the database may be blocking this update).'); } catch(_){} return; }
      c.quest_meta = qm;
      try { if(typeof window.hohBuildAccountBadgeMaps === 'function') await window.hohBuildAccountBadgeMaps(); } catch(_){}
      _hohRepaintBadges(c, id);
    } catch(e){ try { alert('Could not save badge.'); } catch(_){} }
  };

  /* R176: owner equip/unequip. The verified owner toggles whether an EARNED
     trial shows on their card by editing quest_meta.hidden. Guarded so they can
     never equip a trial they didn't earn, and a non-owner never passes the
     ownership check. Uses the same authorized character update the owner
     edit-save relies on. */
  window.hohOwnerToggleBadge = async function(id, key){
    if(!id || !key) return;
    var c = _hohFindCharById(id);
    if(!c) return;
    var isOwner = (typeof window.hohIsCharacterOwner === 'function') && window.hohIsCharacterOwner(c);
    if(!isOwner) return;
    var disp = (typeof window.hohGetHeroDisplayableBadges === 'function') ? window.hohGetHeroDisplayableBadges(c) : [];
    if(disp.indexOf(key) === -1) return; /* R197: owner can show/hide any badge this hero displays */
    var qm = {}; try { qm = Object.assign({}, c.quest_meta || {}); } catch(_){}
    var hidden = Array.isArray(qm.hidden) ? qm.hidden.slice() : [];
    var i = hidden.indexOf(key);
    if(i === -1) hidden.push(key); else hidden.splice(i, 1); /* toggle hidden */
    qm.hidden = hidden;
    var sb = window.hohSupabaseClient;
    if(!sb){ try { alert('Database client unavailable.'); } catch(_){} return; }
    try {
      var res = await sb.from('characters').update({ quest_meta: qm }).eq('id', id).select('id');
      if(res.error){ try { alert('Could not update: ' + (res.error.message || 'error')); } catch(_){} return; }
      c.quest_meta = qm;
      _hohRepaintBadges(c, id);
    } catch(e){ try { alert('Could not update.'); } catch(_){} }
  };
  /* R198: owner badge-loadout edit toggle. Flips an inline edit state on the
     opened card's badges section (scoped to one hero via _hohBadgeEditHero, so
     opening a different hero exits edit) and reopens the detail to reflect it. */
  window.hohToggleBadgeEdit = function(id){
    if(!id) return;
    var c = _hohFindCharById(id);
    if(!c) return;
    var isOwner = (typeof window.hohIsCharacterOwner === 'function') && window.hohIsCharacterOwner(c);
    if(!isOwner) return;
    if(window._hohBadgeEdit && String(window._hohBadgeEditHero) === String(id)){ window._hohBadgeEdit = false; }
    else { window._hohBadgeEdit = true; window._hohBadgeEditHero = String(id); }
    try { if(typeof window.hohOpenDetail === 'function') window.hohOpenDetail(id); } catch(_){}
  };
  /* God-mode toggle for the is_hidden flag. Confirms with the user
     since this is a destructive, public-facing change. Updates the
     row server-side, mutates the local cache, re-renders gallery +
     closes the detail modal so Jangela sees the result immediately. */
  window.hohJangelaToggleHidden = async function(id){
    if(!id) return;
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    if(!god) return; /* defensive: client-side gate */
    /* R168: look up from the FULL list and the god-mode hidden cache, since
       the main fetch no longer keeps hidden heroes in the visible list.
       Without this, Unhide could never find a hidden hero. */
    var all = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull()
            : ((typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : []);
    var c = all.find(function(x){ return x && String(x.id) === String(id); });
    if(!c && Array.isArray(window.__hohHidden)) c = window.__hohHidden.find(function(x){ return x && String(x.id) === String(id); });
    if(!c) return;
    var willHide = !c.is_hidden;
    var name = c.name || 'this hero';
    /* R401 (Jan): HIDING ASKS FOR A REASON. The sheet below collects a kind,
       blame-free line (a preset or Jan's own words) and the submitter reads
       the same line on their hero. Unhide keeps the one-line confirm. */
    if(willHide){
      hohHideReasonSheet(name, function(reason){
        hohJangelaApplyHidden(c, true, reason);
      });
      return;
    }
    var ok = false;
    try { ok = window.confirm('Unhide ' + name + '? They\'ll be visible to everyone again.'); } catch(_){ ok = true; }
    if(!ok) return;
    hohJangelaApplyHidden(c, false, null);
  };
  /* R401: the write, shared by hide (with reason) and unhide (clears it).
     The reason rides in quest_meta beside admin_hidden — same JSONB, same
     update, no new column, so the owner's detail row already carries it. */
  async function hohJangelaApplyHidden(c, willHide, reason){
    var id = c.id, name = c.name || 'this hero';
    var sb = window.hohSupabaseClient;
    if(!sb){ try { alert('Database client unavailable.'); } catch(_){} return; }
    try {
      /* R168: tag manual hides as admin_hidden so the future owner-edit
         unhide rule never resurrects a hero Jangela deliberately hid. On
         unhide, clear it and approve any pending quest hero. */
      var _meta = {}; try { _meta = Object.assign({}, c.quest_meta || {}); } catch(_){}
      _meta.admin_hidden = willHide;
      /* ⛔ R664 (Jan): "if i go to an image in the main hall that i dont like and
         i click hide, it should just get added to a rejected pile."
         R198 stamped a hand-hidden hero `pending`, which is the QUEUE, so every
         hero Jan hid from the Hall came straight back at him to review again.
         That is the "i notice a lot of them get recycled back again" report,
         and it was this line. A hide is a DECISION, so it lands in the rejected
         pile with a strike, exactly like pressing Reject inside the review tool.
         The way back is the owner changing the picture (hoh-section-core.js),
         which re-stamps it pending and returns it to the queue for approval. */
      if(willHide){
        _meta.review_state = 'rejected';
        _meta.reject_count = ((_meta.reject_count | 0) || 0) + 1;
      }
      /* Unhiding is the opposite decision, so it clears every not-public state
         including the two rejected spellings. Before R664 this list omitted
         them, so unhiding a rejected hero left the stamp behind and the review
         tool still counted a live public hero in its rejected pile. */
      else if(_meta.review_state === 'pending' || _meta.review_state === 'no_image'
              || _meta.review_state === 'rejected' || _meta.review_state === 'archived'){
        _meta.review_state = 'approved';
      }
      /* R401: the reason the submitter will read, worded kindly */
      if(willHide && reason){ _meta.hidden_reason = String(reason).slice(0, 240); _meta.hidden_at = new Date().toISOString(); }
      if(!willHide){ try { delete _meta.hidden_reason; delete _meta.hidden_at; } catch(_){} }
      var res = await sb.from('characters').update({ is_hidden: willHide, quest_meta: _meta }).eq('id', id).select('id');
      /* R46 schema-tolerance: if is_hidden column doesn\'t exist yet,
         Supabase rejects with a schema error. Surface a clear message
         pointing to the migration. */
      if(res.error){
        if(/is_hidden/i.test(res.error.message || '')){
          try { alert('SQL migration not yet applied. Run:\n\nALTER TABLE characters ADD COLUMN IF NOT EXISTS is_hidden boolean NOT NULL DEFAULT false;'); } catch(_){}
        } else {
          try { alert('Hide failed: ' + (res.error.message || 'unknown error')); } catch(_){}
        }
        return;
      }
      /* Mutate local cache so re-renders pick up the new flag without
         a full reload. */
      c.is_hidden = willHide;
      try { c.quest_meta = _meta; } catch(_){}
      /* Show a quick toast so Jangela has feedback. */
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast(willHide ? 'Hidden from Hall' : 'Restored to Hall', name + (willHide ? ' is now hidden from everyone.' : ' is visible again.')); } catch(_){}
      }
      /* Close the detail modal and trigger a full reload of the
         characters cache. We can\'t just mutate the local row because
         downstream surfaces (Fresh Faces rail, gallery, search index)
         have their own cached lists derived from allCharacters at
         load time. The cleanest refresh path is to re-run
         loadCharacters which re-fetches, re-filters (god-mode aware),
         and re-renders every surface. Single round-trip; fast.
         Falls back to the event dispatch if loadCharacters isn\'t
         exposed for some reason. */
      try { if(typeof window.hohCloseModal === 'function') window.hohCloseModal(); } catch(_){}
      /* R265: push the hide/unhide to every other open tab live. */
      try { if(typeof window.hohBroadcastHeroChange === 'function') window.hohBroadcastHeroChange(id); } catch(_){}
      /* R432 (Jan): "when i hide dont reset the whole grid so i have to scroll
         down."
         The reload is still the right call — Fresh Faces, the search index and
         the marquee all keep their own lists derived from allCharacters, so
         mutating one row locally leaves those stale. What was wrong is that
         re-rendering the gallery threw away her scroll position, so every hide
         dumped her back at the top of a thousand heroes and she had to find
         her place again. On a moderation pass that is the single most
         expensive thing in the loop.
         The position is captured before the reload and put back after it. Two
         restores on purpose: one on the next frame for the synchronous
         re-render, and one a beat later for the async fetch that follows. Both
         bail if she has already scrolled herself, so it can never fight her. */
      var _sy = 0;
      try { _sy = window.scrollY || window.pageYOffset || 0; } catch(_){}
      var _restore = function(){
        try {
          var now = window.scrollY || window.pageYOffset || 0;
          /* only correct a jump the RE-RENDER caused. If she has moved more
             than a screen herself, that is her intent and it wins. */
          if(Math.abs(now - _sy) > 40 && Math.abs(now - _sy) < (window.innerHeight || 800) * 4){
            window.scrollTo(0, _sy);
          }
        } catch(_){}
      };
      if(typeof window.hohReloadCharacters === 'function'){
        try { window.hohReloadCharacters(); } catch(_){}
      } else {
        try { document.dispatchEvent(new CustomEvent('hoh:characters-loaded')); } catch(_){}
      }
      try { requestAnimationFrame(_restore); } catch(_){ setTimeout(_restore, 0); }
      setTimeout(_restore, 420);
    } catch(e){
      try { alert('Hide failed: ' + (e && e.message ? e.message : 'unknown error')); } catch(_){}
    }
  }
  /* R401: the reason sheet. Presets first — every line states what the HALL
     needs, never what the submitter did wrong, and each implies the road
     back. Custom lets Jan write his own; the same words land on the owner's
     hero, so the sheet says so right at the top. God-only surface, so it is
     self-contained (own styles, no stylesheet dependency). */
  function hohHideReasonSheet(name, onPick){
    var old = document.getElementById('hohHideReason');
    if(old){ try { old.remove(); } catch(_){} }
    /* R432 (Jan): the list is what she actually reaches for, nothing else.
       OUT: "the story text needs a small touch up" (never used) and "the image
       is a mismatch for the Hall's painted style" (both called useless — a
       reason nobody picks is a row of noise between her and the one she wants).
       IN: the three real rejections — a grid or character sheet instead of a
       portrait, text burned into the image, and a real-life photo.
       Every line still names what the HALL needs and implies the road back,
       because the submitter reads it word for word on their own hero. */
    var PRESETS = [
      'This needs to be a single portrait — character sheets and image grids can’t go in the Hall.',
      'The image has text on it. The Hall shows art only, so a clean version is needed.',
      'The Hall is for illustrated heroes, so a real-life photo can’t be shown here.',
      'The image needs a sharper, higher quality version.',
      'This hero looks like a duplicate of another submission.',
      /* Jan, 2026-10-28: "add another reason here for me to pick which is low quality submission" */
      'The Hall shows finished, polished art, so this piece can’t go in yet.'
    ];
    var veil = document.createElement('div');
    veil.id = 'hohHideReason';
    veil.style.cssText = 'position:fixed;inset:0;z-index:100090;background:rgba(6,5,10,.86);display:flex;align-items:center;justify-content:center;padding:18px;';
    var chipCss = 'display:block;width:100%;text-align:left;background:#141020;border:1px solid #3a3050;border-radius:10px;padding:11px 13px;margin:7px 0;color:#cdc4dd;font-size:13.5px;line-height:1.4;cursor:pointer;font-family:inherit;';
    veil.innerHTML =
      '<div style="width:min(430px,94vw);max-height:86vh;overflow-y:auto;background:#0b0911;border:1px solid #362c50;border-radius:16px;padding:18px 16px;box-shadow:0 24px 60px rgba(0,0,0,.85)">' +
        '<div style="font-family:Cinzel,serif;font-size:12px;letter-spacing:.2em;text-align:center;color:#c9a84c;text-transform:uppercase;margin-bottom:4px">Hide ' + String(name).replace(/</g,'&lt;') + '</div>' +
        '<div style="font-size:12.5px;color:#8a8090;text-align:center;font-style:italic;margin-bottom:12px">Tap a reason and it hides straight away. The submitter reads it word for word on their hero.</div>' +
        PRESETS.map(function(p, i){
          return '<button type="button" data-preset="' + i + '" style="' + chipCss + '">' + p + '</button>';
        }).join('') +
        '<textarea id="hohHideReasonCustom" maxlength="240" placeholder="Or write your own reason…" style="' + chipCss + 'min-height:64px;resize:vertical;"></textarea>' +
        '<div style="display:flex;gap:8px;margin-top:10px">' +
          '<button type="button" id="hohHideReasonGo" style="flex:1;background:linear-gradient(180deg,#f2d98e,#cfa95c);border:0;border-radius:10px;padding:12px;font-family:Cinzel,serif;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#1c1426;cursor:pointer">Hide with this reason</button>' +
          '<button type="button" id="hohHideReasonX" style="background:none;border:1px solid #3a3050;border-radius:10px;padding:12px 16px;color:#a9a0bb;cursor:pointer;font-family:inherit">Cancel</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(veil);
    var picked = null;
    var chips = veil.querySelectorAll('[data-preset]');
    var custom = veil.querySelector('#hohHideReasonCustom');
    function markPicked(){
      for(var i = 0; i < chips.length; i++){
        var on = picked === PRESETS[+chips[i].getAttribute('data-preset')];
        chips[i].style.borderColor = on ? '#c9a84c' : '#3a3050';
        chips[i].style.color = on ? '#f2d98e' : '#cdc4dd';
      }
    }
    /* R432 (Jan): "make the hiding process easier for me as the mod."
       A preset used to only SELECT, leaving her to reach for "Hide with this
       reason" afterwards — two taps and a travel across the sheet for the
       common case, on every single hero she reviews. Tapping a preset now
       hides immediately, because choosing the reason IS the decision. The
       button below stays for the custom box, where she is still typing and a
       tap would be premature. */
    for(var ci = 0; ci < chips.length; ci++){
      (function(chip){
        chip.addEventListener('click', function(){
          picked = PRESETS[+chip.getAttribute('data-preset')];
          try { custom.value = ''; } catch(_){}
          markPicked();
          close();
          onPick(picked);
        });
      })(chips[ci]);
    }
    if(custom) custom.addEventListener('input', function(){
      if(custom.value.trim()){ picked = null; markPicked(); }
    });
    function close(){ try { veil.remove(); } catch(_){} }
    veil.addEventListener('click', function(e){ if(e.target === veil) close(); });
    var goB = veil.querySelector('#hohHideReasonGo');
    if(goB) goB.addEventListener('click', function(){
      var custText = custom ? custom.value.trim() : '';
      var reason = custText || picked;
      if(!reason){
        goB.textContent = 'Pick or write a reason first';
        setTimeout(function(){ goB.textContent = 'Hide with this reason'; }, 1600);
        return;
      }
      close();
      onPick(reason);
    });
    var xB = veil.querySelector('#hohHideReasonX');
    if(xB) xB.addEventListener('click', close);
  }
  /* Routing helper: signed-in owner edits, everyone else claims. */
  window.hohOpenEditOrClaim = function(id){
    if(!id) return;
    /* R178: god-mode bypass FIRST, before any not-found return. Jangela goes
       straight to edit on ANY character. A hidden hero (exactly what god mode
       reviews) is not in hohGetAllCharacters(), so the old order bailed at the
       not-found check below before ever reaching this branch, and Edit Hero did
       nothing. hohOpenEditForm loads the hero by id itself (visible list,
       __hohHidden cache, or a direct fetch), so passing the id is enough. */
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    if(god){
      if(typeof window.hohOpenEditForm === 'function') window.hohOpenEditForm(id);
      return;
    }
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    var c = all.find(function(x){ return x.id === id; });
    /* OWNHIDDEN (2026-10-29): a hero Jan hid is not in the visible list, so its owner's Edit did nothing. The
       owner's own hidden heroes (hohFindOwnHidden, loaded only for the signed-in account) open the edit form too. */
    if(!c && typeof window.hohFindOwnHidden === 'function') c = window.hohFindOwnHidden(id);
    if(!c) return;
    if(window.hohYourHallCanEditCharacter && window.hohYourHallCanEditCharacter(c)){
      if(typeof window.hohOpenEditForm === 'function') window.hohOpenEditForm(id);
    } else {
      window.hohOpenClaimCharacter(c);
    }
  };

  /* ============ CLAIM CHARACTER FLOW ============
     Focused 480px modal that lets a user claim a single character by
     typing the email used to submit it. Once email matches, server
     check decides whether to show "Set PIN" (legacy claim, no PIN yet)
     or "Enter PIN" (PIN already exists for this email). On success the
     session token is stored, the modal closes, and the edit form opens
     so the user lands exactly where they wanted to. */
  var CLAIM_STATE = { char: null, email: '', mode: null /* 'set' | 'enter' */ };

  function ensureClaimModal(){
    var m = document.getElementById('hoh-claim-modal');
    if(m) return m;
    m = document.createElement('div');
    m.id = 'hoh-claim-modal';
    m.className = 'hoh-yh-modal hoh-claim-modal';
    m.style.display = 'none';
    m.innerHTML =
      '<div class="hoh-claim-content" role="dialog" aria-label="Claim Hero">' +
        '<button type="button" class="hoh-yh-close" onclick="hohCloseClaim()" aria-label="Close">&#10005;</button>' +
        '<div id="hoh-claim-body"></div>' +
      '</div>';
    m.addEventListener('click', function(e){ if(e.target === m) window.hohCloseClaim(); });
    document.body.appendChild(m);
    return m;
  }

  window.hohOpenClaimCharacter = function(c){
    if(!c || !c.id) return;
    CLAIM_STATE = { char: c, email: '', mode: null };
    var m = ensureClaimModal();
    m.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    renderClaimEmailStep();
  };

  window.hohCloseClaim = function(){
    var m = document.getElementById('hoh-claim-modal');
    if(m) m.style.display = 'none';
    var detail = document.getElementById('hoh-modal');
    var detailOpen = detail && detail.style.display === 'flex';
    if(!detailOpen) document.body.style.overflow = '';
  };

  function renderClaimEmailStep(prefill, errorMsg){
    var body = document.getElementById('hoh-claim-body');
    if(!body) return;
    var c = CLAIM_STATE.char;
    var name = c ? esc(c.name || 'this hero') : 'this hero';
    body.innerHTML =
      '<div class="hoh-claim-pane">' +
        '<div class="hoh-yh-eyebrow">Claim &amp; Edit</div>' +
        '<h2 class="hoh-yh-title">' + name + '</h2>' +
        '<p class="hoh-yh-lead">Type the email used when this hero was submitted to claim them.</p>' +
        '<form class="hoh-yh-form" onsubmit="event.preventDefault();hohClaimSubmitEmail();">' +
          '<label class="hoh-yh-label" for="hoh-claim-email">Email</label>' +
          '<input class="hoh-yh-input" type="email" id="hoh-claim-email" autocomplete="email" required value="' + esc(prefill||'') + '" />' +
          (errorMsg ? '<div class="hoh-yh-error">' + esc(errorMsg) + '</div>' : '') +
          '<button type="submit" class="hoh-yh-submit">Continue</button>' +
        '</form>' +
        '<p class="hoh-yh-foot">Email must match the one on this hero. Your session is encrypted and stays on this device.</p>' +
      '</div>';
    setTimeout(function(){ var el = document.getElementById('hoh-claim-email'); if(el && !el.value) el.focus(); else if(el) el.select(); }, 100);
  }

  window.hohClaimSubmitEmail = async function(){
    var c = CLAIM_STATE.char;
    if(!c) return;
    var em = (document.getElementById('hoh-claim-email')||{}).value || '';
    em = em.trim().toLowerCase();
    if(!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)){
      renderClaimEmailStep(em, 'Enter a valid email.');
      return;
    }
    var charEmail = String(c.auth_email || c.email || '').trim().toLowerCase();
    if(!charEmail || em !== charEmail){
      renderClaimEmailStep(em, 'That email does not match this hero. Try again.');
      return;
    }
    CLAIM_STATE.email = em;
    /* Decide which PIN flow to show. */
    var sb = window.hohSupabaseClient;
    var hasPin = false;
    if(sb && sb.rpc){
      try {
        var res = await sb.rpc('email_has_pin', { user_email: em });
        hasPin = !!(res && res.data === true);
      } catch(e){ hasPin = false; }
    }
    CLAIM_STATE.mode = hasPin ? 'enter' : 'set';
    renderClaimPinStep();
  };

  function renderClaimPinStep(errorMsg){
    var body = document.getElementById('hoh-claim-body');
    if(!body) return;
    var c = CLAIM_STATE.char;
    var name = c ? esc(c.name || 'this hero') : 'this hero';
    var isSet = CLAIM_STATE.mode === 'set';
    body.innerHTML =
      '<div class="hoh-claim-pane">' +
        '<div class="hoh-yh-eyebrow">Claim &amp; Edit</div>' +
        '<h2 class="hoh-yh-title">' + (isSet ? 'Set a 4-digit PIN' : 'Enter your PIN') + '</h2>' +
        '<p class="hoh-yh-lead">' + (isSet ? 'Pick a PIN to claim ' + name + ' and any other heroes on this email.' : 'You set this PIN before. Enter it to claim ' + name + '.') + '</p>' +
        '<form class="hoh-yh-form" onsubmit="event.preventDefault();hohClaimSubmitPin();">' +
          '<div class="hoh-yh-pin-row">' +
            '<input class="hoh-yh-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-claim-pin-1" autocomplete="off" />' +
            '<input class="hoh-yh-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-claim-pin-2" autocomplete="off" />' +
            '<input class="hoh-yh-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-claim-pin-3" autocomplete="off" />' +
            '<input class="hoh-yh-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-claim-pin-4" autocomplete="off" />' +
          '</div>' +
          (errorMsg ? '<div class="hoh-yh-error">' + esc(errorMsg) + '</div>' : '') +
          '<button type="submit" class="hoh-yh-submit">' + (isSet ? 'Set PIN &amp; Claim' : 'Claim') + '</button>' +
        '</form>' +
        '<button type="button" class="hoh-yh-back" onclick="hohClaimBackToEmail()">&larr; Use a different email</button>' +
      '</div>';
    /* Wire PIN input behavior (auto-advance, paste, focus). */
    var pins = ['hoh-claim-pin-1','hoh-claim-pin-2','hoh-claim-pin-3','hoh-claim-pin-4'].map(function(id){ return document.getElementById(id); });
    pins.forEach(function(inp, i){
      if(!inp) return;
      inp.addEventListener('input', function(e){
        var v = (e.target.value || '').replace(/\D/g, '').slice(0, 1);
        e.target.value = v;
        if(v && pins[i+1]) pins[i+1].focus();
      });
      inp.addEventListener('keydown', function(e){
        if(e.key === 'Backspace' && !inp.value && pins[i-1]) pins[i-1].focus();
      });
      inp.addEventListener('paste', function(e){
        var pasted = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
        if(pasted.length >= 4){
          e.preventDefault();
          for(var k = 0; k < 4 && k < pins.length; k++) pins[k].value = pasted.charAt(k);
          if(pins[3]) pins[3].focus();
        }
      });
    });
    if(pins[0]) setTimeout(function(){ pins[0].focus(); }, 100);
  }

  window.hohClaimBackToEmail = function(){
    renderClaimEmailStep(CLAIM_STATE.email);
  };

  window.hohClaimSubmitPin = async function(){
    var c = CLAIM_STATE.char;
    var em = CLAIM_STATE.email;
    if(!c || !em) return;
    var pin = ['hoh-claim-pin-1','hoh-claim-pin-2','hoh-claim-pin-3','hoh-claim-pin-4']
      .map(function(id){ return (document.getElementById(id)||{}).value || ''; })
      .join('');
    if(pin.length !== 4 || !/^\d{4}$/.test(pin)){
      renderClaimPinStep('PIN must be 4 digits.');
      return;
    }
    var sb = window.hohSupabaseClient;
    if(!sb){
      renderClaimPinStep('Database unavailable. Refresh and try again.');
      return;
    }
    try {
      if(CLAIM_STATE.mode === 'set'){
        var setRes = await sb.rpc('set_pin', { user_email: em, old_pin: null, new_pin: pin });
        if(!setRes || setRes.error || setRes.data === false){
          renderClaimPinStep('Could not set PIN. Try again.');
          return;
        }
      } else {
        var verifyRes = await sb.rpc('verify_pin', { user_email: em, user_pin: pin });
        var n = (verifyRes && typeof verifyRes.data === 'number') ? verifyRes.data : 0;
        if(!n){
          renderClaimPinStep('PIN does not match. Try again.');
          return;
        }
      }
      /* Sign in for the session, close claim modal, open edit. */
      if(window.hohYourHallSetSession) window.hohYourHallSetSession(em, pin);
      window.hohCloseClaim();
      setTimeout(function(){
        if(typeof window.hohOpenEditForm === 'function') window.hohOpenEditForm(c.id);
      }, 120);
    } catch(e){
      console.error('[Claim]', e);
      renderClaimPinStep('Something went wrong. Try again in a moment.');
    }
  };

  document.addEventListener('keydown', function(e){
    var m = document.getElementById('hoh-claim-modal');
    if(!m || m.style.display === 'none') return;
    if(e.key === 'Escape') window.hohCloseClaim();
  });

  /* ============ YOUR HALL TOGGLE BUTTON ============
     The hall tabs ("Fresh Faces" / "Your Hall") were retired. Now there
     is a single toggle button at the top of the page header that flips
     between Home state and Your Hall state. The button has THREE visual
     states:

       LOCKED  - Tinder not done today. Click opens Tinder so the user
                 can complete the gate immediately.
       UNLOCKED- Tinder done. Click switches into Your Hall state.
       ACTIVE  - Currently viewing Your Hall. Click returns to Home.

     The unlock check reads localStorage 'hoh-daily-tinder-YYYY-MM-DD'
     (set by Tinder endSession) AND the cached server flag from
     window.hohTicketState. Either source unlocks. */

  function todayYmd(){
    var d = new Date();
    return d.toISOString().slice(0,10);
  }
  function isTinderDoneToday(){
    /* Require an authenticated session before reading completion state.
       Anonymous Tinder runs don't earn server-side tickets, so unlocking
       Your Hall for them would be misleading: clicking it would just
       show the sign-in modal anyway. Gate at the source. */
    /* Your Hall is gated by SIGN-IN ONLY. Anyone with a stored token
       (i.e. has submitted a hero or claimed one) can access. The Tinder
       quest is no longer required. */
    return !!getStoredToken();
  }
  function isInYourHall(){
    return document.body.classList.contains('hoh-page-mode-yours');
  }

  /* Mount the Name's Hall TAB into #hoh-yh-tab-mount. Lives in JS so
     the section file stays under Shopify's 256 KB limit. The tab
     mirrors the visual style of the DM's Satchel and Win a Free
     Portrait blocks but with a right-pointing arrow (it's a navigate
     action, not an expand action). */
  function buildYhTabHtml(){
    /* Reuses the EXACT same inner classes as the Spotlight tab
       (.hoh-spotlight-bar / -left / -icon / -text / -title / -sub
       and .hoh-spotlight-arrow) so the layout — padding, gap, icon
       width, text positioning, arrow position, height — is
       guaranteed identical. The outer .hoh-yh-tab handles state
       (locked/unlocked/active) and color overrides via modifier
       classes targeting the spotlight-* children. */
    return '<div class="hoh-yh-tab" id="hoh-yh-tab" onclick="hohYhToggleClick()" role="button" tabindex="0" ' +
      'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohYhToggleClick();}">' +
      '<div class="hoh-spotlight-bar">' +
        '<div class="hoh-spotlight-bar-left">' +
          /* R732: a fan of three hero cards, the thing you actually collect
             here. Filled silhouettes so the mark keeps its mass at 28px; the
             front card's portrait is punched out with evenodd, not painted
             over in a background colour. The back cards carry the
             hoh-yh-fan classes that hoh-tools.css rotates and spreads. */
          '<span class="hoh-spotlight-bar-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 28 28" width="28" height="28" fill="none">' +
              '<rect class="hoh-yh-fan hoh-yh-fan-l" x="8.5" y="6.5" width="11" height="16" rx="1.6" fill="currentColor" opacity="0.42"/>' +
              '<rect class="hoh-yh-fan hoh-yh-fan-r" x="8.5" y="6.5" width="11" height="16" rx="1.6" fill="currentColor" opacity="0.42"/>' +
              '<path fill="currentColor" fill-rule="evenodd" d="M10.1 6.5h7.8a1.6 1.6 0 0 1 1.6 1.6v12.8a1.6 1.6 0 0 1-1.6 1.6h-7.8a1.6 1.6 0 0 1-1.6-1.6V8.1a1.6 1.6 0 0 1 1.6-1.6Zm3.9 3.3a2.3 2.3 0 1 0 0 4.6 2.3 2.3 0 0 0 0-4.6Zm-3.7 10.1c0-2.8 1.7-4.1 3.7-4.1s3.7 1.3 3.7 4.1Z"/>' +
            '</svg>' +
          '</span>' +
          '<div class="hoh-spotlight-bar-text">' +
            '<span class="hoh-spotlight-bar-title" id="hoh-yh-tab-title">Your Hero Collection</span>' +
            '<span class="hoh-spotlight-bar-sub" id="hoh-yh-tab-sub">Collect heroes, build your party, and earn rewards.</span>' +
          '</div>' +
        '</div>' +
        '<svg class="hoh-spotlight-arrow hoh-yh-tab-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">' +
          '<path d="M5 3L9 7L5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</div>' +
    '</div>';
  }
  /* BINDER-01 (Jan, 2026-10-23: "There should only be three tabs now"). When the Hall's markup carries the static DM
     Tools door (#hoh-dmt[data-static]), that door is the third of three and this file only wires it: its jade frame
     (bgwStyles) and its open/close (dmtToggle, which draws the tiles on first open). The mount stays empty: no Your Hero
     Collection tab, no Enter the Battlegrounds row. */
  function staticDmt(){
    var door = document.getElementById('hoh-dmt');
    return door && door.hasAttribute('data-static') ? door : null;
  }
  function wireStaticDmt(door){
    if(door._wired) return;
    door._wired = true;
    bgwStyles();
    var bar = door.querySelector('#hoh-dmt-bar');
    if(!bar) return;
    bar.addEventListener('click', function(e){ if(e.target.closest && e.target.closest('#hoh-dmt-body')) return; e.preventDefault(); dmtToggle(); });
    bar.addEventListener('keydown', function(e){
      if(e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      dmtToggle();
    });
  }
  /* the binder replaces Your Hall: every door that opened the personal page opens the binder instead */
  function binderInstead(){
    if(!staticDmt() || !window.FFBinder || typeof window.FFBinder.door !== 'function') return false;
    document.body.classList.remove('hoh-page-mode-yours');
    try { if(location.hash === '#my-hall') history.replaceState(null, '', location.pathname + location.search); } catch(_){}
    window.FFBinder.door();
    return true;
  }
  function ensureYhTab(){
    var mount = document.getElementById('hoh-yh-tab-mount');
    if(!mount) return;
    var sd = staticDmt();
    if(sd){ wireStaticDmt(sd); if(mount.firstChild) mount.innerHTML = ''; return; }
    /* R796 opening: while the world is open to this player (or for an admin) the
       row is "Enter the Battlegrounds" with the DM Tools door above it (Jan,
       2026-09-27); otherwise it is today's tab, byte for byte. A swap after the
       first paint eases in. */
    var want = bgwEntryOn() ? 'bgw' : 'tab';
    var have = mount.querySelector('.hoh-bgw-entry') ? 'bgw' : (mount.querySelector('.hoh-yh-tab') ? 'tab' : '');
    if(have === want){
      if(want === 'bgw'){ bgwSyncEntry(); bgwTools(mount); }
      return;
    }
    if(want === 'bgw'){
      bgwStyles();
      mount.innerHTML = bgwEntryHtml();
      bgwTools(mount);
    } else {
      mount.innerHTML = buildYhTabHtml();
    }
    if(have){
      /* every row the swap brings eases in: the DM Tools door and the Battlegrounds row, or today's tab */
      Array.prototype.slice.call(mount.children).forEach(function(row){
        row.classList.remove('hoh-bgw-in'); void row.offsetWidth; row.classList.add('hoh-bgw-in');
        /* the entrance plays once; left on, it would outrank a later arrival animation */
        var done = function(){ try { row.classList.remove('hoh-bgw-in'); } catch(_){} };
        row.addEventListener('animationend', function(ev){ if(ev.animationName === 'hohBgwIn' || ev.animationName === 'hohBgwFade') done(); }, { once: true });
        setTimeout(done, 700);
      });
    }
  }

  /* ============ R796 OPENING (2026-09-23, Jan's go): ENTER THE BATTLEGROUNDS ============
     Jan's ruling D1: the Battlegrounds is its own page with a clean transition from the
     Hall, and the profile-style game in Your Hall ("Jan's Hall": the card collection, the
     Events battle scene, the Shop's packs and tickets) goes away. Only the server knows
     whether the world is open to this player, so the Hall asks the game page's own home
     door, hoh_bgw_home_state (account-checked; it answers open, and admin for Jan's
     account). That lets this file ship before or after Jan opens the world:
       closed, a player   nothing changes; the Hall is exactly today's.
       open, or an admin  the hub's "Your Hero Collection" row becomes "Enter the
                          Battlegrounds": a real link to /pages/battlegrounds, played
                          through a short gate that ends on the game page's own first
                          frame. The DM Tools drawer moves up into the hub under it, and
                          every door into Your Hall (the tab, the Shop and wallet pills,
                          Explore's battle door, #my-hall) comes back to that row.
     A signed-out visitor has no account to ask with, so the hub keeps its signed-out view,
     as it does for the Your Hall row today; the game page asks them to bring a hero. A run
     that is live at launch is finished on the game page (its Unlimited card says Continue,
     locked or not). A battle already started in this page view keeps the old Your Hall
     until the next load, so nothing is torn down mid-fight.
     The last answer is kept on the device (hoh-bgw-open) so a returning player gets the
     right row at once; the door is asked again at most every 10 minutes, and whenever the
     account changes. */
  var BGW_PATH = '/pages/battlegrounds';
  var BGW_KEY = 'hoh-bgw-open';
  var BGW_TTL = 10 * 60 * 1000;
  var BGW_KEEP = 24 * 60 * 60 * 1000;
  var bgw = { open: false, admin: false, acct: '', live: null, at: 0, asked: '', asking: false, tries: 0 };
  function bgwAcct(email){
    var s = String(email || '').trim().toLowerCase(), h = 2166136261;
    for(var i = 0; i < s.length; i++){ h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return s ? (h >>> 0).toString(16) : '';
  }
  try {
    var _bgwSaved = JSON.parse(localStorage.getItem(BGW_KEY) || 'null');
    if(_bgwSaved && typeof _bgwSaved.at === 'number' && Date.now() - _bgwSaved.at < BGW_KEEP){
      bgw.open = _bgwSaved.open === true;
      bgw.admin = _bgwSaved.admin === true;
      bgw.acct = String(_bgwSaved.acct || '');
    }
  } catch(_){}
  function bgwEntryOn(){
    var t = getStoredToken();
    if(!t) return false;
    return bgw.open || (bgw.admin && bgw.acct === bgwAcct(t.email));
  }
  /* a battle started in this page view keeps its old home until the next load */
  function bgwRetired(){ return bgwEntryOn() && !document.getElementById('hoh-contract'); }
  function bgwSub(){
    if(bgw.live) return 'Your last run is still going. Finish it there.';
    if(!bgw.open) return 'Closed to players for now. You can play as the admin.';
    return 'Take your heroes into the levels. It opens on its own page.';
  }
  var BGW_SWORDS = '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
    '<path d="M14.5 3.5H20V9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
    '<path d="M9.5 3.5H4V9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg>';
  function bgwEntryHtml(){
    return '<a class="hoh-bgw-entry" id="hoh-bgw-entry" href="' + BGW_PATH + '" data-bgw-entry>' +
      '<span class="hoh-yh-evtab-embers" aria-hidden="true"></span>' +
      '<span class="hoh-spotlight-bar">' +
        '<span class="hoh-spotlight-bar-left">' +
          '<span class="hoh-spotlight-bar-icon hoh-bgw-entry-mark" aria-hidden="true">' + BGW_SWORDS + '</span>' +
          '<span class="hoh-spotlight-bar-text">' +
            '<span class="hoh-spotlight-bar-title">Enter the Battlegrounds</span>' +
            '<span class="hoh-spotlight-bar-sub" id="hoh-bgw-entry-sub">' + esc(bgwSub()) + '</span>' +
          '</span>' +
        '</span>' +
        '<svg class="hoh-spotlight-arrow hoh-bgw-entry-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">' +
          '<path d="M5 3L9 7L5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
    '</a>';
  }
  function bgwSyncEntry(){
    var sub = document.getElementById('hoh-bgw-entry-sub');
    if(sub && sub.textContent !== bgwSub()) sub.textContent = bgwSub();
  }
  /* ============ THE DM TOOLS DOOR (Jan, 2026-09-27) ============
     "design a tab for the DM tools so its inside a colorful tab that fits the aesthetics of
     the other 4 tabs ... put it right above battlegrounds". The drawer that sat under the
     Battlegrounds row is a hub door of its own now: the hub's envelope (R732/R736: every door
     is told apart by its frame and the light its icon throws) in jade, which no other door
     wears, with a d20 for its mark. It opens in place like Win a Free Portrait and Explore the
     Hall (chevron down, the row grows to show the tools, height and fade in 280ms, instant
     under reduced motion) and starts closed on every load. The tiles are the drawer's own
     (hohHallRenderToolsPanel, bare), so every id and handler is unchanged; #hoh-bgw-tools
     still holds them. */
  var DMT_D20 = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
    '<path d="M12 2.5 20.23 7.25v9.5L12 21.5l-8.23-4.75v-9.5Z"/><path d="M12 6.13 17.09 14.94H6.91Z"/>' +
    '<path stroke-width="1.3" d="M12 2.5v3.63M12 6.13 3.77 7.25M12 6.13l8.23 1.12M17.09 14.94l3.14-7.69M17.09 14.94l3.14 1.81M17.09 14.94 12 21.5M6.91 14.94 3.77 7.25M6.91 14.94l-3.14 1.81M6.91 14.94 12 21.5"/></svg>';
  function dmtDoorHtml(){
    return '<div class="hoh-dmt" id="hoh-dmt" role="region" aria-label="DM Tools">' +
      '<div class="hoh-spotlight-bar hoh-dmt-bar" id="hoh-dmt-bar" role="button" tabindex="0" aria-expanded="false" aria-controls="hoh-dmt-body">' +
        '<div class="hoh-spotlight-bar-left">' +
          '<span class="hoh-spotlight-bar-icon hoh-dmt-icon" aria-hidden="true">' + DMT_D20 + '</span>' +
          '<div class="hoh-spotlight-bar-text">' +
            '<span class="hoh-spotlight-bar-title">DM Tools</span>' +
            '<span class="hoh-spotlight-bar-sub">Turn Hall heroes into NPCs, scenes and taverns for your next session.</span>' +
          '</div>' +
        '</div>' +
        '<span class="hoh-spotlight-arrow" aria-hidden="true"><svg viewBox="0 0 14 14" width="14" height="14" fill="none" focusable="false">' +
          '<path d="M3 5L7 9L11 5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      '</div>' +
      '<div class="hoh-dmt-body" id="hoh-dmt-body" hidden>' +
        '<div class="hoh-dmt-inner"><div class="hoh-bgw-tools" id="hoh-bgw-tools"></div></div>' +
      '</div>' +
    '</div>';
  }
  var dmtT = 0, dmtRollT = 0;
  function dmtToggle(){
    var door = document.getElementById('hoh-dmt'), bar = document.getElementById('hoh-dmt-bar'), body = document.getElementById('hoh-dmt-body');
    if(!door || !bar || !body) return;
    var open = !door.classList.contains('is-open');
    door.classList.toggle('is-open', open);
    bar.setAttribute('aria-expanded', open ? 'true' : 'false');
    clearTimeout(dmtT);
    var still = bgwReduce();
    if(open){
      var tools = document.getElementById('hoh-bgw-tools');
      if(tools && !tools.firstChild && typeof window.hohHallRenderToolsPanel === 'function'){
        try { window.hohHallRenderToolsPanel(tools, { bare: true }); } catch(_){}
      }
      /* from where it stands, so a tap mid-close turns it round without a jump; the start
         values go on before it shows and is measured, so the fade starts from them */
      var from = body.hidden ? 0 : body.getBoundingClientRect().height;
      if(still){ body.hidden = false; body.style.height = ''; body.style.opacity = ''; return; }
      body.style.height = from + 'px'; body.style.opacity = from ? '1' : '0';
      body.hidden = false;
      var to = body.scrollHeight;
      body.style.height = to + 'px'; body.style.opacity = '1';
      dmtT = setTimeout(function(){ body.style.height = ''; body.style.opacity = ''; }, 320);
      /* the die tumbles once as the door opens */
      door.classList.remove('is-rolling'); void door.offsetWidth; door.classList.add('is-rolling');
      clearTimeout(dmtRollT);
      dmtRollT = setTimeout(function(){ door.classList.remove('is-rolling'); }, 650);
    } else {
      door.classList.remove('is-rolling');
      if(still || body.hidden){ body.hidden = true; body.style.height = ''; body.style.opacity = ''; return; }
      body.style.height = body.getBoundingClientRect().height + 'px'; body.style.opacity = '1';
      void body.offsetHeight;
      body.style.height = '0px'; body.style.opacity = '0';
      dmtT = setTimeout(function(){ body.hidden = true; body.style.height = ''; body.style.opacity = ''; }, 300);
    }
  }
  function dmtDoor(){
    var door = document.getElementById('hoh-dmt');
    if(door) return door;
    var box = document.createElement('div');
    box.innerHTML = dmtDoorHtml();
    door = box.firstChild;
    var bar = door.querySelector('#hoh-dmt-bar');
    bar.addEventListener('click', function(e){ e.preventDefault(); dmtToggle(); });
    bar.addEventListener('keydown', function(e){
      if(e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      dmtToggle();
    });
    return door;
  }
  /* the DM Tools door, one node, right above the Battlegrounds row */
  function bgwTools(mount){
    var door = dmtDoor();
    var entry = mount.querySelector('.hoh-bgw-entry');
    if(door.parentNode !== mount || door.nextElementSibling !== entry) mount.insertBefore(door, entry);
    var tools = document.getElementById('hoh-bgw-tools');
    if(tools && !tools.firstChild && typeof window.hohHallRenderToolsPanel === 'function'){
      try { window.hohHallRenderToolsPanel(tools, { bare: true }); } catch(_){}
    }
  }
  function bgwStyles(){
    if(document.getElementById('hoh-bgw-style')) return;
    var D = '#hoh-three-doors-body #hoh-bgw-entry', T = '#hoh-three-doors-body #hoh-dmt';
    var css =
      /* the row is a fourth hub door (R732/R736: one envelope, told apart by its frame and the light
         its icon throws), in the Battlegrounds' ember colours, with the tab's rising embers (R438) */
      D + '{--entry-border:linear-gradient(115deg,#ffb45e,#8a3a12);--entry-fill:linear-gradient(115deg,#2b1c15,#17141f);' +
        '--entry-hover-fill:linear-gradient(115deg,#3a2619,#211c2a);--entry-glow:#ff8a1f2e;' +
        'display:block;position:relative;isolation:isolate;margin:12px 0;padding:0;border:1px solid transparent;border-radius:12px;' +
        'background:var(--entry-fill) padding-box,var(--entry-border) border-box;box-shadow:0 4px 18px #00000026;' +
        'transition:box-shadow .2s ease,transform .2s ease;text-decoration:none;color:#f5e2c4;cursor:pointer;-webkit-tap-highlight-color:transparent}' +
      D + '>.hoh-spotlight-bar{box-sizing:border-box;min-height:82px;padding:15px 22px;gap:16px}' +
      D + ' .hoh-spotlight-bar-icon{color:#ffb45e;filter:drop-shadow(0 0 8px #ff8a1f59)}' +
      D + ' .hoh-spotlight-bar-icon svg{display:block;width:28px;height:28px}' +
      D + ' .hoh-spotlight-bar-icon::before{background:radial-gradient(ellipse,#ff8a1f47,transparent 68%);animation-delay:-1.2s}' +
      D + ' .hoh-spotlight-bar-icon::after{animation-delay:-3.4s;box-shadow:-17px -7px 0 #ffb45e,14px -14px 0 -.5px #ffd694,18px 10px 0 -.5px #ff8a1f}' +
      D + ' .hoh-spotlight-arrow{transition:transform .25s ease}' +
      D + ':focus-visible{outline:2px solid #ffc27a;outline-offset:-4px;border-radius:12px}' +
      '@media(hover:hover){' + D + ':hover{background:var(--entry-hover-fill) padding-box,var(--entry-border) border-box;border-color:transparent;' +
        'box-shadow:0 4px 22px var(--entry-glow),inset 0 0 24px #ffffff05;transform:translateY(-1px)}' + D + ':hover .hoh-spotlight-arrow{transform:translateX(3px)}}' +
      '@media(max-width:768px){' + D + '>.hoh-spotlight-bar{min-height:0;padding:13px 14px;gap:12px}}' +
      '@media(prefers-reduced-motion:reduce){' + D + '{transition:none;transform:none}}' +
      '.hoh-bgw-in{animation:hohBgwIn .28s cubic-bezier(.2,.8,.2,1) both}' +
      '@keyframes hohBgwIn{from{opacity:0;transform:translateY(6px) scale(.985)}to{opacity:1;transform:none}}' +
      /* the DM Tools door (Jan, 2026-09-27): the same envelope in jade, a colour no other door
         wears, and a d20 that throws a green light; the tools open inside it */
      T + '{--entry-border:linear-gradient(115deg,#6ff0bd,#169c76);--entry-fill:linear-gradient(115deg,#12291f,#16171f);' +
        '--entry-hover-fill:linear-gradient(115deg,#193628,#1d1f29);--entry-glow:#3ddc972e;' +
        'display:block;position:relative;isolation:isolate;margin:12px 0;padding:0;border:1px solid transparent;border-radius:12px;' +
        'background:var(--entry-fill) padding-box,var(--entry-border) border-box;box-shadow:0 4px 18px #00000026;' +
        'transition:box-shadow .2s ease,transform .2s ease;color:#e8e0d0}' +
      T + '>.hoh-spotlight-bar{box-sizing:border-box;min-height:82px;padding:15px 22px;gap:16px;cursor:pointer;-webkit-tap-highlight-color:transparent}' +
      T + ' .hoh-spotlight-bar-left{flex:1 1 auto}' +
      T + ' .hoh-spotlight-bar-icon{color:#7cf2c2;filter:drop-shadow(0 0 8px #3ddc9759)}' +
      T + ' .hoh-spotlight-bar-icon svg{display:block;width:28px;height:28px;transform-origin:50% 50%;animation:hohDmtIdle 6.2s ease-in-out infinite}' +
      T + ' .hoh-spotlight-bar-icon::before{background:radial-gradient(ellipse,#3ddc9747,transparent 68%);animation-delay:-2.2s}' +
      T + ' .hoh-spotlight-bar-icon::after{animation-delay:-5.2s;box-shadow:-17px -7px 0 #7cf2c2,14px -14px 0 -.5px #c9ffe8,18px 10px 0 -.5px #3ddc97}' +
      T + ' .hoh-spotlight-arrow{color:#9ef3cf;transition:transform .25s ease}' +
      T + '.is-open .hoh-spotlight-arrow{transform:rotate(180deg)}' +
      T + '.is-rolling .hoh-spotlight-bar-icon svg{animation:hohDmtRoll .6s cubic-bezier(.2,.8,.2,1) both}' +
      T + '>.hoh-spotlight-bar:focus-visible{outline:2px solid #8ff5c8;outline-offset:-4px;border-radius:12px}' +
      '@media(hover:hover){' + T + ':hover{background:var(--entry-hover-fill) padding-box,var(--entry-border) border-box;' +
        'box-shadow:0 4px 22px var(--entry-glow),inset 0 0 24px #ffffff05}' + T + ':not(.is-open):hover{transform:translateY(-1px)}}' +
      T + ' .hoh-dmt-body{overflow:hidden;transition:height .28s cubic-bezier(.2,.8,.2,1),opacity .22s ease}' +
      T + ' .hoh-dmt-body[hidden]{display:none}' +
      T + ' .hoh-dmt-inner{padding:4px 22px 22px}' +
      T + ' .hoh-pouch-heroes{padding-top:0;margin-bottom:0}' +
      T + ' .hoh-pouch-utils{margin-top:14px}' +
      T + ' .hoh-pouch-util{box-sizing:border-box;min-height:44px}' +
      '@keyframes hohDmtIdle{0%,100%{transform:rotate(-7deg) translateY(.5px)}50%{transform:rotate(7deg) translateY(-2px)}}' +
      '@keyframes hohDmtRoll{0%{transform:rotate(0) scale(1)}55%{transform:rotate(300deg) scale(.86)}100%{transform:rotate(360deg) scale(1)}}' +
      '@media(max-width:768px){' + T + '>.hoh-spotlight-bar{min-height:0;padding:13px 14px;gap:12px}' + T + ' .hoh-dmt-inner{padding:2px 12px 14px}}' +
      '@media(prefers-reduced-motion:reduce){' + T + '{transition:none;transform:none}' + T + ' .hoh-dmt-body{transition:none}' +
        T + ' .hoh-spotlight-bar-icon svg,' + T + '.is-rolling .hoh-spotlight-bar-icon svg{animation:none;transform:none}}' +
      /* the gate: fades in over the Hall and ends on the game page's first frame (its boot panel) */
      '.hoh-bgw-gate{position:fixed;inset:0;z-index:2147483000;box-sizing:border-box;min-height:100svh;display:flex;align-items:center;justify-content:center;' +
        'padding:24px 16px;overflow:hidden;text-align:center;color:#EEF1F6;pointer-events:auto;' +
        'background:radial-gradient(50% 40% at 50% 42%,rgba(217,178,106,.07),rgba(217,178,106,0) 70%),#0B0C10;' +
        'animation:hohBgwGateIn .26s cubic-bezier(.2,.8,.2,1) both}' +
      '.hoh-bgw-gate-in{position:relative;display:flex;flex-direction:column;align-items:center;gap:8px;width:100%;' +
        'animation:hohBgwGateRise .3s cubic-bezier(.2,.8,.2,1) both}' +
      '.hoh-bgw-gate-mark{width:100%;margin:0;font-family:Georgia,"Times New Roman",serif;font-weight:500;font-size:32px;line-height:38px;letter-spacing:.005em;color:#EEF1F6}' +
      '.hoh-bgw-gate-line{width:100%;margin:0;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#9AA3B2;font-size:13px;line-height:18px;font-weight:500}' +
      '.hoh-bgw-gate-bar{display:block;width:120px;height:2px;margin-top:14px;border-radius:1px;background:rgba(201,208,218,.12);overflow:hidden}' +
      '.hoh-bgw-gate-bar span{display:block;width:40%;height:100%;border-radius:1px;background:#D9B26A;animation:hohBgwBar 1.1s ease-in-out infinite}' +
      '.hoh-bgw-gate-embers{position:absolute;left:0;right:0;bottom:0;height:55%;pointer-events:none;opacity:.45;background-repeat:repeat;background-size:100% 140px;' +
        'background-image:radial-gradient(1.6px 1.6px at 18% 90%,rgba(255,176,74,.9),transparent 62%),radial-gradient(1.2px 1.2px at 52% 96%,rgba(255,120,40,.85),transparent 62%),' +
        'radial-gradient(1.4px 1.4px at 81% 88%,rgba(255,214,148,.8),transparent 62%);' +
        '-webkit-mask-image:linear-gradient(to top,#000 0 25%,transparent 100%);mask-image:linear-gradient(to top,#000 0 25%,transparent 100%);' +
        'animation:hohBgwRise 4.2s linear infinite}' +
      '@keyframes hohBgwGateIn{from{opacity:0}to{opacity:1}}' +
      '@keyframes hohBgwGateRise{from{opacity:0;transform:translateY(10px) scale(.985)}to{opacity:1;transform:none}}' +
      '@keyframes hohBgwBar{0%{transform:translateX(-110%)}100%{transform:translateX(260%)}}' +
      '@keyframes hohBgwRise{from{background-position:0 0}to{background-position:0 -140px}}' +
      '@keyframes hohBgwFade{from{opacity:0}to{opacity:1}}' +
      /* reduced motion: opacity only, no particles, a still bar */
      '@media (prefers-reduced-motion:reduce){.hoh-bgw-in,.hoh-bgw-gate{animation:hohBgwFade .16s ease both}.hoh-bgw-gate-in{animation:none}' +
        '.hoh-bgw-gate-embers{display:none}.hoh-bgw-gate-bar span{animation:none;width:100%;opacity:.5}}';
    var st = document.createElement('style');
    st.id = 'hoh-bgw-style';
    st.textContent = css;
    /* after the Hall's own stylesheets (they load in the section's body), so a tie goes to these rules */
    (document.body || document.head || document.documentElement).appendChild(st);
  }
  function bgwReduce(){ try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch(_){ return false; } }
  function bgwGate(href){
    bgwStyles();
    var old = document.getElementById('hoh-bgw-gate');
    if(old && old.parentNode) old.parentNode.removeChild(old);
    var still = bgwReduce();
    var g = document.createElement('div');
    g.id = 'hoh-bgw-gate';
    g.className = 'hoh-bgw-gate';
    g.setAttribute('role', 'status');
    g.setAttribute('aria-live', 'polite');
    g.innerHTML = '<span class="hoh-bgw-gate-embers" aria-hidden="true"></span>' +
      '<div class="hoh-bgw-gate-in"><p class="hoh-bgw-gate-mark">Battlegrounds</p>' +
      '<p class="hoh-bgw-gate-line">Opening the Battlegrounds</p>' +
      '<span class="hoh-bgw-gate-bar" aria-hidden="true"><span></span></span></div>';
    document.body.appendChild(g);
    /* the page is asked for once the gate covers the Hall, so a fast answer never
       freezes the fade half way */
    setTimeout(function(){
      try { window.location.assign(href); } catch(_){ window.location.href = href; }
    }, still ? 170 : 290);
  }
  function bgwPrefetch(){
    if(bgwPrefetch.done) return;
    bgwPrefetch.done = true;
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch'; l.as = 'document'; l.href = BGW_PATH;
      document.head.appendChild(l);
    } catch(_){}
  }
  function bgwEntryOf(e){ var t = e && e.target; return (t && t.closest) ? t.closest('a[data-bgw-entry]') : null; }
  document.addEventListener('click', function(e){
    var a = bgwEntryOf(e);
    if(!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if(a.target && a.target !== '_self') return;
    e.preventDefault();
    bgwGate(a.getAttribute('href') || BGW_PATH);
  });
  document.addEventListener('pointerover', function(e){ if(bgwEntryOf(e)) bgwPrefetch(); }, { passive: true });
  document.addEventListener('touchstart', function(e){ if(bgwEntryOf(e)) bgwPrefetch(); }, { passive: true });
  document.addEventListener('focusin', function(e){ if(bgwEntryOf(e)) bgwPrefetch(); });
  /* Back from the game page restores this page from the back/forward cache with the
     gate still up; take it down */
  window.addEventListener('pageshow', function(e){
    if(!e.persisted) return;
    var g = document.getElementById('hoh-bgw-gate');
    if(g && g.parentNode) g.parentNode.removeChild(g);
  });
  /* ask the world's state for the signed-in account (at most every BGW_TTL, and again
     for a new account); keep the last answer when the door or the network fails */
  function bgwAsk(){
    var t = getStoredToken();
    if(!t || bgw.asking) return;
    var acct = bgwAcct(t.email);
    if(bgw.asked === acct && Date.now() - bgw.at < BGW_TTL) return;
    var sb = window.hohSupabaseClient;
    if(!sb || typeof sb.rpc !== 'function'){
      /* the section core makes the client once supabase-js is in */
      if(bgw.tries++ < 30) setTimeout(bgwAsk, 500);
      return;
    }
    bgw.asking = true;
    var settled = false;
    var stop = function(){ settled = true; bgw.asking = false; };
    var timer = setTimeout(function(){ if(!settled) stop(); }, 9000);
    var p = null;
    try { p = sb.rpc('hoh_bgw_home_state', { p_email: t.email, p_pin: t.pin }); } catch(_){}
    if(!p || typeof p.then !== 'function'){ clearTimeout(timer); stop(); return; }
    p.then(function(res){
      if(settled) return;
      clearTimeout(timer); stop();
      if(!hallAccountCurrent(t)) return;
      var d = (res && !res.error) ? res.data : null;
      if(!d || typeof d !== 'object') return;
      bgw.at = Date.now(); bgw.asked = acct;
      if(d.ok === true){
        bgw.open = d.open === true;
        bgw.admin = d.admin === true;
        bgw.acct = acct;
        bgw.live = (d.live_run && typeof d.live_run === 'object') ? d.live_run : null;
      } else {
        /* this PIN no longer signs in: no admin view; the world's state stays as it was */
        bgw.admin = false; bgw.live = null;
      }
      try { localStorage.setItem(BGW_KEY, JSON.stringify({ open: bgw.open, admin: bgw.admin, acct: bgw.acct, at: bgw.at })); } catch(_){}
      if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
    }, function(){ if(!settled){ clearTimeout(timer); stop(); } });
  }
  /* every door into Your Hall comes here while the world is open: back to the main Hall,
     where the row is, brought into view */
  function bgwBackToHub(){
    try {
      if(document.body.classList.contains('hoh-page-mode-yours')){
        document.body.classList.remove('hoh-page-mode-yours');
        if(typeof window.hohYhSetHeaderModeHome === 'function') window.hohYhSetHeaderModeHome();
      }
      if(location.hash === '#my-hall'){ try { history.replaceState(null, '', location.pathname + location.search); } catch(_){} }
      if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
      var row = document.getElementById('hoh-bgw-entry');
      if(!row) return;
      var still = bgwReduce();
      requestAnimationFrame(function(){
        try { row.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' }); } catch(_){}
        setTimeout(function(){
          try {
            row.classList.remove('hoh-bgw-in', 'hoh-yh-tab-arrive'); void row.offsetWidth; row.classList.add('hoh-yh-tab-arrive');
            setTimeout(function(){ try { row.classList.remove('hoh-yh-tab-arrive'); } catch(_){} }, 1600);
          } catch(_){}
        }, still ? 0 : 380);
      });
    } catch(_){}
  }

  /* Render the Name's Hall tab state. Replaces the old single-button
     toggle: now we have a full-width tab block matching the visual
     style of DM's Satchel / Win a Free Portrait. Three states:
       Locked   — neutral copy + soft purple border + sub explains gating
       Unlocked — "[Name]'s Hall →" + personalized sub
       Active   — green tint + "Back to main hall" copy */
  window.hohRefreshYhToggle = function(){
    /* R423 (Jan): keep html.hoh-hall-signed-in TRUE TO THE TOKEN, live.
       The class was only ever set by the pre-paint detector in the section,
       so someone signing in mid-session (the new "Log in here" line, or the
       anon modal's sign-in button) kept the anon CSS until a full reload —
       the Your Hall tab stayed hidden and the login line stayed shown even
       though they were in. This runs on every toggle refresh, which every
       auth transition already calls, and it heals the other drift too: the
       pre-paint mirror still counts a 30-day session while getStoredToken
       honours 365, so a 100-day token painted anon until now. */
    try { document.documentElement.classList.toggle('hoh-hall-signed-in', !!getStoredToken()); } catch(_){}
    ensureYhTab();
    if(!staticDmt()) bgwAsk();   /* R796 opening: is the world open to this account (at most every 10 minutes); the three-door Hall has no Battlegrounds row to ask for */
    var tab = document.getElementById('hoh-yh-tab');
    if(!tab) return;
    var unlocked = isTinderDoneToday();
    var active = isInYourHall();
    tab.classList.remove('hoh-yh-tab-locked','hoh-yh-tab-unlocked','hoh-yh-tab-active');
    var titleEl = document.getElementById('hoh-yh-tab-title');
    var subEl = document.getElementById('hoh-yh-tab-sub');
    if(active){
      tab.classList.add('hoh-yh-tab-active');
      tab.setAttribute('aria-label', 'Back to the main hall');
      /* Arrow is the SVG chevron on the right (rotated 180deg in
         active state via CSS). No text-arrow — would be redundant. */
      if(titleEl) titleEl.textContent = 'Back to the main hall';
      if(subEl) subEl.textContent = 'Return to Fresh Faces and the full gallery.';
    } else if(unlocked){
      tab.classList.add('hoh-yh-tab-unlocked');
      var label = 'Your Hero Collection';
      tab.setAttribute('aria-label', 'Open Your Hero Collection');
      if(titleEl) titleEl.textContent = label;
      if(subEl) subEl.textContent = 'Collect heroes, build your party, and earn rewards.';
    } else {
      tab.classList.add('hoh-yh-tab-locked');
      tab.setAttribute('aria-label', 'Explore Your Hero Collection');
      if(titleEl) titleEl.textContent = 'Your Hero Collection';
      if(subEl) subEl.textContent = 'Sign in to collect heroes, build your party, and earn rewards.';
    }
  };

  /* R425 (Jan): "then show an animated thing of the personal hall tab
     showing up."
     WHY IT MATTERS, not just what it does: signing in from the Hall changes
     one visible thing on the page — a tab appears in a stack of other
     panels, well below where the eye happens to be. Without this the reward
     for signing in is a control the player has to go find, and a change
     nobody sees may as well not have happened.
     So the tab arrives on purpose: it is brought into view, it drops in with
     a glow, and it says who it belongs to. Everything is guarded — a missing
     tab, a page that moved on, or reduced-motion all degrade to "the tab is
     simply there", which is the pre-R425 behaviour and never worse. */
  window.hohYhCelebrateTab = function(){
    try {
      ensureYhTab();
      /* R796 opening: while the world is open the row that arrives is Enter the Battlegrounds */
      var tab = document.getElementById('hoh-bgw-entry') || document.getElementById('hoh-yh-tab');
      if(!tab) return;
      var toBgw = tab.id === 'hoh-bgw-entry';
      if(toBgw) tab.classList.remove('hoh-bgw-in');
      var reduce = false;
      try { reduce = matchMedia('(prefers-reduced-motion: reduce)').matches; } catch(_){}
      /* the toggle repaint names the tab after them, so run it FIRST and the
         animation lands on the finished label rather than on "Your Hall" */
      try { if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle(); } catch(_){}
      var r = tab.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var off = (r.top < 0 || r.bottom > vh - 8);
      if(off){
        try { tab.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); } catch(_){}
      }
      /* let the scroll settle before the entrance, or the two motions fight
         and the tab appears to jump */
      setTimeout(function(){
        try {
          if(!document.body.contains(tab)) return;
          tab.classList.remove('hoh-yh-tab-arrive');
          void tab.offsetWidth;                       /* restart the animation */
          tab.classList.add('hoh-yh-tab-arrive');
          setTimeout(function(){ try { tab.classList.remove('hoh-yh-tab-arrive'); } catch(_){} }, 2600);
          var name = '';
          try { name = (typeof window.hohYourHallDisplayName === 'function') ? (window.hohYourHallDisplayName() || '') : ''; } catch(_){}
          if(typeof window.hohShowHeartToast === 'function'){
            window.hohShowHeartToast(name ? ('Welcome back, ' + name) : 'You are signed in',
              toBgw ? 'Enter the Battlegrounds is just below.' : 'Your hall is open below — your heroes, your hearts, and your tickets.');
          }
        } catch(_){}
      }, off && !reduce ? 460 : 60);
    } catch(_){}
  };

  /* Swap the global FF Presents pill text + click target so it acts
     as a "Return to Main Hall" button while in Your Hall mode. The
     pill keeps its existing styling so the header reads identically
     in both modes — just one element changing role. */
  function setHeaderLabelMode(mode){
    var el = document.querySelector('.hoh-header-label');
    if(!el) return;
    if(mode === 'yours'){
      /* Save the full inner markup (the .hoh-header-label-text span,
         the .hoh-header-label-hover span, and the colored sub-spans
         inside each) so we can restore it cleanly when the user
         returns to the main hall. Replacing textContent of the inner
         text span loses its colored sub-spans — that\'s fine for the
         YH "← Return to Main Hall" label since it\'s plain. The hover
         span is hidden in YH mode via the .hoh-header-label-yh CSS
         rule, so we don\'t need to touch it here. */
      if(!el.dataset.hohOriginalHtml) el.dataset.hohOriginalHtml = el.innerHTML;
      el.dataset.hohOriginalHref = el.dataset.hohOriginalHref || (el.getAttribute('href') || '');
      var inner = el.querySelector('.hoh-header-label-text');
      if(inner) inner.textContent = '← Return to Main Hall';
      else el.innerHTML = '<span class="hoh-header-label-text">← Return to Main Hall</span>';
      el.setAttribute('aria-label', 'Return to the main hall');
      el.removeAttribute('href');
      el.style.cursor = 'pointer';
      el.onclick = function(ev){ ev.preventDefault(); window.hohYhToggleClick(); };
      el.classList.add('hoh-header-label-yh');
    } else {
      /* Restore original innerHTML — brings back both the text and
         hover spans plus their colored sub-spans intact. */
      if(el.dataset.hohOriginalHtml) el.innerHTML = el.dataset.hohOriginalHtml;
      if(el.dataset.hohOriginalHref) el.setAttribute('href', el.dataset.hohOriginalHref);
      el.setAttribute('aria-label', 'Visit FramedFantasy');
      el.onclick = null;
      el.classList.remove('hoh-header-label-yh');
    }
  }
  window.hohYhSetHeaderModeYours = function(){ setHeaderLabelMode('yours'); };
  window.hohYhSetHeaderModeHome = function(){ setHeaderLabelMode('home'); };

  /* Click handler for the toggle. Branches by current state. */
  window.hohYhToggleClick = function(){
    if(binderInstead()) return;
    var unlocked = isTinderDoneToday();
    var active = isInYourHall();
    if(active){
      /* Return to Home state. */
      document.body.classList.remove('hoh-page-mode-yours');
      setHeaderLabelMode('home');
      /* v20 (Jan): the page REMEMBERS which hall you were in. Leaving
         clears the mark; entering writes it; a refresh reads it back. */
      try{ history.replaceState(null, '', location.pathname + location.search); }catch(_){ }
      window.hohRefreshYhToggle();
      /* Land the user on the Fresh Faces rail when they come back to
         the main hall — that's where the freshly-submitted heroes
         live, and seeing new faces first is the most rewarding entry
         point. Falls back to scrolling to the gallery, then to the
         page top if neither section is mounted. Wrapped in rAF so
         the layout settles after .hoh-page-mode-yours is removed
         before we measure the target's position. */
      requestAnimationFrame(function(){
        var fresh = document.getElementById('hoh-fresh-faces');
        var gallery = document.getElementById('hoh-gallery');
        var target = (fresh && fresh.offsetParent !== null) ? fresh : gallery;
        try {
          if(target && typeof target.scrollIntoView === 'function'){
            target.scrollIntoView({ behavior:'smooth', block:'start' });
          } else {
            window.scrollTo({ top:0, behavior:'smooth' });
          }
        } catch(_){
          if(target) target.scrollIntoView();
          else window.scrollTo(0, 0);
        }
      });
      return;
    }
    if(!unlocked){
      /* Not signed in. Show a dedicated explainer lightbox that
         walks the user through the "submit a hero → claim your hall"
         flow. Better than dropping them into the daily quests panel
         (which reads "0/4" out of context for a brand new user). */
      window.hohOpenAnonHallIntro();
      return;
    }
    /* Unlocked: switch into Your Hall state. */
    document.body.classList.add('hoh-page-mode-yours');
    setHeaderLabelMode('yours');
    try{ history.replaceState(null, '', location.pathname + location.search + '#my-hall'); }catch(_){ }
    if(typeof window.hohOpenYourHall === 'function') window.hohOpenYourHall();
    window.hohRefreshYhToggle();
  };

  /* v20 (Jan): STATE SURVIVES THE REFRESH. If the URL carries the my-hall
     mark and the account is unlocked, re-enter the personal hall on load
     instead of dumping the player back at the main hall. Deferred a beat so
     the toggle, header and panels have all mounted first. */
  try {
    if (location.hash === '#my-hall') {
      setTimeout(function(){
        try {
          if (staticDmt()) return;   /* BINDER-01: the binder answers #my-hall (ff-binder.js deepLink) */
          if (isTinderDoneToday() && !isInYourHall()) {
            document.body.classList.add('hoh-page-mode-yours');
            setHeaderLabelMode('yours');
            if (typeof window.hohOpenYourHall === 'function') window.hohOpenYourHall();
            if (typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
            window.scrollTo(0, 0);
          }
        } catch(_){ }
      }, 350);
    }
  } catch(_){ }
  /* The header's account menu (ff-account-menu.js) sends a signed-out visitor on another page here with #sign-in: the
     sign-in opens over the Hall. Signed in already (another tab got there first), the mark is just cleared. */
  try {
    if (location.hash === '#sign-in') {
      history.replaceState(null, '', location.pathname + location.search);
      if (!getStoredToken()) setTimeout(function(){ try { window.hohSignInToYourHall(); } catch(_){ } }, 350);
    }
  } catch(_){ }
  /* HERO-CARD-01 (Jan, 2026-10-28: from the end of a commission, "an easy and clean way" to "design their custom
     character card ... signed by Jan", live "automatically without my approval", then "prompt them to sign in with
     Google"). "Make <name>'s card" on the client's finished commission page (commission-portal-live.js cardStart) signs
     this browser in to the Hall as the order's email, leaves the blueprint, a fresh link to the finished painting and a
     one-time pass in localStorage 'ff-portal-hero' for half an hour, and opens #add-hero. Once the form is open, the
     name, race, class, level, story and painting go into empty fields, the FramedFantasy box is ticked and already
     confirmed (the portal knows this client commissioned it), and the form starts past its gate
     (hoh-section-core.js hohPortalCardMode). Nothing is locked. The hero is live the moment it is added, like every
     Hall hero; the submit links it to the portrait with the pass (hohPortalCardLink, hoh_portal_card_link in
     sql/HERO-CARD-01-UPDATE.sql), so it waits for Jan to sign with no studio step, and the form ends on its own screen
     (hohPortalCardSuccess). An older note with no pass (the first "Add my portrait to the Hall") fills the name, the
     email and the painting as before. If the form does not open within ten seconds nothing is filled. */
  (function takePortalHero(){
    var KEY = 'ff-portal-hero', raw = null;
    try { raw = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch(_){ raw = null; }
    if(!raw) return;
    if(!(Date.now() - (+raw.at || 0) < 30 * 60 * 1000)){ try { localStorage.removeItem(KEY); } catch(_){ } return; }
    var pass = /^[0-9a-f]{64}$/.test(String(raw.pass || '')) ? String(raw.pass) : '';
    var who = String(raw.email || '').trim().toLowerCase(), hero = String(raw.name || '').trim().slice(0, 60);
    var story = String(raw.story || '').replace(/\r\n?/g, '\n').trim().slice(0, 4000);
    /* set at once, before the form opens, so the day's one-hero limit and the stamp panel already know */
    if(pass) window.hohPortalCard = { pass: pass, name: hero, email: who, order: String(raw.order || '').slice(0, 20), story: story.length >= 80 };
    var tries = 0;
    (function wait(){
      var modal = document.getElementById('hoh-form-modal'), name = document.getElementById('hoh-f-name');
      var open = !!(modal && name && getComputedStyle(modal).display !== 'none' && modal.getBoundingClientRect().height > 0);
      if(!open){ if(++tries < 40) setTimeout(wait, 250); else if(pass) window.hohPortalCard = null; return; }
      try { localStorage.removeItem(KEY); } catch(_){ }
      function fill(el, v){
        if(!el || !v || String(el.value || '').trim()) return;
        el.value = v;
        try { el.dispatchEvent(new Event('input', { bubbles: true })); } catch(_){ }
      }
      /* a race or class the list has is chosen; anything else is "Other" with their own words */
      function choose(id, otherId, v){
        var sel = document.getElementById(id); v = String(v || '').trim().slice(0, 40);
        if(!sel || !v || sel.value) return;
        var hit = '', other = '';
        for(var i = 0; i < sel.options.length; i++){
          var o = sel.options[i].value;
          if(o && o.toLowerCase() === v.toLowerCase()) hit = o;
          if(o === 'Other') other = o;
        }
        if(!hit && !other) return;
        sel.value = hit || other;
        try { sel.dispatchEvent(new Event('change', { bubbles: true })); } catch(_){ }
        if(!hit){ var own = document.getElementById(otherId); if(own){ own.style.display = 'block'; fill(own, v); } }
      }
      fill(name, hero);
      if(pass){
        choose('hoh-f-race', 'hoh-f-custom-race', raw.race);
        choose('hoh-f-class', 'hoh-f-custom-class', raw.charClass);
        var lvl = parseInt(String(raw.level || '').replace(/[^0-9]/g, ''), 10);
        if(lvl >= 1 && lvl <= 20) fill(document.getElementById('hoh-f-level'), String(lvl));
        fill(document.getElementById('hoh-f-story'), story);
      }
      if(!getStoredToken() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(who)) fill(document.getElementById('hoh-f-email'), who);
      var ff = document.getElementById('hoh-f-ff-portrait');
      if(ff){
        if(!ff.checked){
          ff.checked = true;
          try { ff.dispatchEvent(new Event('change', { bubbles: true })); } catch(_){ }
        }
        /* After the change, and also when a restored draft had it ticked: the Hall's change listener
           (hall-of-heroes.liquid) clears the confirmation on every toggle, and opening the form clears it too. */
        window.__hohFfConfirmed = true;
      }
      if(pass){
        var first = hero.split(/\s+/)[0];
        try { document.getElementById('hoh-form-modal-title').textContent = first ? 'Make ' + first + (/s$/i.test(first) ? '\u2019' : '\u2019s') + ' hero card' : 'Make your hero card'; } catch(_){ }
        /* above the fields it is about, under the card preview */
        var lead = name.closest('[data-wstep="1"]');
        if(lead && !document.getElementById('hoh-portal-note')) lead.insertAdjacentHTML('beforebegin',
          '<p class="hoh-portal-note" id="hoh-portal-note" data-wstep="1">Filled in from your order' + (window.hohPortalCard.order ? ' ' + esc(window.hohPortalCard.order) : '') + '. All of it shows in the Hall, so change anything you like.</p>');
        if(typeof window.hohPortalCardMode === 'function') window.hohPortalCardMode(window.hohPortalCard.story);
      }
      var url = String(raw.image || ''), zone = document.getElementById('hoh-upload-zone-0');
      if(!/^https:\/\//.test(url) || typeof window.hohPreviewImg !== 'function' || (zone && zone.querySelector('img'))) return;
      fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' }).then(function(r){
        if(!r.ok) throw new Error('picture');
        return r.blob();
      }).then(function(b){
        if(!/^image\//.test(b.type) || (zone && zone.querySelector('img'))) return;
        var ext = b.type === 'image/png' ? 'png' : b.type === 'image/webp' ? 'webp' : 'jpg', file;
        try { file = new File([b], 'portrait.' + ext, { type: b.type }); } catch(_){ file = b; try { file.name = 'portrait.' + ext; } catch(__){ } }
        window.hohPreviewImg(0, { files: [file] });
      }).catch(function(){ /* the client picks the picture themselves, as on any Add a Hero */ });
    })();
  })();

  /* The submit (hoh-section-core.js) hands the new hero here right after its sign-in is settled: the session the
     commission page kept, or a PIN typed on the form. The answer waits on the success screen. */
  window.hohPortalCardLink = function(heroId, email){
    var p = window.hohPortalCard;
    if(!p || !heroId) return Promise.resolve(null);
    if(p.linking) return p.linking;
    var em = String(email || '').trim().toLowerCase(), t = getStoredToken(), sb = window.hohSupabaseClient;
    var pin = t && String(t.email || '').trim().toLowerCase() === em ? String(t.pin || '') : '';
    p.hero = String(heroId); p.email = em || p.email;
    p.linking = (!sb || !pin) ? Promise.resolve({ ok: false, reason: 'auth' })
      : Promise.resolve(sb.rpc('hoh_portal_card_link', { p_email: em, p_pin: pin, p_hero: String(heroId), p_pass: p.pass })).then(function(r){
          return r && !r.error && r.data ? r.data : { ok: false, reason: 'error' };
        }, function(){ return { ok: false, reason: 'error' }; });
    return p.linking;
  };

  /* The last screen of a portrait from the commission page: the card first, then their page, then Google so they can
     come back from any phone or computer (Google refuses in-app browsers, so there it says how a code works instead). */
  window.hohPortalCardSuccess = function(charName, heroId){
    var p = window.hohPortalCard, inner = document.getElementById('hoh-form-inner');
    if(!p || !inner || !heroId) return false;
    /* done with the pass: another hero added on this page is an ordinary add */
    window.hohPortalCard = null;
    var first = String(charName || p.name || '').trim().split(/\s+/)[0] || 'Your hero';
    var poss = /s$/i.test(first) ? first + '\u2019' : first + '\u2019s';
    var page = '/pages/hero?h=' + encodeURIComponent(String(heroId));
    var email = String(p.email || '').trim().toLowerCase();
    var google = !!(window.hohGoogle && typeof window.hohGoogle.available === 'function' && window.hohGoogle.available());
    try { document.getElementById('hoh-form-modal-title').textContent = poss + ' hero card'; } catch(_){ }
    inner.innerHTML = '<div class="hoh-success hoh-portal-done" role="status" aria-live="polite">' +
      '<div class="hoh-success-icon" aria-hidden="true">&#9876;</div>' +
      '<h3 class="hoh-success-title">' + esc(first) + ' is in the Hall of Heroes</h3>' +
      '<p class="hoh-success-text" data-portal-line>' + esc(first) + ' is live for everyone to see.</p>' +
      '<div class="hoh-portal-done-btns">' +
        '<button type="button" class="hoh-btn-primary" data-portal-design>Design ' + esc(poss) + ' card</button>' +
        '<a class="hoh-portal-done-link" href="' + esc(page) + '">Open ' + esc(poss) + ' page</a>' +
      '</div>' +
      '<div class="hoh-portal-google">' +
        '<p class="hoh-portal-google-title">Come back to ' + esc(first) + ' any time</p>' +
        (google
          ? '<p class="hoh-portal-google-text">This browser stays signed in. To open your Hall on any phone or computer, sign in with Google' + (email ? ' as <b>' + esc(email) + '</b>' : '') + '.</p>' +
            '<button type="button" class="hoh-portal-google-btn" data-portal-google><svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>Continue with Google</button>'
          : '<p class="hoh-portal-google-text">This browser stays signed in. On another phone or computer, open the Hall of Heroes and sign in with ' + (email ? '<b>' + esc(email) + '</b>' : 'your email') + '. I\u2019ll email you a 4-digit code.</p>') +
      '</div>' +
    '</div>';
    try { if(window.hohResetViewportModal) window.hohResetViewportModal('hoh-form-modal'); } catch(_){ }
    var line = inner.querySelector('[data-portal-line]');
    Promise.resolve(p.linking).then(function(r){
      if(!line || !line.isConnected) return;
      line.innerHTML = r && r.ok
        ? esc(first) + ' is live, and linked to your order. I\u2019ll <b>sign and number</b> the card next, and the number shows on your commission page.'
        : esc(first) + ' is live. I\u2019ll link ' + esc(first) + ' to your order and <b>sign and number</b> the card next.';
    });
    inner.querySelector('[data-portal-design]').addEventListener('click', function(){
      var go = function(){ try { window.hohCloseForm(); } catch(_){ } window.FFBinder.design(String(heroId)); };
      if(window.FFBinder && typeof window.FFBinder.design === 'function') return go();
      /* the binder loads once the Hall knows the sign-in (snippets/ff-binder.liquid); its hero page always has it */
      var n = 0;
      (function later(){
        if(window.FFBinder && typeof window.FFBinder.design === 'function') return go();
        if(++n < 12) return setTimeout(later, 250);
        location.href = page + '&design=1';   /* hoh-hero-page.js opens the design there */
      })();
    });
    var g = inner.querySelector('[data-portal-google]');
    if(g) g.addEventListener('click', function(){
      g.disabled = true;
      Promise.resolve(window.hohGoogle.start({ from: 'hall', email: email, tab: true })).then(function(){ g.disabled = false; }, function(){ g.disabled = false; });
    });
    return true;
  };

  /* ============ ANONYMOUS YOUR HALL INTRO ============
     A dedicated welcome lightbox that explains what "Your Hall"
     means before asking the user to sign in. Three steps in plain
     language: submit a hero, claim your hall with a PIN, then come
     back to track your tickets and odds. Two CTAs: primary path
     (Submit Your Hero), secondary path (I already have a hero).

     Lives separate from the daily quests modal because that one is
     for users who already understand the system. This one is for
     first-time visitors. */
  function ensureAnonModal(){
    var m = document.getElementById('hoh-anon-hall-modal');
    if(m) return m;
    m = document.createElement('div');
    m.id = 'hoh-anon-hall-modal';
    m.className = 'hoh-anon-hall-modal';
    m.style.display = 'none';
    document.body.appendChild(m);
    m.addEventListener('click', function(e){
      if(e.target === m) window.hohCloseAnonHallIntro();
    });
    return m;
  }
  window.hohOpenAnonHallIntro = function(){
    var m = ensureAnonModal();
    /* Subtle gold theme replacing the older purple. Lead and step
       copy reframed around BENEFITS — what the user gets — instead
       of describing the system. The em dashes were removed per the
       house style; sentence breaks carry the same beat. */
    m.innerHTML =
      '<div class="hoh-anon-hall-backdrop"></div>' +
      '<div class="hoh-anon-hall-sheet" role="dialog" aria-label="Your Hall, getting started">' +
        '<button type="button" class="hoh-anon-hall-x" onclick="hohCloseAnonHallIntro()" aria-label="Close">&times;</button>' +
        '<div class="hoh-anon-hall-eyebrow">Your Hall</div>' +
        '<h2 class="hoh-anon-hall-title">A home for your heroes.</h2>' +
        '<p class="hoh-anon-hall-lead">Sign in to keep every hero you make in one place. See their hearts, their raffle tickets, and your odds at the monthly free portrait. Build parties. Run encounters. Save your prep.</p>' +
        '<div class="hoh-anon-hall-steps">' +
          '<div class="hoh-anon-hall-step">' +
            '<div class="hoh-anon-hall-step-num">1</div>' +
            '<div class="hoh-anon-hall-step-body">' +
              '<div class="hoh-anon-hall-step-title">Submit your first hero</div>' +
              '<div class="hoh-anon-hall-step-sub">Name, race, class, story, portrait. One minute. No account needed up front.</div>' +
            '</div>' +
          '</div>' +
          '<div class="hoh-anon-hall-step">' +
            '<div class="hoh-anon-hall-step-num">2</div>' +
            '<div class="hoh-anon-hall-step-body">' +
              '<div class="hoh-anon-hall-step-title">Set a 4-digit PIN to claim your hall</div>' +
              '<div class="hoh-anon-hall-step-sub">Email plus PIN is your sign-in. No password to remember, no inbox to dig through.</div>' +
            '</div>' +
          '</div>' +
          '<div class="hoh-anon-hall-step">' +
            '<div class="hoh-anon-hall-step-num">3</div>' +
            '<div class="hoh-anon-hall-step-body">' +
              '<div class="hoh-anon-hall-step-title">Save heroes, build parties, run encounters</div>' +
              '<div class="hoh-anon-hall-step-sub">Heart the heroes you love, drag them into a party of up to five, then summon NPCs and roll scenes from your Satchel.</div>' +
            '</div>' +
          '</div>' +
          '<div class="hoh-anon-hall-step">' +
            '<div class="hoh-anon-hall-step-num">4</div>' +
            '<div class="hoh-anon-hall-step-body">' +
              '<div class="hoh-anon-hall-step-title">' +
                '<span class="hoh-anon-hall-step-title-icon hoh-tk-icon" aria-hidden="true"></span>' +
                'Bank up to 4 raffle tickets a day' +
              '</div>' +
              '<div class="hoh-anon-hall-step-sub">Three small daily quests stack tickets across months until your hero wins a free portrait.</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="hoh-anon-hall-cta-stack">' +
          '<button type="button" class="hoh-anon-hall-primary" onclick="hohCloseAnonHallIntro();if(window.hohOpenForm)hohOpenForm();">Submit Your First Hero</button>' +
          /* v38: this button says "sign me in" and used to open a dashboard.
             hohOpenYourHall now routes a signed-out visitor to the form, so the
             promise and the destination finally agree. */
          '<button type="button" class="hoh-anon-hall-secondary" onclick="hohCloseAnonHallIntro();document.body.classList.add(\'hoh-page-mode-yours\');if(window.hohYhSetHeaderModeYours)window.hohYhSetHeaderModeYours();if(window.hohSignInToYourHall)window.hohSignInToYourHall();else if(window.hohOpenYourHall)hohOpenYourHall();if(window.hohRefreshYhToggle)window.hohRefreshYhToggle();window.scrollTo({top:0,behavior:\'smooth\'});">I already have a hero. Sign me in.</button>' +
        '</div>' +
      '</div>';
    m.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function(){ m.classList.add('hoh-anon-hall-open'); });
  };
  window.hohCloseAnonHallIntro = function(){
    var m = document.getElementById('hoh-anon-hall-modal');
    if(!m) return;
    m.classList.remove('hoh-anon-hall-open');
    setTimeout(function(){
      m.style.display = 'none';
      document.body.style.overflow = '';
    }, 220);
  };
  document.addEventListener('keydown', function(e){
    var m = document.getElementById('hoh-anon-hall-modal');
    if(!m || m.style.display === 'none') return;
    if(e.key === 'Escape') window.hohCloseAnonHallIntro();
  });

  /* Mount initial state on page load + react to ticket-state changes. */
  function initToggle(){
    window.hohRefreshYhToggle();
    document.addEventListener('hoh:characters-loaded', window.hohRefreshYhToggle);
    document.addEventListener('visibilitychange', function(){
      if(!document.hidden) window.hohRefreshYhToggle();
    });
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', initToggle);
  } else {
    initToggle();
  }

  /* ============ IN-PAGE VIEW ============
     Your Hall is no longer a modal. The view renders into the page's
     #hoh-yh-page section, which the section file declares right after
     the Fresh Faces rail. The Your Hall toggle button (above) flips
     visibility via the hoh-page-mode-yours class on body. */

  function getPageContainer(){
    return document.getElementById('hoh-yh-page');
  }

  /* Render the small summary line under the Your Hall title. Shows
     total raffle tickets across the user's characters and the count
     of characters in the draw. Falls back gracefully if data isn't
     loaded yet. */
  function renderSummary(){
    var el = document.getElementById('hoh-yh-summary');
    if(!el) return;
    var rows = (SOURCE_ROWS && SOURCE_ROWS.characters) || [];
    /* Profile combines submitted + hearted rows; the summary is for
       the user's OWN raffle stake so we filter hearted rows out
       before computing totals. */
    rows = rows.filter(function(r){ return !r || r._kind !== 'hearted'; });
    if(!rows.length && window.hohTicketState && Array.isArray(window.hohTicketState.my_characters)){
      rows = window.hohTicketState.my_characters;
    }
    if(!rows.length){
      /* R793: nothing is banked in the live Hall; a hero takes its ticket
         on its own page, one a day. */
      el.innerHTML = ((typeof window.hohR793Live === 'function') && window.hohR793Live())
        ? '<span class="hoh-yh-summary-text">Add a hero and it can take a raffle ticket every day.</span>'
        : '<span class="hoh-yh-summary-text">Submit a hero to start banking raffle tickets.</span>';
      return;
    }
    /* R663 (Jan): the hearts tile is gone from this row. Hearts live in
       the header counter and on every card, and this row is about the
       user's own stake in the draw, so the first tile now counts the
       heroes they have submitted. `rows` is already filtered to their
       submitted heroes above, so its length IS that count. */
    var totalHeroes = rows.length;
    var totalTickets = 0;
    rows.forEach(function(r){
      totalTickets += (r && r.tickets) || 0;
    });
    /* Win % across all the user's characters: sum of their tickets
       divided by total tickets in the entire pool (all characters in
       the Hall). Pulled from window.hohGetAllCharacters() which the
       section exposes after data load. */
    var winPct = 0;
    try {
      if(typeof window.hohGetAllCharacters === 'function'){
        var all = window.hohGetAllCharacters() || [];
        var poolTotal = 0;
        all.forEach(function(c){ poolTotal += Math.max(1, (c && c.tickets) || 1); });
        if(poolTotal > 0) winPct = (totalTickets / poolTotal) * 100;
      }
    } catch(_){}
    var pending = (window.hohTicketState && window.hohTicketState.pending_tickets) || 0;
    var pendingHtml = pending > 0
      ? '<span class="hoh-yh-summary-pending">+' + pending + ' pending</span>'
      : '';
    var pctText = winPct < 1 ? winPct.toFixed(2) : winPct.toFixed(1);
    /* R793 (Jan, 2026-09-22): tickets are "not part of Battlegrounds or the
       profile". In the live Hall the profile keeps its Heroes count and the
       ticket and win-chance tiles go; a hero's ticket lives on its own page
       in the main Hall. Legacy mode keeps all three. */
    if(typeof window.hohR793Live === 'function' && window.hohR793Live()){
      el.innerHTML =
        '<div class="hoh-yh-stat" data-stat="heroes" tabindex="0">' +
          '<div class="hoh-yh-stat-num"><span class="hoh-yh-stat-hero" aria-hidden="true"></span> ' + totalHeroes + '</div>' +
          '<div class="hoh-yh-stat-label">Heroes</div>' +
          '<div class="hoh-yh-stat-pop" aria-hidden="true"></div>' +
        '</div>';
      try { wireStatPopovers(rows, totalTickets); } catch(_){}
      return;
    }
    /* Each stat gets a hover popover with the top 5 contributors plus
       an "others" rollup. Win% gets the math breakdown instead of a
       list. data-stat lets one delegated handler show/hide the popover
       for whichever stat the user is on. */
    el.innerHTML =
      '<div class="hoh-yh-stat" data-stat="heroes" tabindex="0">' +
        '<div class="hoh-yh-stat-num"><span class="hoh-yh-stat-hero" aria-hidden="true"></span> ' + totalHeroes + '</div>' +
        /* R663: the label is one word for a measured reason. "Heroes
           submitted" renders 137.8px on a 375px phone, and the three
           pills only have 323px, so the win chance pill dropped onto a
           second row. "Heroes" is 84px, which is NARROWER than the
           hearts pill it replaces, so the row gets air back instead. */
        '<div class="hoh-yh-stat-label">Heroes</div>' +
        '<div class="hoh-yh-stat-pop" aria-hidden="true"></div>' +
      '</div>' +
      '<div class="hoh-yh-stat" data-stat="tickets" tabindex="0">' +
        '<div class="hoh-yh-stat-num"><span class="hoh-tk-icon" aria-hidden="true"></span> ' + totalTickets + '</div>' +
        '<div class="hoh-yh-stat-label">Tickets</div>' +
        '<div class="hoh-yh-stat-pop" aria-hidden="true"></div>' +
      '</div>' +
      /* R62: win-chance tile now carries a small trophy icon for
         parity with hearts (♥) and tickets (ticket icon). User
         testing on mobile reported "the icon disappears" after
         claiming the daily bonus on this tile — the underlying cause
         was that this tile had no leading icon at all, and the %
         text plus the claimed-state opacity combined to make the
         tile look empty next to its iconified neighbors. */
      '<div class="hoh-yh-stat" data-stat="winpct" tabindex="0">' +
        '<div class="hoh-yh-stat-num"><span class="hoh-yh-stat-trophy" aria-hidden="true">&#x1F3C6;&#xFE0E;</span> ' + pctText + '%</div>' +
        '<div class="hoh-yh-stat-label">Win chance</div>' +
        '<div class="hoh-yh-stat-pop" aria-hidden="true"></div>' +
      '</div>' +
      pendingHtml +
      /* R45 Jangela god-mode debug pill: only renders when god-mode
         check returns true (signed in as the dev account). Click
         grants 100 hearts via the standard wallet path so all
         downstream surfaces (header counter, fly animation, etc.)
         stay in sync. Re-clickable any time — no daily cap. */
      '';
    /* R380 (2026-08-06, Jan): the floating +100 hearts tile is gone. The
       debug grant lives in the gear panel now (+1000 hearts, Shop tab of
       the DEBUG menu in hoh-fresh-faces.js). */
    /* R148-Z22l: Submitter Standings panel removed per user. The
       panel was injected here (renderSummary) and lived directly
       below the stat tiles. Removal includes:
         - this inject block
         - the hohFetchSubmitterStats / _renderSubmitterStandings
           helpers further down in this file
         - the .hoh-yh-standings* CSS rules in hall-of-heroes.css
       Defensive: if a stale panel from a cached render somehow
       survives (e.g. mid-deploy reload), strip it on every
       renderSummary so it doesn't ghost. */
    try {
      var existingPanel = document.getElementById('hoh-yh-standings');
      if(existingPanel && existingPanel.parentNode) existingPanel.parentNode.removeChild(existingPanel);
    } catch(_){}
    wireStatPopovers(rows, totalTickets);
    /* Wire the debug click here (outside wireStatPopovers so it doesn\'t
       inherit the once-per-day claimed gate the regular stats use). */
    /* R380: +100 tile wiring removed with the tile. */
  }

  /* Build and lazily fill the hover popover for a stat. The popovers
     are minimal: a header line, top 5 rows, and an "others" rollup.
     Win% shows the math (your tickets / pool * 100). */
  /* R148-Z22l: Submitter standings (hohFetchSubmitterStats +
     _renderSubmitterStandings + __hohStatsCache state) removed per
     user. The associated SQL migration sql/R148-Z16-submitter-stats.sql
     remains on disk but is no longer called from anywhere. The
     get_submitter_stats RPC, if deployed, is now an orphaned function
     in the database — safe to leave or drop manually. */

  function wireStatPopovers(rows, totalTickets){
    var stats = document.querySelectorAll('#hoh-yh-summary .hoh-yh-stat');
    if(!stats || !stats.length) return;
    var poolTotal = 0;
    try {
      if(typeof window.hohGetAllCharacters === 'function'){
        var all = window.hohGetAllCharacters() || [];
        all.forEach(function(c){ poolTotal += Math.max(1, (c && c.tickets) || 1); });
      }
    } catch(_){}
    stats.forEach(function(stat){
      var key = stat.getAttribute('data-stat');
      var pop = stat.querySelector('.hoh-yh-stat-pop');
      if(!pop) return;
      pop.innerHTML = buildPopHtml(key, rows, totalTickets, poolTotal);
    });
    /* R43 daily click bonus: tapping a stat pill the FIRST time per day
       grants +10 hearts. Three independent flags (one per stat) so a
       user can collect up to 30 hearts per day from the trio. The flag
       is keyed by ymd so it auto-resets each new local day. The hearts
       fly from the clicked pill to the header heart counter using the
       same flyHeartsToPill helper hoh-extras.js exposes. After the
       grant, the pill gets a "claimed" class so the user sees a subtle
       visual confirmation on subsequent same-day clicks. */
    function ymdToday(){
      var d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    }
    function statBonusKey(statKey){ return 'hoh-yh-stat-bonus-' + statKey + '-' + ymdToday(); }
    function statClaimedToday(statKey){
      try { return localStorage.getItem(statBonusKey(statKey)) === '1'; } catch(_){ return false; }
    }
    function markStatClaimedToday(statKey){
      try { localStorage.setItem(statBonusKey(statKey), '1'); } catch(_){}
    }
    /* Apply the claimed visual class on first paint so a user who
       already claimed earlier today sees the muted state immediately. */
    stats.forEach(function(stat){
      var sk = stat.getAttribute('data-stat');
      if(statClaimedToday(sk)) stat.classList.add('hoh-yh-stat-claimed');
    });
    stats.forEach(function(stat){
      if(stat.__hohBonusBound) return;
      stat.__hohBonusBound = true;
      stat.addEventListener('click', function(e){
        /* Don\'t hijack interactions inside the popover (links, buttons). */
        if(e.target && e.target.closest && e.target.closest('.hoh-yh-stat-pop')) return;
        var sk = stat.getAttribute('data-stat');
        if(!sk) return;
        if(statClaimedToday(sk)) return; /* silent; already claimed today */
        markStatClaimedToday(sk);
        stat.classList.add('hoh-yh-stat-claimed');
        var BONUS = 10;
        if(typeof window.hohAddHeartsToBank === 'function'){
          try { window.hohAddHeartsToBank(BONUS, 'earn_yourhall'); } catch(_){}
        }
        if(typeof window.hohShowHeartToast === 'function'){
          /* R663: the hearts tile became the heroes tile, so its
             "A daily kindness" line moved with it. */
          var label = sk === 'heroes' ? 'A daily roll call'
                    : sk === 'tickets' ? 'A daily fortune'
                    : 'A daily wager';
          try { window.hohShowHeartToast(label, 'Claimed for today.', true); } catch(_){}
        }
        /* Heart-fly from the clicked pill to the header counter so the
           reward visually lands. flyHeartsToPill is defined in
           hoh-extras.js and reads the global #hoh-tk-header element
           as the destination. */
        try {
          if(typeof window.hohFlyHeartsToPill === 'function'){
            window.hohFlyHeartsToPill(stat, Math.min(BONUS, 12));
          }
        } catch(_){}
      });
    });
  }

  /* Render-only: returns HTML for the popover body. Top 5 rows by
     metric, "others" rollup for everything beyond row 5, and a
     gentle empty state when the user has no characters yet. */
  function buildPopHtml(key, rows, totalTickets, poolTotal){
    if(key === 'winpct'){
      var pool = poolTotal || 0;
      var mine = totalTickets || 0;
      var pctRaw = pool > 0 ? (mine / pool) * 100 : 0;
      var pctTxt = pctRaw < 1 ? pctRaw.toFixed(2) : pctRaw.toFixed(1);
      return '<div class="hoh-yh-pop-head">Win odds</div>' +
        '<div class="hoh-yh-pop-math">' +
          '<div class="hoh-yh-pop-mathline"><span>Your tickets</span><span>' + mine.toLocaleString() + '</span></div>' +
          '<div class="hoh-yh-pop-mathline"><span>Total in pool</span><span>' + pool.toLocaleString() + '</span></div>' +
          '<div class="hoh-yh-pop-mathline hoh-yh-pop-mathres"><span>Win chance</span><span>' + pctTxt + '%</span></div>' +
        '</div>';
    }
    /* R663: the heroes tile lists the roster instead of a metric. Newest
       first, because the question this tile answers is "what have I put
       in the hall lately". The right column carries the hero's class (or
       race when no class is set) so the list is not five bare names. */
    if(key === 'heroes'){
      if(!rows || !rows.length){
        return '<div class="hoh-yh-pop-head">Your heroes</div>' +
               '<div class="hoh-yh-pop-empty">Submit a hero to fill this list.</div>';
      }
      var newest = rows.slice().sort(function(a, b){
        var ta = (a && a.created_at) ? new Date(a.created_at).getTime() : 0;
        var tb = (b && b.created_at) ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      var topH = newest.slice(0, 5);
      var restH = newest.length - topH.length;
      var heroRows = topH.map(function(r){
        var meta = (r && (r.character_class || r.race)) || '';
        return '<div class="hoh-yh-pop-row">' +
          '<span class="hoh-yh-pop-name">' + esc(r.name || 'Unnamed') + '</span>' +
          (meta ? '<span class="hoh-yh-pop-val">' + esc(meta) + '</span>' : '') +
        '</div>';
      }).join('');
      var moreH = restH > 0
        ? '<div class="hoh-yh-pop-row hoh-yh-pop-others">' +
            '<span class="hoh-yh-pop-name">+' + restH + ' other' + (restH === 1 ? '' : 's') + '</span>' +
          '</div>'
        : '';
      return '<div class="hoh-yh-pop-head">Your heroes</div>' + heroRows + moreH;
    }
    if(!rows || !rows.length){
      return '<div class="hoh-yh-pop-head">Top by tickets</div>' +
             '<div class="hoh-yh-pop-empty">Submit a hero to fill this list.</div>';
    }
    var sorted = rows.slice().sort(function(a, b){
      return (b.tickets || 0) - (a.tickets || 0);
    });
    var top = sorted.slice(0, 5);
    var rest = sorted.slice(5);
    var restSum = 0;
    rest.forEach(function(r){ restSum += (r.tickets || 0); });
    var head = 'Top by tickets';
    var iconCh = '<span class="hoh-tk-icon" aria-hidden="true"></span>';
    var rowsHtml = top.map(function(r){
      var v = (r.tickets || 0);
      return '<div class="hoh-yh-pop-row">' +
        '<span class="hoh-yh-pop-name">' + esc(r.name || 'Unnamed') + '</span>' +
        '<span class="hoh-yh-pop-val">' + iconCh + ' ' + v.toLocaleString() + '</span>' +
      '</div>';
    }).join('');
    var moreHtml = rest.length > 0
      ? '<div class="hoh-yh-pop-row hoh-yh-pop-others">' +
          '<span class="hoh-yh-pop-name">+' + rest.length + ' other' + (rest.length === 1 ? '' : 's') + '</span>' +
          '<span class="hoh-yh-pop-val">' + iconCh + ' ' + restSum.toLocaleString() + '</span>' +
        '</div>'
      : '';
    return '<div class="hoh-yh-pop-head">' + head + '</div>' + rowsHtml + moreHtml;
  }
  /* Expose so the load functions can re-render after fetching rows. */
  window.hohYourHallRefreshSummary = renderSummary;

  window.hohCloseYourHall = function(){
    /* Called by the tab switcher when the user clicks Fresh Faces.
       Nothing to tear down — the body class hides the in-page view via
       CSS. We keep the dashboard rendered so a return is instant. */
  };

  /* Open Your Hall directly on the Profile (characters) tab — used by
     the Daily Tickets "Enter Your Hall" CTA so users land where they
     can see their hero, ticket count, and heart bank, instead of on
     whatever tab they last visited. */
  window.hohOpenYourHallProfile = function(){
    if(binderInstead()) return true;
    document.body.classList.add('hoh-page-mode-yours');
    if(typeof window.hohYhSetHeaderModeYours === 'function'){
      try { window.hohYhSetHeaderModeYours(); } catch(_){}
    }
    currentView = 'characters';
    /* R66: Always reset the Profile filter to "My Submitted" on every
       entry — this is the page where the user manages their own
       heroes (apply tickets, edit, etc.) and any other slice would
       hide the heroes they came here to act on. */
    if(FILTER_STATE && FILTER_STATE.characters){
      FILTER_STATE.characters.show = 'submitted';
    }
    renderDashboard();
    if(typeof window.hohRefreshYhToggle === 'function'){
      try { window.hohRefreshYhToggle(); } catch(_){}
    }
  };

  window.hohOpenYourHall = function(){
    if(binderInstead()) return true;
    /* R82: ALWAYS land on Profile / My Submitted, regardless of
       lastUsedTab, regardless of viewport, regardless of entry point
       (3 doors, sticky pill, anon CTA, deep link, etc.). The user
       wants every door into Your Hall to drop them on their own
       submitted heroes — that\'s the action surface where they apply
       tickets, edit, manage their own work. The previous lastUsedTab
       restore felt smart but produced surprises ("why am I on Cast?")
       so we drop it entirely.
       Migration: the old localStorage 'lastUsedTab' value is kept on
       disk so other code that reads it doesn\'t blow up, but it no
       longer drives the landing tab. */
    currentView = 'characters';
    /* Reset the Profile filter to "My Submitted" so the user sees
       their own characters right away (apply tickets, edit, etc.). */
    if(FILTER_STATE && FILTER_STATE.characters){
      FILTER_STATE.characters.show = 'submitted';
    }
    renderDashboard();
    /* R148-Z22p: Passive-XP catch-up moved INTO renderDashboard so
       it picks up the post-loadCharacters render (when SOURCE_ROWS
       finally has rows). Internally cached 5min. */
  };

  /* v38: a NAMED door. Until now nothing outside this file could open the
     sign-in, because renderSignIn was private and every caller was an error
     branch. Anything in the theme that wants to offer a login can call this. */
  window.hohSignInToYourHall = function(prefill){
    try {
      /* R425: this used to force <body> into hoh-page-mode-yours, because the
         sign-in was a PAGE and that class is what makes #hoh-yh-page visible.
         It is a lightbox now, so the Hall stays on screen behind it and the
         page never switches. That also fixes the thing that made signing in
         feel like a punishment: you were taken away from what you were
         looking at before you had done anything.
         _siFrom remembers where they came from, so a session that expired
         INSIDE the personal hall still lands back on the dashboard while a
         sign-in from the Hall stays in the Hall. */
      _siFrom = document.body.classList.contains('hoh-page-mode-yours') ? 'page' : 'hall';
      currentView = 'characters';
      renderSignIn(typeof prefill === 'string' ? prefill : '', '', 'enter');
      return true;
    } catch(_){ return false; }
  };

  /* ============ SIGN IN (lightbox, two steps) ============
     R425 (Jan) — from her screenshot of the old screen, four things were
     wrong at once and they compound:

       1. TWO WAYS BACK. The page header already carries
          "← Return to Main Hall", and the form drew its own
          "← Back to the Hall" button directly beneath it. Two controls,
          one job, stacked — the second one reads as a different
          destination and there isn't one.
       2. AN EYEBROW THAT SAID NOTHING. "YOUR HALL" sat above a heading
          that already read "Sign in to Your Hall".
       3. THE PIN WAS ASKED BEFORE THE EMAIL WAS KNOWN. Four PIN boxes sat
          open next to an empty email field, labelled "(optional)". A
          person cannot know their PIN is right for an email the page has
          not checked, and "optional" invited them to skip the only field
          that actually signs them in. Email first, verified, THEN the PIN
          — and only if that email really has one.
       4. IT TOOK OVER THE WHOLE PAGE. Signing in is a detour, not a
          destination: it swapped the Hall out for a near-empty screen,
          so the thing they were looking at was gone before they had
          done anything.

     So: a lightbox over the Hall, two steps, PIN gated behind a real
     email check, and on success the Hall stays exactly where it was
     while the personal hall tab arrives with an animation pointing at
     it (hohYhCelebrateTab). The three server calls are untouched —
     email_has_pin, verify_pin, set_pin — and so is the brute-force
     throttle. This is the same flow with the guesswork removed. */
  var _siStep = 1;        /* 1 = email, 2 = PIN, 3 = claim (set a PIN) */
  var _siEmail = '';
  var _siBusy = false;
  var _siFrom = 'hall';   /* 'hall' = stay and celebrate, 'page' = render the dashboard */

  /* SELF-CAUGHT RACE: close fades for 200ms and THEN removes the node. Reopen
     inside that window — an expired session bouncing straight back, or a
     visitor who taps the login line again immediately — and siShell found the
     still-present dying node, rendered the fresh form into it, and the
     pending timer then deleted the whole thing out from under them. The
     lightbox would simply vanish with no error anywhere.
     The timer is held ON the node now, so reopening cancels it. */
  /* ============ R472 · CLOSING SIGN IN MUST NOT LEAVE A BLANK PAGE ======
     R425 turned sign-in into a lightbox appended to <body> that never writes
     into #hoh-yh-page, and removed its "Back to the Hall" link. Callers that
     still set body.hoh-page-mode-yours BEFORE opening it were never updated,
     and that class hides the entire main hall with !important. So closing the
     lightbox by the X, the backdrop, or the "Add your first one" link left a
     page with nothing on it and no way back except a reload.

     The caller above is fixed too. This is the net, because the class is set
     from several places and only ONE thing is actually true everywhere: the
     mode may only stay on while something has filled the container it hides
     the rest of the page in favour of. */
  function siUnblank(){
    try{
      var pg = document.getElementById('hoh-yh-page');
      if(document.body.classList.contains('hoh-page-mode-yours') && (!pg || !pg.children.length)){
        document.body.classList.remove('hoh-page-mode-yours');
        if(typeof window.hohYhSetHeaderModeHome === 'function') window.hohYhSetHeaderModeHome();
        if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
      }
    }catch(_){}
  }
  function siClose(){
    var m = document.getElementById('hoh-yh-signin-lb');
    if(!m){ siUnblank(); return; }
    m.classList.remove('hoh-yh-lb-open');
    m.__siKill = setTimeout(function(){
      try { if(m.parentNode) m.parentNode.removeChild(m); } catch(_){}
      try { document.body.style.overflow = ''; } catch(_){}
      siUnblank();   /* R472 */
    }, 200);
  }
  window.hohYhSignInClose = siClose;

  function siShell(){
    var m = document.getElementById('hoh-yh-signin-lb');
    if(m){
      /* cancel a close that is still fading out, and put it back on screen */
      if(m.__siKill){ try { clearTimeout(m.__siKill); } catch(_){} m.__siKill = null; }
      try { document.body.style.overflow = 'hidden'; } catch(_){}
      if(!m.classList.contains('hoh-yh-lb-open')) requestAnimationFrame(function(){ m.classList.add('hoh-yh-lb-open'); });
      return m;
    }
    m = document.createElement('div');
    m.id = 'hoh-yh-signin-lb';
    m.className = 'hoh-yh-lb';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', 'Sign in to Your Hall');
    m.innerHTML = '<div class="hoh-yh-lb-backdrop"></div><div class="hoh-yh-lb-box" id="hoh-yh-lb-box"></div>';
    document.body.appendChild(m);
    /* the backdrop closes, the box never does — a stray tap inside a form
       that is mid-typing must not throw the work away */
    m.querySelector('.hoh-yh-lb-backdrop').addEventListener('click', siClose);
    try { document.body.style.overflow = 'hidden'; } catch(_){}
    requestAnimationFrame(function(){ m.classList.add('hoh-yh-lb-open'); });
    return m;
  }

  /* Step 1 — the email, and nothing else. */
  function siRenderEmail(errorMsg){
    _siStep = 1;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML =
      '<button type="button" class="hoh-yh-lb-x" aria-label="Close" onclick="hohYhSignInClose()">&#10005;</button>' +
      '<h2 class="hoh-yh-lb-title">Sign in to Your Hall</h2>' +
      /* HALL-GOOGLE-01: Continue with Google first where Google sign-in works (hoh-google-signin.js fills the
         slot). Everywhere else the slot stays empty and takes no room, so this is the screen it always was. */
      '<div class="hgs-slot" data-hgs-from="hall" data-hgs-or="after"></div>' +
      '<p class="hoh-yh-lb-lead">Enter the email you submitted a hero with.</p>' +
      /* novalidate: the input is type=email so the BROWSER would block submit
         and pop its own grey bubble, which meant our styled, specific error
         never rendered and the visitor got two different error languages in
         one form. We own the message now; the type stays for the mobile
         keyboard. */
      '<form class="hoh-yh-lb-form" novalidate onsubmit="event.preventDefault();hohYhSignInEmailStep();">' +
        '<label class="hoh-yh-lb-label" for="hoh-yh-email">Email</label>' +
        '<input class="hoh-yh-lb-input" type="email" id="hoh-yh-email" autocomplete="email" inputmode="email" ' +
          'autocapitalize="off" autocorrect="off" spellcheck="false" required value="' + esc(_siEmail) + '" placeholder="name@example.com" />' +
        (errorMsg === '__cantcheck__'
          /* Kept separate from a rejection on purpose. Telling somebody their
             email does not exist when the truth is that the Hall never
             answered sends them away to re-check an address that was right
             all along. */
          ? '<div class="hoh-yh-lb-error" role="alert"><b>Could not check that email.</b> ' +
              'The Hall did not answer just then. Try again in a moment.</div>'
          : errorMsg === '__nomatch__'
          /* A rejection is a dead end unless it names a way forward, and there
             are three real ones: a typo (the address is still in the field,
             one character from right), a different address (people submit from
             whichever inbox they had open), and never having submitted at all.
             The pending line matters because a hero awaiting approval may not
             be visible yet, and that person did nothing wrong. */
          ? '<div class="hoh-yh-lb-error" role="alert"><b>No hero under that email.</b> ' +
              'Check it for a typo, or try the address you submitted with.</div>' +
            '<p class="hoh-yh-lb-nomatch">Just submitted? A hero waiting for approval can take a moment to appear. Try again shortly.</p>'
          : (errorMsg ? '<div class="hoh-yh-lb-error" role="alert">' + esc(errorMsg) + '</div>' : '')) +
        '<button type="submit" class="hoh-yh-lb-go" id="hoh-yh-lb-go">Continue</button>' +
      '</form>' +
      (errorMsg === '__nomatch__'
        ? '<p class="hoh-yh-lb-foot">Never submitted a hero? ' +
            '<button type="button" class="hoh-yh-lb-link" onclick="hohYhSignInClose();if(window.hohOpenForm)window.hohOpenForm();">Add your first one</button> ' +
            'and your hall opens with it.</p>'
        : '<p class="hoh-yh-lb-foot">We check the email first, then ask for your PIN.</p>');
    siFillGoogle(box);
    var el = document.getElementById('hoh-yh-email');
    if(el) setTimeout(function(){ try { el.focus(); } catch(_){} }, 120);
  }

  /* Step 2 — the PIN, shown only once the email is known to have one. */
  function siRenderPin(errorMsg){
    _siStep = 2;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML =
      '<button type="button" class="hoh-yh-lb-x" aria-label="Close" onclick="hohYhSignInClose()">&#10005;</button>' +
      '<h2 class="hoh-yh-lb-title">Enter your PIN</h2>' +
      '<p class="hoh-yh-lb-lead">Signing in as <b>' + esc(_siEmail) + '</b> ' +
        '<button type="button" class="hoh-yh-lb-change" onclick="hohYhSignInBack()">change</button></p>' +
      '<form class="hoh-yh-lb-form" onsubmit="event.preventDefault();hohYhSignInPinStep();">' +
        '<label class="hoh-yh-lb-label" for="hoh-yh-pin-1">4-digit PIN</label>' +
        siPinRow() +
        (errorMsg ? '<div class="hoh-yh-lb-error" role="alert">' + esc(errorMsg) + '</div>' : '') +
        '<button type="submit" class="hoh-yh-lb-go" id="hoh-yh-lb-go">Sign in</button>' +
      '</form>' +
      /* This used to offer "Set a new PIN", which walked into a wall: reaching
         this screen means the address ALREADY has one, and the server refuses
         set_pin with a null old_pin in that case. It promised a reset it could
         never perform. Resetting a forgotten PIN needs a mail-out this theme
         has no path for, so the honest answer is the one that is printed.
         HALL-GOOGLE-01: where Google sign-in works, that is the path (siPinFoot). */
      siPinFoot();
    siWirePins();
    siFillGoogle(box);
  }
  /* HALL-GOOGLE-01: where Google sign-in works, a forgotten PIN is reset through it (the Google session may set a
     new PIN, set_pin in HALL-GOOGLE-01); "Not now" on the new-PIN screen keeps the Google sign-in alone. */
  function siPinFoot(){
    var g = window.hohGoogle;
    if(g && g.available()){
      return siGoogleSlot(' data-hgs-email="' + esc(_siEmail) + '" data-hgs-reset="1" data-hgs-lead="Forgot it? Sign in with Google and pick a new one."');
    }
    if(_siGoogle && g && g.inApp()) return siGoogleSlot(' data-hgs-inapp="note"');
    return '<p class="hoh-yh-lb-foot">Forgot it? Get in touch and it can be reset by hand. The PIN is only ever stored on your own device.</p>';
  }

  /* Step 3 — no PIN on this email yet, so they pick one and claim the hall. */
  function siRenderClaim(errorMsg){
    _siStep = 3;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML =
      '<button type="button" class="hoh-yh-lb-x" aria-label="Close" onclick="hohYhSignInClose()">&#10005;</button>' +
      '<h2 class="hoh-yh-lb-title">Claim your hall</h2>' +
      '<p class="hoh-yh-lb-lead">No PIN is linked to <b>' + esc(_siEmail) + '</b> yet. Pick four digits and it is yours. ' +
        '<button type="button" class="hoh-yh-lb-change" onclick="hohYhSignInBack()">change</button></p>' +
      '<form class="hoh-yh-lb-form" onsubmit="event.preventDefault();hohYhSignInClaimStep();">' +
        '<label class="hoh-yh-lb-label" for="hoh-yh-pin-1">Pick a 4-digit PIN</label>' +
        siPinRow() +
        (errorMsg ? '<div class="hoh-yh-lb-error" role="alert">' + esc(errorMsg) + '</div>' : '') +
        '<button type="submit" class="hoh-yh-lb-go" id="hoh-yh-lb-go">Set PIN &amp; sign in</button>' +
      '</form>' +
      '<p class="hoh-yh-lb-foot">This email plus your PIN signs you in to every hero you have submitted. It stays on this device.</p>' +
      siGoogleSlot(' data-hgs-email="' + esc(_siEmail) + '" data-hgs-or="before"');
    siWirePins();
    siFillGoogle(box);
  }

  /* ============ HALL-GOOGLE-01 · the Google screens ============
     _siGoogle: the email in hand has signed in with Google before (hoh_google_hint). _siReset: the visitor came
     through "Forgot it?", so the PIN screen after Google asks for a new one. */
  var _siGoogle = false;
  var _siReset = false;
  var PIN_ASKED_KEY = 'hoh-google-pin-asked';
  function siGoogleSlot(attrs){ return '<div class="hgs-slot" data-hgs-from="hall"' + (attrs || '') + '></div>'; }
  function siFillGoogle(box){ try { if(window.hohGoogle) window.hohGoogle.fill(box); } catch(_){} }
  function siX(){ return '<button type="button" class="hoh-yh-lb-x" aria-label="Close" onclick="hohYhSignInClose()">&#10005;</button>'; }

  /* Step 4: the email signs in with Google and has no PIN. Google it is, or (inside an app browser, where Google
     refuses to open) where to go so it works, or a PIN after all. */
  function siRenderGoogleOnly(){
    _siStep = 4;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML = siX() +
      '<h2 class="hoh-yh-lb-title">Sign in with Google</h2>' +
      '<p class="hoh-yh-lb-lead"><b>' + esc(_siEmail) + '</b> signs in with Google. ' +
        '<button type="button" class="hoh-yh-lb-change" onclick="hohYhSignInBack()">change</button></p>' +
      siGoogleSlot(' data-hgs-email="' + esc(_siEmail) + '" data-hgs-inapp="note"') +
      '<p class="hoh-yh-lb-foot">Can\'t use Google here? <button type="button" class="hoh-yh-lb-link" onclick="hohYhSignInPickPin()">Pick a PIN instead</button></p>';
    siFillGoogle(box);
  }

  window.hohYhSignInPickPin = function(){ siRenderClaim(''); };

  /* After Google: an email with no hero yet. The hero comes first, and the form already has the email. */
  function siRenderNoHero(){
    _siStep = 6;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML = siX() +
      '<h2 class="hoh-yh-lb-title">No hero here yet</h2>' +
      '<p class="hoh-yh-lb-lead"><b>' + esc(_siEmail) + '</b> has no hero in the Hall yet. Add your first one and your hall opens with it.</p>' +
      '<div class="hoh-yh-lb-form"><button type="button" class="hoh-yh-lb-go" onclick="hohYhSignInClose();if(window.hohOpenForm)window.hohOpenForm();">Add your hero</button></div>' +
      '<p class="hoh-yh-lb-foot">Submitted with a different email? <button type="button" class="hoh-yh-lb-link" onclick="hohYhSignInBack()">Sign in with that one</button></p>';
  }

  /* Step 5, after Google: a hall with no PIN gets the offer of one (Instagram and Facebook can't open Google
     sign-in, a PIN works there), and a forgotten PIN is replaced. The Google session sets it. */
  function siRenderAddPin(errorMsg){
    _siStep = 5;
    var box = siShell().querySelector('#hoh-yh-lb-box');
    box.innerHTML = siX() +
      '<h2 class="hoh-yh-lb-title">' + (_siReset ? 'Pick a new PIN' : 'Add a PIN') + '</h2>' +
      '<p class="hoh-yh-lb-lead">' + (_siReset
        ? 'Signed in with Google as <b>' + esc(_siEmail) + '</b>. Your new PIN signs you in anywhere in the Hall.'
        : 'You\'re signed in with Google. Google sign-in doesn\'t open inside Instagram or Facebook, so a 4-digit PIN gets you in there.') + '</p>' +
      '<form class="hoh-yh-lb-form" onsubmit="event.preventDefault();hohYhSignInAddPinStep();">' +
        '<label class="hoh-yh-lb-label" for="hoh-yh-pin-1">' + (_siReset ? 'New 4-digit PIN' : 'Pick a 4-digit PIN') + '</label>' +
        siPinRow() +
        (errorMsg ? '<div class="hoh-yh-lb-error" role="alert">' + esc(errorMsg) + '</div>' : '') +
        '<button type="submit" class="hoh-yh-lb-go" id="hoh-yh-lb-go">Save PIN</button>' +
      '</form>' +
      '<p class="hoh-yh-lb-foot"><button type="button" class="hoh-yh-lb-link" onclick="hohYhSignInSkipPin()">Not now</button></p>';
    siWirePins();
  }
  window.hohYhSignInAddPinStep = async function(){
    if(_siBusy) return;
    var pin = siPinValue();
    if(!/^\d{4}$/.test(pin)){ siRenderAddPin('Pick four digits.'); return; }
    var g = window.hohGoogle, session = g ? g.sessionFor(_siEmail) : '';
    if(!session){ siOk(); return; }
    siBusy(true, 'Saving…');
    var saved = false;
    try { saved = await g.setPin(_siEmail, session, pin); } catch(_){}
    siBusy(false);
    if(!saved){ siRenderAddPin('That PIN did not save. Try again in a moment.'); return; }
    /* the PIN they just picked is the sign-in now: it also opens the pages that only take a PIN */
    setStoredEmail(_siEmail);
    setStoredToken(_siEmail, pin);
    siOk();
  };
  window.hohYhSignInSkipPin = function(){
    try { localStorage.setItem(PIN_ASKED_KEY, String(Date.now())); } catch(_){}
    siOk();
  };
  function siPinAskedLately(){
    try { return Date.now() - (+localStorage.getItem(PIN_ASKED_KEY) || 0) < 30 * 86400000; } catch(_){ return false; }
  }

  /* Back from Google (hoh-google-signin.js has already kept the session, or set it aside when the email has no
     hero yet): say what happened, here in the Hall. */
  function siGoogleResult(res){
    if(!res || res.from !== 'hall') return;
    _siFrom = document.body.classList.contains('hoh-page-mode-yours') ? 'page' : 'hall';
    currentView = 'characters';
    _siEmail = String(res.email || getStoredEmail() || '').trim().toLowerCase();
    _siGoogle = false;
    _siReset = !!res.reset;
    if(!res.ok){ siRenderEmail(res.reason === 'cancel' ? '' : (res.message || '')); return; }
    if(!res.heroes){ siRenderNoHero(); return; }
    if(_siReset || (!res.has_pin && !siPinAskedLately())){ siRenderAddPin(''); return; }
    siOk();
  }
  if(window.hohGoogle) window.hohGoogle.onResult(siGoogleResult);

  function siPinRow(){
    return '<div class="hoh-yh-lb-pins">' +
      '<input class="hoh-yh-lb-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-yh-pin-1" autocomplete="off" aria-label="PIN digit 1" />' +
      '<input class="hoh-yh-lb-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-yh-pin-2" autocomplete="off" aria-label="PIN digit 2" />' +
      '<input class="hoh-yh-lb-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-yh-pin-3" autocomplete="off" aria-label="PIN digit 3" />' +
      '<input class="hoh-yh-lb-pin" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" id="hoh-yh-pin-4" autocomplete="off" aria-label="PIN digit 4" />' +
    '</div>';
  }
  function siPinValue(){
    return ['hoh-yh-pin-1','hoh-yh-pin-2','hoh-yh-pin-3','hoh-yh-pin-4']
      .map(function(id){ return (document.getElementById(id) || {}).value || ''; }).join('');
  }
  function siWirePins(){
    var pins = ['hoh-yh-pin-1','hoh-yh-pin-2','hoh-yh-pin-3','hoh-yh-pin-4'].map(function(id){ return document.getElementById(id); });
    pins.forEach(function(inp, i){
      if(!inp) return;
      inp.addEventListener('input', function(e){
        var v = (e.target.value || '').replace(/\D/g, '').slice(0, 1);
        e.target.value = v;
        if(v && pins[i+1]) pins[i+1].focus();
        /* four digits in and there is nothing left to decide, so submit —
           on a phone that saves hunting for a button under the keyboard */
        if(v && i === 3 && siPinValue().length === 4){
          if(_siStep === 2) window.hohYhSignInPinStep();
          else if(_siStep === 3) window.hohYhSignInClaimStep();
          else if(_siStep === 5) window.hohYhSignInAddPinStep();
        }
      });
      inp.addEventListener('keydown', function(e){
        if(e.key === 'Backspace' && !inp.value && pins[i-1]) pins[i-1].focus();
      });
      inp.addEventListener('paste', function(e){
        var pasted = ((e.clipboardData || window.clipboardData).getData('text') || '').replace(/\D/g, '');
        if(pasted.length >= 4){
          e.preventDefault();
          for(var k = 0; k < 4 && k < pins.length; k++) pins[k].value = pasted.charAt(k);
          if(pins[3]) pins[3].focus();
          if(_siStep === 2) window.hohYhSignInPinStep();
          else if(_siStep === 3) window.hohYhSignInClaimStep();
          else if(_siStep === 5) window.hohYhSignInAddPinStep();
        }
      });
    });
    if(pins[0]) setTimeout(function(){ try { pins[0].focus(); } catch(_){} }, 120);
  }
  /* Every server call on this screen took an unknown amount of time and said
     nothing while it ran, which on a slow phone reads as a dead button. */
  function siBusy(on, label){
    _siBusy = !!on;
    var b = document.getElementById('hoh-yh-lb-go');
    if(!b) return;
    b.disabled = !!on;
    b.classList.toggle('hoh-yh-lb-go-busy', !!on);
    if(on){ b.setAttribute('data-idle', b.textContent); b.textContent = label || 'Checking…'; }
    else if(b.getAttribute('data-idle')){ b.textContent = b.getAttribute('data-idle'); }
  }
  function siOk(){
    /* the token is written by the caller; this is only what happens after */
    siClose();
    try { if(typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle(); } catch(_){}
    if(_siFrom === 'page'){
      try { renderDashboard(); } catch(_){}
      return;
    }
    /* They signed in FROM the Hall, so they stay in the Hall. The thing that
       changed is that they now have a personal hall — so it arrives, with an
       animation, instead of leaving them to notice it on their own. */
    try { window.hohYhCelebrateTab(); } catch(_){}
  }

  /* renderSignIn keeps its old name and signature: every error branch in
     this file, and the theme's login line, call it. It just opens the
     lightbox at the right step now. */
  function renderSignIn(prefillEmail, errorMsg, mode){
    _siEmail = (prefillEmail || getStoredEmail() || '').trim().toLowerCase();
    if(siSheet(_siEmail, errorMsg)) return;
    if(mode === 'setpin'){ siRenderClaim(errorMsg); return; }
    if(mode === 'needpin'){ siRenderPin(errorMsg); return; }
    siRenderEmail(errorMsg);
  }

  /* One FramedFantasy sign-in (ff-signin.js, Jan 2026-10-28: "the 4 digit pin is no longer really needed"): Google, or
     a 4-digit code by email, both of which prove the email is theirs. It keeps the session through
     hohYourHallSetSession; this only does what siOk does after. An email with no hero yet is offered the add-hero
     form. The lightbox above stays for a page without ff-signin.js. */
  function siSheet(email, errorMsg){
    var sheet = window.ffSignIn;
    if(!sheet || typeof sheet.open !== 'function') return false;
    var lead = errorMsg && errorMsg.charAt(0) !== '_' ? esc(errorMsg) : '';
    sheet.open({ email: email || '', title: 'Sign in to Your Hall', lead: lead,
      noHero: { action: function(){ if(window.hohOpenForm) window.hohOpenForm(); } } })
      .then(function(r){ if(r && r.heroes > 0) siOk(); else siUnblank(); });
    return true;
  }

  window.hohYhSignInBack = function(){ siRenderEmail(''); };
  /* kept as a named door for anything outside this file that still calls it */
  window.hohYhSignInForgot = function(){ siRenderClaim(''); };

  /* R426 (Jan): "dont let them just make a random email and submit it — if
     it's an unrecognized email say it doesn't match and reject them. this is
     mainly for people to log BACK in."
     Exactly the hole I flagged last drop. The old branch was binary: an email
     with a PIN went to the PIN screen, and EVERYTHING ELSE was offered a
     "claim your hall" screen — including an address that has never been near
     the Hall. That turns a sign-in door into an account-minting door, and it
     is the opposite of what this screen is for.

     Three states now, not two:
       has a PIN        -> the PIN screen. Already proven known, no extra
                           lookup needed, and this is the overwhelming case
                           since the submit form asks for a PIN.
       no PIN, has heroes -> the claim screen. This is the real reason claim
                           exists: submitters from before PINs were a thing.
       no PIN, no heroes -> REJECTED on the email step, where the typo is
                           still on screen and one character from being fixed.

     WHY IT FAILS OPEN. hohYhEmailKnown returns null, not false, when it
     cannot answer — a network blip, a permissions surprise, anything. Null
     is treated as "known" so the claim screen still opens. Locking a real
     submitter out of their own hall because a count query hiccuped is far
     worse than the thing this check is closing, and the server still refuses
     set_pin on an address that already has one. */
  async function hohYhEmailKnown(email){
    /* free positive: the Hall grid is usually already in memory, so a known
       author is often confirmed with no network call at all */
    try {
      var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      for(var i = 0; i < all.length; i++){
        var c = all[i]; if(!c) continue;
        if(String(c.auth_email || '').toLowerCase() === email) return true;
        if(String(c.email || '').toLowerCase() === email) return true;
      }
    } catch(_){}
    var sb = window.hohSupabaseClient;
    if(!sb) return null;
    try {
      /* R428 — WHY THIS STOPPED BEING A COUNT.
         The first version asked PostgREST for `{count:'exact', head:true}`,
         which issues a HEAD request with a count preference. Nothing else in
         this entire theme reads that way — every proven query here is a plain
         .select(cols).eq(...) — and it came back unusable, so the check
         answered "cannot tell", the old fail-open let it through, and Jan
         landed on Claim Your Hall for an address with no heroes on it. The
         guard was there; the query under it was the unproven part.
         A one-row select is the pattern the rest of the Hall already runs
         thousands of times a day, so it either works everywhere or nothing
         works anywhere.
         Still two separate .eq() calls rather than one .or(): .or() takes a
         comma-separated expression and an email is user input, so an address
         containing a comma would corrupt the filter.
         No approved/is_hidden filter on purpose — a hero awaiting approval is
         still a hero this person submitted. */
      var q = await Promise.all([
        sb.from('characters').select('id').eq('auth_email', email).limit(1),
        sb.from('characters').select('id').eq('email', email).limit(1)
      ]);
      var found = q.some(function(r){ return r && !r.error && Array.isArray(r.data) && r.data.length > 0; });
      if(found) return true;
      var err = q.some(function(r){ return !r || r.error; });
      if(err) return null;    /* asked and could not be answered */
      return false;           /* asked, answered, nobody there */
    } catch(_){ return null; }
  }

  /* Step 1 submit — the email check that decides which screen comes next. */
  window.hohYhSignInEmailStep = async function(){
    if(_siBusy) return;
    var el = document.getElementById('hoh-yh-email');
    var email = ((el && el.value) || '').trim().toLowerCase();
    if(!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)){
      _siEmail = email; siRenderEmail('Enter a valid email address.'); return;
    }
    _siEmail = email;
    var sb = window.hohSupabaseClient;
    if(!sb){ siRenderEmail('The Hall is unreachable right now. Refresh and try again.'); return; }
    siBusy(true, 'Checking…');
    try {
      /* R428 (Jan): "make sure there is a submitted character to move to the
         pin stage." A hero on the address is now the gate on BOTH roads, not
         just the claim one — no character, no PIN screen, no PIN setting,
         nothing. This door is for people coming BACK.
         Both questions go out together so it stays one round trip: does this
         address own a hero, and does it already have a PIN. */
      var pair = await Promise.all([
        hohYhEmailKnown(email),
        sb.rpc('email_has_pin', { user_email: email }).then(function(r){ return r; }, function(){ return null; }),
        /* HALL-GOOGLE-01: has this email signed in with Google? (false when it can't tell) */
        window.hohGoogle ? window.hohGoogle.hint(email).catch(function(){ return false; }) : Promise.resolve(false)
      ]);
      var known = pair[0], res = pair[1];
      _siGoogle = pair[2] === true;
      siBusy(false);
      /* FAILS CLOSED NOW. It used to let an unverified address through to the
         claim screen so a network hiccup could never lock a real submitter
         out — but that same door let a made-up address mint a PIN, which is
         the thing being closed. An unverifiable address is refused too, with
         its own wording, because "we could not check" and "there is nobody
         here" are different problems and a person can only act on one of
         them. Nobody's existing hall is at risk either way: a real submitter
         with a PIN still signs in the moment the check can answer. */
      if(known === null){ siRenderEmail('__cantcheck__'); return; }
      if(known === false){ siRenderEmail('__nomatch__'); return; }
      if(res && res.data === true){ siRenderPin(''); return; }
      /* an email that signs in with Google and has no PIN is offered Google first (it can still pick a PIN there) */
      if(_siGoogle){ siRenderGoogleOnly(); return; }
      siRenderClaim('');
    } catch(e){
      siBusy(false);
      siRenderEmail('Could not check that email. Try again in a moment.');
    }
  };

  /* Step 2 submit — verify. */
  window.hohYhSignInPinStep = async function(){
    if(_siBusy) return;
    var pin = siPinValue();
    if(pin.length !== 4 || !/^\d{4}$/.test(pin)){ siRenderPin('Enter all four digits.'); return; }
    var waitMs = rlCheck(_siEmail);
    if(waitMs > 0){ siRenderPin(rlMessage(waitMs)); return; }
    var sb = window.hohSupabaseClient;
    if(!sb){ siRenderPin('The Hall is unreachable right now. Refresh and try again.'); return; }
    siBusy(true, 'Signing in…');
    try {
      var res = await sb.rpc('verify_pin', { user_email: _siEmail, user_pin: pin });
      var matchCount = (res && typeof res.data === 'number') ? res.data : 0;
      if(!matchCount){
        rlRecordFail(_siEmail);
        var wait2 = rlCheck(_siEmail);
        siBusy(false);
        siRenderPin(wait2 > 0 ? rlMessage(wait2) : 'That PIN does not match this email.');
        return;
      }
      rlClear(_siEmail);
      setStoredEmail(_siEmail);
      setStoredToken(_siEmail, pin);
      siOk();
    } catch(e){
      siBusy(false);
      siRenderPin('Sign in failed. Try again in a moment.');
    }
  };

  /* Step 3 submit — claim. */
  window.hohYhSignInClaimStep = async function(){
    if(_siBusy) return;
    var pin = siPinValue();
    if(pin.length !== 4 || !/^\d{4}$/.test(pin)){ siRenderClaim('Pick four digits.'); return; }
    var sb = window.hohSupabaseClient;
    if(!sb){ siRenderClaim('The Hall is unreachable right now. Refresh and try again.'); return; }
    siBusy(true, 'Claiming…');
    try {
      var setRes = await sb.rpc('set_pin', { user_email: _siEmail, old_pin: null, new_pin: pin });
      if(!setRes || setRes.error || setRes.data === false){
        siBusy(false);
        siRenderPin('This email already has a PIN. Enter it below.');
        return;
      }
      setStoredEmail(_siEmail);
      setStoredToken(_siEmail, pin);
      siOk();
    } catch(e){
      siBusy(false);
      siRenderClaim('Could not set that PIN. Try again in a moment.');
    }
  };

  /* The old single-shot entry point kept working for anything still calling
     it — it now just routes into the two steps above. */
  window.hohYourHallSignIn = function(mode){
    if(mode === 'setpin') return window.hohYhSignInClaimStep();
    if(_siStep === 2) return window.hohYhSignInPinStep();
    if(_siStep === 3) return window.hohYhSignInClaimStep();
    return window.hohYhSignInEmailStep();
  };

  /* Escape closes, like every other overlay in the Hall. */
  document.addEventListener('keydown', function(e){
    if(e.key !== 'Escape') return;
    if(document.getElementById('hoh-yh-signin-lb')) siClose();
  });

  /* ============================================================
     PIN BRUTE-FORCE RATE LIMIT (client-side)
     ============================================================
     A 4-digit PIN is only 10,000 combinations. Without a throttle,
     an attacker who knows a victim's email could try every PIN in
     a few minutes. Real protection has to live in the verify_pin
     RPC server-side (per-email lockout in the DB), but we slow the
     attack from the client too:
       • track failed verify attempts per (email, sessionStorage)
       • after 3 fails  → 5s cooldown before next attempt
       • after 5 fails  → 30s cooldown
       • after 10 fails → 5min lockout
     Cleared on successful sign-in. sessionStorage means a fresh
     tab resets the counter, so this isn't a hard wall — but it
     stops the trivial in-tab brute-force loop and gives the user
     visible feedback that they're being throttled. */
  var SIGNIN_RL_KEY = 'hoh-signin-rl-v1';
  function rlGet(){
    try {
      var raw = sessionStorage.getItem(SIGNIN_RL_KEY);
      if(!raw) return { attempts: {}, lockedUntil: 0 };
      var o = JSON.parse(raw);
      if(!o || typeof o !== 'object') return { attempts: {}, lockedUntil: 0 };
      if(typeof o.attempts !== 'object' || !o.attempts) o.attempts = {};
      if(typeof o.lockedUntil !== 'number') o.lockedUntil = 0;
      return o;
    } catch(e){ return { attempts: {}, lockedUntil: 0 }; }
  }
  function rlSet(o){
    try { sessionStorage.setItem(SIGNIN_RL_KEY, JSON.stringify(o)); } catch(e){}
  }
  function rlCooldownMsFor(n){
    if(n >= 10) return 5 * 60 * 1000;   /* 5 minutes */
    if(n >= 5)  return 30 * 1000;        /* 30 seconds */
    if(n >= 3)  return 5 * 1000;         /* 5 seconds */
    return 0;
  }
  /* Returns 0 if allowed, OR the number of ms the user must wait. */
  function rlCheck(email){
    var rl = rlGet();
    var now = Date.now();
    if(rl.lockedUntil && now < rl.lockedUntil) return rl.lockedUntil - now;
    var rec = rl.attempts[email] || { count: 0, nextAt: 0 };
    if(rec.nextAt && now < rec.nextAt) return rec.nextAt - now;
    return 0;
  }
  function rlRecordFail(email){
    var rl = rlGet();
    var rec = rl.attempts[email] || { count: 0, nextAt: 0 };
    rec.count = (rec.count || 0) + 1;
    var cd = rlCooldownMsFor(rec.count);
    rec.nextAt = Date.now() + cd;
    rl.attempts[email] = rec;
    if(rec.count >= 10) rl.lockedUntil = Date.now() + (5 * 60 * 1000);
    rlSet(rl);
  }
  function rlClear(email){
    var rl = rlGet();
    if(email && rl.attempts[email]) delete rl.attempts[email];
    rl.lockedUntil = 0;
    rlSet(rl);
  }
  function rlMessage(ms){
    if(ms <= 0) return '';
    var s = Math.ceil(ms / 1000);
    if(s < 60) return 'Too many attempts. Try again in ' + s + 's.';
    var m = Math.ceil(s / 60);
    return 'Too many attempts. Try again in ' + m + ' min.';
  }

  /* R425: the legacy single-screen submit handler lived here. It read the
     email and all four PIN boxes off one form and branched on what happened
     to be filled in, which is exactly the guesswork the two-step lightbox
     removes. Its three server calls moved intact into
     hohYhSignInEmailStep / PinStep / ClaimStep above; the throttle helpers
     it used (rlCheck / rlRecordFail / rlClear / rlMessage) are unchanged and
     still guard the verify path. */

  /* ============ DASHBOARD (in-page) ============ */
  /* Per-sub-view filter state. Each sub-tab maintains its own search
     and sort so switching between tabs doesn't reset the user's
     filtering work.

     Tab map (v2.1 hub):
       characters       Profile — signed-in submitted characters + tickets
       cast             saved heroes with role tags + location filter
       party            named parties with 6-slot drag editor
       satchel          DM tools hub (Scene / Tavern / Company)

     The legacy 'characters' key is the internal name for the
     Profile tab so the existing loadCharacters / renderFilterBar /
     renderGridContent code path keeps working untouched.

     Removed in v2.1 (folded into Satchel):
       scenes_taverns   → satchel sub-mode 'scene' or 'tavern'
       companies        → satchel sub-mode 'company' */
  var currentView = 'characters';
  var FILTER_STATE = {
    cast:           { search: '', race: '', cls: '', sort: 'order', role: '', location: '' },
    party:          {},
    satchel:        {},
    shop:           {},
    profile:        { search: '', race: '', cls: '', sort: 'most' },
    /* Preserved so the legacy renderGridContent path still works for
       the Profile tab, which reuses the old Your Characters loader.
       Profile gets an additional `show` field that toggles which
       slice of the combined feed (submitted + hearted) is rendered. */
    characters:     { search: '', race: '', cls: '', sort: 'most', show: 'submitted' },
    hearted:        { search: '', race: '', cls: '', sort: 'mine' }
  };
  /* Cached source rows per view so filter changes don't re-fetch. */
  var SOURCE_ROWS = { cast: null, characters: null, hearted: null };

  window.hohYourHallSwitchView = function(name){
    /* Tab alias map. The legacy "characters" key is preserved as the
       internal name for the Profile tab so the existing loadCharacters
       / renderFilterBar / renderGridContent / SOURCE_ROWS.characters /
       FILTER_STATE.characters paths keep working untouched.

       Old (v2) tab names map into Satchel sub-modes for back compat
       with any deeplinks: scenes_taverns/scene/tavern → satchel,
       companies/company → satchel. The Satchel renderer reads
       meta.lastSatchelMode to land on the right sub-tool. */
    if(name === 'hearted' || name === 'witnessed') name = 'cast';
    if(name === 'profile') name = 'characters';
    /* R245: My Party merged into the Satchel — route any party request to
       the Satchel, where the party builder now lives at the top. */
    if(name === 'party') name = 'satchel';
    var subHint = null;
    if(name === 'scenes_taverns' || name === 'scene'){ subHint = 'scene'; name = 'satchel'; }
    else if(name === 'tavern'){ subHint = 'tavern'; name = 'satchel'; }
    else if(name === 'companies' || name === 'company'){ subHint = 'company'; name = 'satchel'; }
    /* R231: cast (My Favorites) retired. R245: party folded into satchel. */
    /* R234: shop = card packs + collection (hoh-shop.js). */
    /* R380: satchel is folded into Profile, events is new. Legacy names
       (satchel, party, cast, tavern, scene, company) all land on Profile. */
    if(name === 'satchel') name = 'characters';
    var allowed = { characters:1, events:1, shop:1 };
    currentView = allowed[name] ? name : 'characters';
    /* Anonymous gate: My Favorites / My Party / Satchel are signed-in
       only. If the caller asks for one while the user has no token,
       force back to Profile so they see the sign-in screen. The pill
       clicks already route to profile when locked, but this catches
       any programmatic call too (deep links, tour buttons, etc). */
    try {
      if(currentView !== 'characters' && !getStoredToken()){
        currentView = 'characters';
      }
    } catch(_){}
    try {
      var saved = JSON.parse(localStorage.getItem('hoh-saved-v1') || '{}');
      saved.meta = saved.meta || {};
      saved.meta.lastUsedTab = currentView;
      if(subHint) saved.meta.lastSatchelMode = subHint;
      localStorage.setItem('hoh-saved-v1', JSON.stringify(saved));
    } catch(_){}
    /* R216: lightweight tab switch. Update only the active pill highlight and the
       tab content, not the whole dashboard. The old full renderDashboard rebuilt
       the header, re-moved the Explore section, and jerked the screen up to it on
       every tab click. Fall back to a full render only if the dashboard is not
       built yet (programmatic deep links before first open). */
    var _host = document.getElementById('hoh-yh-tab-content');
    if(!_host || bgwRetired()){ renderDashboard(); return; }   /* R796 opening: the open world goes to the hub */
    try {
      var _pv = { profile:'characters', events:'events', shop:'shop' };
      var _pills = document.querySelectorAll('.hoh-yh-subtabs .hoh-yh-subtab');
      for(var _i=0;_i<_pills.length;_i++){
        var _k = _pills[_i].getAttribute('data-yh-subtab');
        if(_pv[_k] === currentView) _pills[_i].classList.add('hoh-yh-subtab-active');
        else _pills[_i].classList.remove('hoh-yh-subtab-active');
      }
    } catch(_){}
    renderActiveTab(getStoredToken());
  };

  /* ============ HALL TAGLINE ============
     Static themed subtitle that explains what Your Hall is for. Used
     to be a personalized "weekly archetype" read computed from the
     user's character pool — that was retired (per user request) in
     favor of a single themed line so the page reads consistently
     for everyone and there's no per-user computation cost. The
     archetype templates below are kept in source for reference /
     possible future revival but are no longer rendered. */
  /* R381 (Jan): the hearted line described the old hall. New line matches
     the Profile / Events / Shop structure. */
  var HALL_TAGLINE = 'Your heroes and everything they’ve earned.';
  function thisIsoWeek(){
    var d = new Date();
    var t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    var day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    var yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    var weekNo = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
    return t.getUTCFullYear() + '-W' + String(weekNo).padStart(2, '0');
  }
  /* The archetype templates. Each entry has:
       match(stats) → returns true if stats fit this archetype
       lines       → 2-3 thematic one-liners; pick deterministically
                     from the week-key so it doesn't change mid-week.
     Templates are checked in order; the first match wins. The
     final "wanderer" entry catches anyone who doesn't fit a
     specific pattern. */
  var ARCHETYPES = [
    { key:'scholar', test:function(s){ return s.topClass === 'Wizard' || s.topClass === 'Artificer'; },
      lines:['The Scholar — drawn to study, schools, and heroes who read before they swing.',
             'A reader of magic. Your party always has someone who knows the lore.',
             'Books before blades. You collect minds that bend reality.'] },
    { key:'voice', test:function(s){ return s.topClass === 'Bard'; },
      lines:['The Storyteller — you love heroes with stories on their lips and stages in their bones.',
             'A patron of bards. Every adventure deserves a song afterward.',
             'You build the parties that get sung about, not just the ones that win.'] },
    { key:'shadow', test:function(s){ return s.topClass === 'Rogue' || s.topClass === 'Blood Hunter'; },
      lines:['The Shadowed Hand — drawn to clever fingers, quieter footfalls, and longer shadows.',
             'You favor the ones who slip through. Doors are inconveniences, not obstacles.',
             'Your heroes work better in the dark. The light comes after.'] },
    { key:'oath', test:function(s){ return s.topClass === 'Paladin' || s.topClass === 'Cleric'; },
      lines:['The Oath-Keeper — you build heroes who carry the weight of vows and quiet faith.',
             'Steel and stained glass. You collect the ones who stand for something.',
             'Your parties hold the line. Always have, always will.'] },
    { key:'wild', test:function(s){ return s.topClass === 'Druid' || s.topClass === 'Ranger'; },
      lines:['The Wild Heart — your heroes belong to the trees, the trails, and the long quiet.',
             'You favor the ones who listen. Wolves recognize them; cities do not.',
             'Roots and ranges. Your party always has someone who knows the way home.'] },
    { key:'fury', test:function(s){ return s.topClass === 'Barbarian' || s.topClass === 'Fighter'; },
      lines:['The Steady Sword — a taste for heroes who solve problems with steel and steadiness.',
             'You build the front line. The ones who absorb the hit so the others can swing.',
             'Practical, plate-armored, and patient. Your parties rarely panic.'] },
    { key:'spark', test:function(s){ return s.topClass === 'Sorcerer' || s.topClass === 'Warlock'; },
      lines:['The Spark — drawn to heroes born with the gift, or the ones who paid for it.',
             'You collect the ones who burn brightly. The price tag rarely matters.',
             'Your magic is interesting because it costs something. You like that.'] },
    { key:'monk', test:function(s){ return s.topClass === 'Monk'; },
      lines:['The Stillness — your heroes leave their boots at the door and their fists at the ready.',
             'Discipline over flash. Your favorites finish fights before they start.',
             'Quiet, fast, and rarely outclassed. Your parties land softly.'] },
    { key:'bloodline', test:function(s){ return s.topRace === 'Tiefling' || s.topRace === 'Dragonborn' || s.topRace === 'Aasimar'; },
      lines:['The Inheritor — you favor heroes whose blood remembers something old.',
             'Horns, scales, halos. Your favorites carry weight before they speak.',
             'Long lineages. Your parties always have one with a story written before they were born.'] },
    { key:'longblood', test:function(s){ return s.topRace === 'Elf' || s.topRace === 'Half-Elf' || s.topRace === 'Drow'; },
      lines:['The Old Soul — drawn to long-lived kin and the slow weight of centuries.',
             'You favor the ones who remember what others forget. Time is your party\'s favorite ally.',
             'Long ears, longer memories. Your heroes know what waits.'] },
    { key:'small', test:function(s){ return s.topRace === 'Halfling' || s.topRace === 'Gnome' || s.topRace === 'Goblin' || s.topRace === 'Harengon'; },
      lines:['The Small Folk Champion — you favor the quick, the clever, and the ones who eat second breakfast.',
             'Underestimated, undersized, undefeated. Your favorites have the best stories.',
             'You build the heroes others overlook. Then you watch them win.'] },
    { key:'mythic', test:function(s){ return s.avgHearts >= 60; },
      lines:['The Tastemaker — your heroes climb tiers fast. The Hall remembers what you submit.',
             'You build the ones who get hearted. There\'s a pattern in your eye for character.',
             'Other players notice your roster. That\'s a real skill.'] },
    { key:'collector', test:function(s){ return s.totalChars >= 3; },
      lines:['The Collector — you don\'t pick a type. You build a roster.',
             'Every door opens differently for you. Your party isn\'t a party — it\'s a portfolio.',
             'You like variety. Different races, classes, stories. The Hall is wider for it.'] },
    { key:'beginner', test:function(s){ return s.totalChars <= 1; },
      lines:['The First Chapter — your story in the Hall is just beginning.',
             'One hero in. The next will tell us more about you.',
             'You\'re early. Submit a few more, the read gets sharper.'] },
    { key:'wanderer', test:function(){ return true; },
      lines:['The Wanderer — your taste is harder to pin down. You like what you like.',
             'Eclectic. Your party reads like a stranger\'s playlist — and that\'s a compliment.',
             'You don\'t build to a type. You build to a feeling. The Hall is wider for it.'] }
  ];
  /* Compute character-pool stats and pick the matching archetype line.
     Now hard-returns the static HALL_TAGLINE — the personalized read
     was retired. The body below is preserved as dead code so if we
     ever want to bring the archetype picker back, the logic is intact. */
  function computeWeeklyArchetype(){
    return HALL_TAGLINE;
    /* eslint-disable */
    var weekKey = thisIsoWeek();
    var cacheKey = 'hoh-yh-archetype-' + weekKey;
    try {
      var cached = localStorage.getItem(cacheKey);
      if(cached) return cached;
    } catch(e){}
    /* Combine the user's submitted chars + hearted chars for a richer
       picture. SOURCE_ROWS.characters is loaded by the dashboard;
       hoh-hearts gives the hearted set. */
    var pool = [];
    if(SOURCE_ROWS.characters) pool = pool.concat(SOURCE_ROWS.characters);
    try {
      var raw = localStorage.getItem('hoh-hearts');
      if(raw){
        var heartMap = JSON.parse(raw) || {};
        var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
        Object.keys(heartMap).forEach(function(id){
          if((heartMap[id] || 0) > 0){
            var c = all.find(function(x){ return x && x.id === id; });
            if(c) pool.push(c);
          }
        });
      }
    } catch(e){}
    var raceCount = {};
    var classCount = {};
    var totalHearts = 0;
    pool.forEach(function(c){
      if(!c) return;
      if(c.race) raceCount[c.race] = (raceCount[c.race] || 0) + 1;
      if(c.character_class) classCount[c.character_class] = (classCount[c.character_class] || 0) + 1;
      totalHearts += c.hearts || 0;
    });
    function topOf(map){
      var top = null, n = 0;
      Object.keys(map).forEach(function(k){
        if(map[k] > n){ n = map[k]; top = k; }
      });
      return top;
    }
    var stats = {
      totalChars: (SOURCE_ROWS.characters || []).length,
      topRace: topOf(raceCount),
      topClass: topOf(classCount),
      avgHearts: pool.length ? Math.round(totalHearts / pool.length) : 0
    };
    var pick = ARCHETYPES.find(function(a){ return a.test(stats); }) || ARCHETYPES[ARCHETYPES.length - 1];
    /* Deterministic line index based on week-key so the same week
       always shows the same line; switches each week. */
    var hash = 0;
    for(var i = 0; i < weekKey.length; i++) hash = ((hash << 5) - hash) + weekKey.charCodeAt(i) | 0;
    var line = pick.lines[Math.abs(hash) % pick.lines.length];
    /* User preference: never em/en dashes in the tagline. The line
       templates use " — " between the title and the read. Swap them
       for a colon so the read still feels like one breath. Also strip
       any stray en-dashes for safety. */
    line = String(line).replace(/\s[—–]\s/g, ': ').replace(/[—–]/g, '');
    /* Only cache when we actually have data — otherwise the very
       first render (before SOURCE_ROWS.characters loads) would
       lock the user into a "First Chapter" read for the week. */
    if(pool.length > 0){
      try { localStorage.setItem(cacheKey, line); } catch(e){}
    }
    return line;
  }
  /* Refresh just the archetype line in-place. Called after the
     character list loads so we don't rebuild the whole dashboard. */
  function refreshArchetype(){
    var el = document.getElementById('hoh-yh-archetype');
    if(!el) return;
    el.textContent = computeWeeklyArchetype();
  }

  function renderDashboard(){
    var body = getPageContainer();
    if(!body) return;
    /* v38 (Cortez, by email): "There doesn't seem to be a place to log in."
       He is right, and the reason is worth writing down.
       renderSignIn has twelve call sites. ELEVEN of them are error re-renders
       inside the sign-in submit handler itself, which you can only reach if you
       are already looking at the form. The twelfth fires only when a token
       exists but verify_pin comes back 0, which is an expired session. So there
       has never been a path from "signed out" to "the form", and the anon
       modal's own button, the one that literally says "I already have a hero.
       Sign me in.", calls hohOpenYourHall, which lands here and renders a hall
       with nobody in it.
       This is that path. A visitor with no token who asks for Your Hall gets
       the sign-in, which is what asking for Your Hall means when you are signed
       out.

       R472 CORRECTION: the sentence that used to end this comment said
       renderSignIn "always draws its own Back to the Hall link, so this cannot
       trap anyone". That stopped being true at R425, which moved sign-in into
       a lightbox appended to <body>. renderSignIn writes NOTHING into
       #hoh-yh-page, so this early return leaves that container empty while
       body.hoh-page-mode-yours hides the rest of the page. It could trap
       someone, and it did. siUnblank below is the net. */
    if(!getStoredToken()){
      try { renderSignIn(getStoredEmail() || '', '', 'enter'); return; }
      catch(_){}
      finally { try{ siUnblank(); }catch(_2){} }
    }
    /* R796 opening: while the world is open the old game is not drawn; every door into
       it comes back to the hub's Enter the Battlegrounds row */
    if(bgwRetired()){ bgwBackToHub(); return; }
    /* R215: Explore the Hall (#hoh-fresh-faces) now lives INSIDE the dashboard,
       between the stats and Add a Hero. Detach it before the innerHTML rebuild so
       the node and its built state survive, then re-home it into the slot below. */
    var _ff = document.getElementById('hoh-fresh-faces');
    if(_ff && _ff.parentNode){ try{ _ff.parentNode.removeChild(_ff); }catch(_){} }
    /* R226: Explore the Hall always starts open when the profile page is visited. The user can
       still minimize it during the session, but the collapsed state is no longer restored on
       load, so every visit lands with it open. */
    var _exploreCollapsed = false;
    /* R148-Z22p: Fire passive-XP catchup on every dashboard render.
       Internally cached 5min so we're not pounding the RPC — first
       render gates the call. Placed HERE (not just on
       hohOpenYourHall) because loadCharacters is async; SOURCE_ROWS
       can be empty when the user first opens Your Hall, populated
       later by loadCharacters, and renderDashboard fires again on
       that completion. By living here, the catchup picks up
       whichever render has populated rows. */
    try { if(typeof window.hohYhCatchupPassiveXp === 'function') window.hohYhCatchupPassiveXp(); } catch(_){}
    var token = getStoredToken();
    var name = (typeof window.hohYourHallDisplayName === 'function') ? window.hohYourHallDisplayName() : '';
    var displayName = name || 'Your';
    var title = 'The Hall of ' + esc(displayName);
    var archetype = computeWeeklyArchetype();

    /* The 4-tab hub (v2.1). Profile leads so a returning user can
       sign in immediately. Satchel collapses Scene/Tavern/Company
       into one tab with sub-modes.

       The pill id maps to currentView via the SwitchView aliasing:
       'profile' pill → currentView 'characters' so the legacy data
       path stays intact. */
    var pillToView = {
      profile: 'characters',
      events:  'events',
      shop:    'shop'
    };
    /* R231: My Favorites tab removed. Favoriting stays as the star on a
       card, surfaced through the Favorited only option in the Show
       filter, so it no longer needs a whole tab. */
    /* R245: My Party merged into the Satchel (it renders at the top of the
       Satchel now), so it no longer needs its own tab. */
    /* R380 (2026-08-06, Jan): three doors. Profile is home, Events is the
       daily loop (Explore + the Battlegrounds), Shop sells packs and
       Battlegrounds Tickets. */
    var tabs = [
      ['profile', 'Profile'],
      ['events',  'Events'],
      ['shop',    'Shop']
    ];
    /* When the user isn't signed in, only the Profile pill is
       interactive (it shows the sign-in screen). The other three
       become locked pills with a subtle padlock affordance — clicking
       routes back to Profile so the user knows where to go to unlock
       the rest. Without this gate the user could land on My Favorites
       (empty), My Party (empty), or Satchel (limited tools) and not
       know they were missing the auth step. */
    var isSignedIn = !!token;
    var tabsHtml = tabs.map(function(t){
      var view = pillToView[t[0]];
      var active = (currentView === view) ? ' hoh-yh-subtab-active' : '';
      var locked = (!isSignedIn && t[0] !== 'profile');
      var lockedCls = locked ? ' hoh-yh-subtab-locked' : '';
      var label = esc(t[1]) + (locked ? ' <span class="hoh-yh-subtab-lock" aria-hidden="true">&#x1f512;</span>' : '');
      var clickAction = locked
        ? 'hohYourHallSwitchView(\'profile\')'
        : 'hohYourHallSwitchView(\'' + t[0] + '\')';
      var titleAttr = locked ? ' title="Sign in to unlock"' : '';
      return '<button type="button" class="hoh-yh-subtab' + active + lockedCls + '" data-yh-subtab="' + t[0] + '"' + titleAttr + ' onclick="' + clickAction + '">' + label + '</button>';
    }).join('');

    body.innerHTML =
      '<div class="hoh-yh-dash hoh-yh-dash-page hoh-yh-dash-' + currentView + '">' +
        '<div class="hoh-yh-dash-head">' +
          '<h1 class="hoh-yh-title hoh-yh-title-big">' + title + '</h1>' +
          '<p class="hoh-yh-archetype" id="hoh-yh-archetype">' + esc(archetype) + '</p>' +
          '<div class="hoh-yh-summary" id="hoh-yh-summary" aria-live="polite"></div>' +
        '</div>' +
        '<div class="hoh-yh-add-hero-wrap">' +
          '<button class="hoh-btn-primary hoh-btn-glow hoh-yh-add-hero-btn" onclick="hohOpenForm()">' +
            '<span class="hoh-cta-spark" aria-hidden="true"></span>' +
            '<span class="hoh-cta-label">Add a Hero</span>' +
          '</button>' +
        '</div>' +
        '<div class="hoh-yh-subtabs hoh-yh-subtabs-multi" role="tablist">' + tabsHtml + '</div>' +
        '<div id="hoh-yh-tab-content" class="hoh-yh-tab-content"></div>' +
        /* R380: the parking bay. When the Events tab is not on screen the
           live Explore node waits here, hidden, with all of its state. */
        '<div id="hoh-yh-explore-park" style="display:none" aria-hidden="true"></div>' +
      '</div>';
    /* R380: park the live Explore node. renderActiveTab moves it into the
       Events tab when that tab is on screen. It is never rebuilt. */
    if(_ff){ var _park = document.getElementById('hoh-yh-explore-park'); if(_park) _park.appendChild(_ff); }
    /* Render the ticket summary line. Counts come from cached ticket
       state where available; falls back gracefully when offline. */
    renderSummary();
    /* Dispatch tab content. Each renderer owns its own DOM under
       #hoh-yh-tab-content. */
    renderActiveTab(token);
  }
  /* R217: minimize / restore the Explore the Hall panel. We HIDE the live section
     instead of destroying it, so its exact state survives — a shop stays a shop —
     and the countdown pauses while hidden. The choice persists across visits. */
  window.hohYhToggleExplore = function(){
    /* R380: the collapsible header panel is gone. Explore lives on the
       Events tab now, so the old toggle just takes you there. */
    try { window.hohYourHallSwitchView('events'); } catch(_){}
  };
  /* R380: the two explore movers. The node is a singleton with live state,
     so it MOVES between the Events tab and the hidden parking bay and is
     never destroyed or rebuilt. */
  function parkExplore(){
    var ff = document.getElementById('hoh-fresh-faces');
    var park = document.getElementById('hoh-yh-explore-park');
    if(ff && park && ff.parentNode !== park){
      park.appendChild(ff);
      try { if(window.hohFreshFacesStopTimer) window.hohFreshFacesStopTimer(); } catch(_){}
    }
    /* R380 AUDIT FIX: a battle host sitting in the tab content (mounted but
       not yet focused, or resting after a fight) must survive a tab switch
       the same way Explore does. While a fight is FOCUSED the host lives in
       the fixed overlay, not here, so this only ever moves a dormant host. */
    try {
      var ct = document.getElementById('hoh-contract');
      var tc = document.getElementById('hoh-yh-tab-content');
      if(ct && park && tc && tc.contains(ct)) park.appendChild(ct);
    } catch(_){}
    /* leaving Events also closes an open party builder cleanly */
    if(window.__hohCollMode === 'builder'){
      window.__hohCollMode = '';
      window.__hohBuilderRepaint = null;
      document.body.classList.remove('hoh-bg-building');
    }
  }
  function moveExploreInto(panel){
    var ff = document.getElementById('hoh-fresh-faces');
    if(!ff || !panel) return;
    panel.appendChild(ff);
    try {
      requestAnimationFrame(function(){
        try { if(window.hohFreshFacesEnsureLive) window.hohFreshFacesEnsureLive(); } catch(_){}
      });
    } catch(_){ try { if(window.hohFreshFacesEnsureLive) window.hohFreshFacesEnsureLive(); } catch(_){} }
  }

  /* Tab dispatcher. The tab content area is a single child div that
     each renderer fills. Cast/Party/Satchel render from the local
     hall data layer (works without sign-in). Profile uses the
     legacy 'characters' view internally — see SwitchView aliasing. */
  /* Short, themed intro per tab — sets context so a new visitor
     understands what each tab is for without needing a tutorial.
     The renderers fully replace innerHTML, so we prepend the intro
     AFTER the renderer runs (it's safe because the intro lives at
     the top and doesn't depend on inner state). */
  function tabIntroCopy(view){
    /* R78: Dropped the party tab intro — it duplicated the panel
       header below it. The panel header now carries the full
       consolidated value-prop. */
    /* R380: three tabs, three sentences. Plain words, no lore in the intro. */
    /* R381: the events intro is gone — its sub tabs carry the names. */
    return ({
      /* R437: the section below now carries its own header and tally, so
         this line stopped introducing and started repeating. Trimmed to the
         one thing the header cannot say: what a card is FOR. */
      characters: "Tap any card to level it, roll its powers, and give it tickets.",
      shop:       "Open packs with hearts, plus one free pack every day. FramedFantasy portraits and your own heroes become cards you keep; everything else turns into hearts on the spot."
    })[view] || '';
  }
  function prependTabIntro(host, view){
    if(!host) return;
    var copy = tabIntroCopy(view);
    if(!copy) return;
    /* Don't double-prepend if the renderer already injected one. */
    if(host.querySelector(':scope > .hoh-yh-tab-intro')) return;
    var intro = document.createElement('div');
    intro.className = 'hoh-yh-tab-intro';
    intro.textContent = copy;
    host.insertBefore(intro, host.firstChild);
  }

  function renderActiveTab(token){
    var host = document.getElementById('hoh-yh-tab-content');
    if(!host) return;
    /* R380 CRITICAL ORDER: the live Explore node may be sitting inside this
       host (Events tab). Park it BEFORE any innerHTML write or it would be
       destroyed mid state, which is the one thing this rebuild must never
       do. */
    parkExplore();
    /* R380: legacy views collapse into the three that exist. Satchel and the
       old party/cast views land on Profile so no stored state can strand
       anyone on a tab that is gone. */
    if(currentView === 'cast' || currentView === 'party' || currentView === 'satchel') currentView = 'characters';
    if(currentView === 'events'){
      /* R381: no intro line here — the two sub tabs name themselves. */
      renderEventsTab(host, token);
      return;
    }
    if(currentView === 'shop'){
      if(typeof window.hohHallRenderShop === 'function') window.hohHallRenderShop(host);
      else host.innerHTML = '<div class="hoh-yh-empty">Shop loading...</div>';
      prependTabIntro(host, 'shop');
      return;
    }
    /* Profile tab — currentView === 'characters'. Requires sign-in. */
    if(currentView === 'characters'){
      if(!token){
        renderSignInInto(host);
        return;
      }
      /* R380 (2026-08-06, Jan): ONE GRID. The heart bank claim on top, then
         the card collection in the minimal builder style (every collected
         card plus every hero you own, uncollected ones grayed with the Yours
         tag), then the DM tools in one collapsed drawer. The old big-card
         grid with its own filters, stars and per-card ticket buttons is
         retired; leveling, rolls and tickets live in the card viewer now. */
      window.__hohCollMode = 'profile';
      /* R437: the bank slot is gone. Profile is now one thing — your
         collection — instead of a claim box, a tally, a button and a grid
         stacked as four unrelated strangers. */
      host.innerHTML =
        '<div id="hoh-yh-coll" class="hoh-yh-coll"><div class="hoh-yh-loading">Loading your cards...</div></div>' +
        '<div id="hoh-yh-tools" class="hoh-yh-tools"></div>';
      loadCharacters(token);
      prependTabIntro(host, 'characters');
      var collHost = host.querySelector('#hoh-yh-coll');
      if(collHost && typeof window.hohShopRenderCollection === 'function'){
        try { window.hohShopRenderCollection(collHost); } catch(_){}
      }
      if(!window.__hohYhCollSyncBound){
        window.__hohYhCollSyncBound = 1;
        /* the hero rows land after the first collection paint; repaint once
           they do so uncollected own heroes join the grid. */
        document.addEventListener('hoh:characters-loaded', function(){
          try { if(typeof window.hohYhRefreshCollection === 'function') window.hohYhRefreshCollection(); } catch(_){}
        });
      }
      window.hohYhRefreshCollection = function(){
        var ch = document.getElementById('hoh-yh-coll');
        if(ch && document.body.contains(ch) && window.__hohCollMode === 'profile' && typeof window.hohShopRenderCollection === 'function'){
          try { window.hohShopRenderCollection(ch); } catch(_){}
        }
      };
      var toolsHost = host.querySelector('#hoh-yh-tools');
      if(toolsHost && typeof window.hohHallRenderToolsPanel === 'function'){
        try { window.hohHallRenderToolsPanel(toolsHost); } catch(_){}
      }
      return;
    }
  }

  /* R381 (Jan): THE EVENTS TAB, now two sub tabs. Explore the Hall first and
     default, The Battlegrounds second as its own full scene (painted by
     hohPaintBgDoor into #hoh-yh-bg-door, with the builder and the fight on
     #hoh-yh-bg-stage beside it). The two panes toggle with the hidden
     attribute ONLY — the explore node is MOVED once and never rebuilt, so
     nothing inside it can lose state on a sub tab switch. */
  function eventsBgOn(){
    try {
      return !!window.HOH_CONTRACT_ENABLED ||
        (typeof window.hohIsGodMode === 'function' && window.hohIsGodMode());
    } catch(_){ return !!window.HOH_CONTRACT_ENABLED; }
  }
  function syncEvBgCount(){
    var el = document.getElementById('hoh-yh-evtab-bg-count');
    if(!el) return;
    var held = 0;
    try { held = (typeof window.hohGetBgTickets === 'function') ? (window.hohGetBgTickets() | 0) : 0; } catch(_){}
    el.textContent = held > 0 ? String(held) : '';
  }
  /* R793 (Jan, 2026-09-22): Explore the Hall is an event in the MAIN Hall
     now, not in Your Hall. In the live Hall the Events tab holds only the
     Battlegrounds scene, and every door that used to land on the Explore
     sub tab opens the main Hall's event instead. Legacy mode keeps both. */
  function exploreInYourHall(){
    try { return !((typeof window.hohR793Live === 'function') && window.hohR793Live()); } catch(_){ return true; }
  }
  function setEventsTab(name){
    if(!eventsBgOn()) name = 'explore';
    name = (name === 'bg') ? 'bg' : 'explore';
    if(!exploreInYourHall()) name = 'bg';
    window.__hohEvTab = name;
    var ex = document.getElementById('hoh-yh-evpane-explore');
    var bg = document.getElementById('hoh-yh-evpane-bg');
    if(!ex) return;
    var showBg = (name === 'bg');
    /* hidden-attribute toggle only. A one shot fade-in class restarts on each
       show (transform and opacity, nothing that costs layout). */
    if(showBg){
      ex.setAttribute('hidden', '');
      if(bg){
        bg.removeAttribute('hidden');
        bg.classList.remove('hoh-yh-evpane-in'); void bg.offsetWidth;
        bg.classList.add('hoh-yh-evpane-in');
      }
    } else {
      if(bg) bg.setAttribute('hidden', '');
      ex.removeAttribute('hidden');
      ex.classList.remove('hoh-yh-evpane-in'); void ex.offsetWidth;
      ex.classList.add('hoh-yh-evpane-in');
      try { if(window.hohFreshFacesEnsureLive) window.hohFreshFacesEnsureLive(); } catch(_){}
    }
    var pills = document.querySelectorAll('.hoh-yh-evtab');
    for(var i = 0; i < pills.length; i++){
      if(pills[i].getAttribute('data-evtab') === name) pills[i].classList.add('hoh-yh-evtab-active');
      else pills[i].classList.remove('hoh-yh-evtab-active');
    }
    if(showBg && typeof window.hohPaintBgDoor === 'function'){
      try { window.hohPaintBgDoor(); } catch(_){}
    }
    syncEvBgCount();
  }
  /* Public door. From anywhere: lands on the Events tab with the asked sub
     tab up. The Battlegrounds scene's free-ticket path uses this to jump to
     Explore, and entering a battle uses it to jump to the Battlegrounds. */
  window.hohYourHallSetEventsTab = function(name){
    var want = (name === 'bg') ? 'bg' : 'explore';
    if(want === 'explore' && !exploreInYourHall()){
      if(typeof window.hohExploreEventOpen === 'function') window.hohExploreEventOpen();
      return;
    }
    if(currentView !== 'events'){
      window.__hohEvTab = want;
      window.hohYourHallSwitchView('events');
      return;
    }
    if(document.getElementById('hoh-yh-evpane-explore')) setEventsTab(want);
  };
  function renderEventsTab(host, token){
    if(!token){
      host.innerHTML = '<div class="hoh-yh-empty">Sign in on the Profile tab to explore and battle.</div>';
      return;
    }
    var bgOn = eventsBgOn();
    var exHere = exploreInYourHall();
    if(!exHere && !bgOn){
      host.innerHTML = '<div class="hoh-yh-empty">Explore the Hall is in the main Hall now, just above the gallery.</div>';
      return;
    }
    /* Land on the remembered sub tab; a mounted battle or an open builder
       always pulls the Battlegrounds tab up so the fight is never hidden. */
    var tab = (window.__hohEvTab === 'bg' || !exHere) ? 'bg' : 'explore';
    try {
      var _pk0 = document.getElementById('hoh-yh-explore-park');
      if((_pk0 && _pk0.querySelector('#hoh-contract')) || document.body.classList.contains('hoh-bg-building')) tab = 'bg';
    } catch(_){}
    host.innerHTML =
      ((bgOn && exHere) ?
        '<div class="hoh-yh-evtabs" role="tablist">' +
          /* R439 (Jan): "explore the hall should have its own minimalistic icon
             and pill effect just like the battlegrounds ... in a similar style
             but with different colors" — brand purple #ef94ff and blue
             #b3f5ee. Same three parts as the Battlegrounds pill (mark,
             gradient ring, drifting motes) so the two read as a matched pair
             rather than one designed button next to a plain one. A compass,
             because the verb is explore and it has to survive at 16px. */
          '<button type="button" class="hoh-yh-evtab hoh-yh-evtab-ex" role="tab" data-evtab="explore">' +
            '<span class="hoh-yh-evtab-motes" aria-hidden="true"></span>' +
            '<span class="hoh-yh-evtab-mark" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
                '<circle cx="12" cy="12" r="9"/><path d="M15.4 8.6 13.6 13.6 8.6 15.4 10.4 10.4z"/>' +
              '</svg>' +
            '</span>' +
            'Explore the Hall</button>' +
          /* R431 (Jan): "the battlegrounds tab should be more exciting and make
             them want to click it — the circle it's in needs to be more
             stylized and feel like something cool to click on."
             It read as the quieter of two pills, which is backwards: Explore
             is a daily chore and this is the game. It gets a crossed-swords
             mark of its own and a class the stylesheet can dress, so the
             difference is carried by the button rather than by the word. */
          '<button type="button" class="hoh-yh-evtab hoh-yh-evtab-bg" role="tab" data-evtab="bg">' +
            /* R438 (Jan): "have subtle fire particles flying off of it".
               An empty span, because a pseudo-element pair on the button is
               already spent on the gradient ring and the sheen. This one
               carries two independent ember fields (itself and its ::before)
               at different speeds, so the rise never reads as a loop. */
            '<span class="hoh-yh-evtab-embers" aria-hidden="true"></span>' +
            '<span class="hoh-yh-evtab-mark" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M14.5 3.5H20V9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
                '<path d="M9.5 3.5H4V9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/>' +
              '</svg>' +
            '</span>' +
            'The Battlegrounds<span class="hoh-yh-evtab-count" id="hoh-yh-evtab-bg-count"></span></button>' +
        '</div>' : '') +
      '<div id="hoh-yh-evpane-explore" class="hoh-yh-evpane"><div id="hoh-yh-explore-slot"><div id="hoh-yh-explore-panel"></div></div></div>' +
      '<div id="hoh-yh-evpane-bg" class="hoh-yh-evpane" hidden><div id="hoh-yh-bg-door"></div><div id="hoh-yh-bg-stage"></div></div>';
    if(exHere) moveExploreInto(host.querySelector('#hoh-yh-explore-panel'));
    /* R381: a parked battle host rides back into the BATTLEGROUNDS pane, the
       same first-child position hohEnterBattlegrounds gives it. */
    try {
      var _pk = document.getElementById('hoh-yh-explore-park');
      var _ct = _pk ? _pk.querySelector('#hoh-contract') : null;
      var _bgPane = host.querySelector('#hoh-yh-evpane-bg');
      if(_ct && _bgPane) _bgPane.insertBefore(_ct, _bgPane.firstChild);
    } catch(_){}
    var bar = host.querySelector('.hoh-yh-evtabs');
    if(bar) bar.addEventListener('click', function(e){
      var b = e.target && e.target.closest ? e.target.closest('.hoh-yh-evtab') : null;
      if(!b) return;
      setEventsTab(b.getAttribute('data-evtab'));
    });
    if(!window.__hohEvBgCountWired){
      window.__hohEvBgCountWired = 1;
      document.addEventListener('hoh:bg-tickets-changed', function(){ try { syncEvBgCount(); } catch(_){} });
    }
    setEventsTab(tab);
  }

  /* R425: this was a SECOND full copy of the old email-plus-PIN form, mounted
     into the Profile tab when there is no token. Two problems, one of them
     latent and nasty: it repeated every mistake the lightbox just fixed (an
     eyebrow above a heading that says the same thing, four PIN boxes open
     before any email had been checked), AND it minted a second set of
     elements with the SAME ids — hoh-yh-email, hoh-yh-pin-1..4. With the
     lightbox open over the Profile tab, getElementById would have returned
     whichever came first in the document and the sign-in would have read
     digits out of the wrong boxes.
     One sign-in in this theme now. The tab shows why it is worth doing and
     opens the lightbox; the lightbox does the work. */
  function renderSignInInto(host){
    if(!host) return;
    host.innerHTML =
      '<div class="hoh-yh-signin hoh-yh-signin-page">' +
        '<h2 class="hoh-yh-title">Sign in</h2>' +
        '<p class="hoh-yh-lead">Profile shows every hero you have made and the raffle tickets they have banked.</p>' +
        '<button type="button" class="hoh-yh-submit" onclick="if(window.hohSignInToYourHall)window.hohSignInToYourHall();">Sign in</button>' +
        '<p class="hoh-yh-foot">Your sign-in stays on your device.</p>' +
      '</div>';
  }

  /* Filter bar mirrors the main grid: search, race, class, sort. State
     is per-sub-view (FILTER_STATE) so switching tabs preserves filters. */
  /* R136-quest: Inline explanation card for the questing / receive
     system. Reframed from "harvest" (farming) to "quests" (the hero
     adventures in other people's campaigns and brings back spoils).
     Visible on the Profile / My Submitted view regardless of active
     sort. Hearts are today's spoils; the copy stays generic about
     "spoils" so future reward types slot in without rewrites. */
  /* R145-D: Rarity bonus on top of the per-level grant. Common stays
     at the level baseline (+0 bonus), each rarity tier above adds a
     fixed bump. Mirrors the server-side nightly_heart_grace logic
     exactly so the displayed Receive amount equals what the user will
     actually receive. */
  function hohYhRarityBonus(c){
    if(!c) return 0;
    try {
      if(typeof window.hohIsMythicCharacter === 'function' && window.hohIsMythicCharacter(c)){
        return 7;
      }
    } catch(_){}
    var tier = 'common';
    try {
      if(typeof window.hohGetRarity === 'function'){
        var r = window.hohGetRarity(c.hearts || 0);
        if(r && r.tier) tier = String(r.tier).toLowerCase();
      }
    } catch(_){}
    if(tier === 'uncommon')  return 2;
    if(tier === 'rare')      return 3;
    if(tier === 'epic')      return 4;
    if(tier === 'legendary') return 5;
    if(tier === 'mythic')    return 7;
    return 0;
  }
  function hohYhTotalReceive(c, lvl){
    var l = (typeof lvl === 'number') ? lvl : (typeof window.hohCharLevel === 'function' ? window.hohCharLevel(c) : 1);
    return l + hohYhRarityBonus(c);
  }
  window.hohYhRarityBonus = hohYhRarityBonus;
  window.hohYhTotalReceive = hohYhTotalReceive;
  /* R145-D: The full text panel was replaced with a quieter eyebrow +
     info button. Clicking the button opens a lightbox listing both the
     level bonus AND the new rarity bonus tiers. Keeps the top of the
     view clean while making the rules discoverable. */
  function renderProfileLevelNote(){
    if(currentView !== 'characters') return '';
    return '<div class="hoh-yh-level-note hoh-yh-level-note-compact" role="note" aria-label="How quests work">' +
      '<span class="hoh-yh-level-note-icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M12 2L14.5 8.5L21 9.5L16.5 14L17.5 21L12 17.5L6.5 21L7.5 14L3 9.5L9.5 8.5Z"/>' +
        '</svg>' +
      '</span>' +
      '<span class="hoh-yh-level-note-text">' +
        '<strong>Your heroes adventure while you\'re away.</strong>&nbsp;' +
        '<button type="button" class="hoh-yh-level-note-link" onclick="window.hohYhOpenQuestRules&&hohYhOpenQuestRules()">How it works</button>' +
      '</span>' +
    '</div>';
  }
  /* Lightbox renderer. Lazy-mounted on first open. Closes on backdrop
     tap, X button, or Escape. */
  window.hohYhOpenQuestRules = function(){
    var existing = document.getElementById('hoh-yh-quest-rules-lb');
    if(existing){
      existing.classList.add('hoh-yh-quest-rules-on');
      return;
    }
    var lb = document.createElement('div');
    lb.id = 'hoh-yh-quest-rules-lb';
    lb.className = 'hoh-yh-quest-rules-lb hoh-yh-quest-rules-on';
    lb.innerHTML =
      '<div class="hoh-yh-quest-rules-backdrop" onclick="window.hohYhCloseQuestRules&&hohYhCloseQuestRules()"></div>' +
      '<div class="hoh-yh-quest-rules-modal" role="dialog" aria-label="How quests work">' +
        '<button type="button" class="hoh-yh-quest-rules-close" aria-label="Close" onclick="window.hohYhCloseQuestRules&&hohYhCloseQuestRules()">&times;</button>' +
        '<div class="hoh-yh-quest-rules-eyebrow">How quests work</div>' +
        '<h2 class="hoh-yh-quest-rules-title">Your heroes adventure while you\'re away</h2>' +
        '<p class="hoh-yh-quest-rules-lead">' +
          'Check back each week to claim the hearts they\'ve brought home. The amount a hero returns scales with their level AND their rarity.' +
        '</p>' +
        '<div class="hoh-yh-quest-rules-section">' +
          '<div class="hoh-yh-quest-rules-section-title">Per-level baseline</div>' +
          '<div class="hoh-yh-quest-rules-section-body">' +
            'Every <strong>30 days in the hall</strong> earns a hero another level. Each level adds +1 to their weekly claim. A Level 5 hero brings back 5 hearts a week; a Level 12 brings back 12.' +
          '</div>' +
        '</div>' +
        '<div class="hoh-yh-quest-rules-section">' +
          '<div class="hoh-yh-quest-rules-section-title">Rarity bonus <span class="hoh-yh-quest-rules-section-sub">added on top of level</span></div>' +
          '<table class="hoh-yh-quest-rules-table">' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-common">Common</td><td>+0</td></tr>' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-uncommon">Uncommon</td><td>+2</td></tr>' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-rare">Rare</td><td>+3</td></tr>' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-epic">Epic</td><td>+4</td></tr>' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-legendary">Legendary</td><td>+5</td></tr>' +
            '<tr><td class="hoh-yh-quest-rules-tier hoh-yh-quest-rules-tier-mythic">Mythic</td><td>+7</td></tr>' +
          '</table>' +
        '</div>' +
        '<div class="hoh-yh-quest-rules-example">' +
          'A <strong>Level 5 Epic</strong> hero returns <strong>9 hearts</strong> each week (5 from level + 4 from rarity).' +
        '</div>' +
        '<div class="hoh-yh-quest-rules-section">' +
          '<div class="hoh-yh-quest-rules-section-title">Level up faster</div>' +
          '<div class="hoh-yh-quest-rules-section-body">' +
            'Tap the <strong>+</strong> on a hero to feed one heart, or hold it to stream. <strong>Double-tap the +</strong> to fill the rest of the current level at once, spending the hearts it takes (or all you have if you are short).' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(lb);
    /* Esc close */
    var onKey = function(e){
      if(e.key === 'Escape'){
        window.hohYhCloseQuestRules();
      }
    };
    lb.__hohKeyHandler = onKey;
    document.addEventListener('keydown', onKey);
  };
  window.hohYhCloseQuestRules = function(){
    var lb = document.getElementById('hoh-yh-quest-rules-lb');
    if(!lb) return;
    lb.classList.remove('hoh-yh-quest-rules-on');
    if(lb.__hohKeyHandler){
      try { document.removeEventListener('keydown', lb.__hohKeyHandler); } catch(_){}
    }
    setTimeout(function(){ if(lb.parentNode) lb.parentNode.removeChild(lb); }, 240);
  };
  function renderFilterBar(){
    var s = FILTER_STATE[currentView] || {};
    var races = ['','Human','Elf','Dragonborn','Tiefling','Half-Elf','Dwarf','Half-Orc','Halfling','Gnome','Aasimar','Drow','Goliath','Genasi','Tabaxi','Orc','Warforged','Changeling','Goblin','Harengon','Fairy','Other'];
    var classes = ['','Barbarian','Bard','Cleric','Druid','Fighter','Monk','Paladin','Ranger','Rogue','Sorcerer','Warlock','Wizard','Artificer','Blood Hunter','Other'];
    function opts(arr, sel){
      return arr.map(function(v){
        var label = v === '' ? (arr === races ? 'Races' : 'Classes') : v;
        return '<option value="' + esc(v) + '"' + (v === sel ? ' selected' : '') + '>' + esc(label) + '</option>';
      }).join('');
    }
    /* Sort menu order differs by tab: Hearted leads with "Most yours"
       (the user's own investment); Your Characters leads with "Most
       hearts" (community recognition). All four options (Most hearts,
       Most yours, Most tickets, Newest added) are available on both
       tabs so users can re-sort their roster by any axis.
       R136-tickets: Added "Most tickets" — surfaces the heroes with
       the most raffle tickets banked, useful when checking who's
       closest to a giveaway draw. */
    var sortOpts = currentView === 'hearted'
      ? [['mine', 'Most yours'], ['most', 'Most hearts'], ['tickets', 'Most tickets'], ['newest', 'Newest added']]
      : [['most', 'Most hearts'], ['mine', 'Most yours'], ['tickets', 'Most tickets'], ['newest', 'Newest added']];
    var sortHtml = sortOpts.map(function(o){
      return '<option value="' + o[0] + '"' + (o[0] === s.sort ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
    }).join('');
    /* Profile view gets a "Show" select that lets the user narrow
       the combined feed (submitted + hearted + favorites). The
       Favorited option filters to heroes the user has starred from
       Profile (saved into the local Hall data layer). */
    var showHtml = '';
    if(currentView === 'characters'){
      /* R231: My submitted only leads and is the default — that is what
         users expect when they open their own hall, their own work first.
         All liked heroes was removed because it overlapped with Hearted
         only, leaving three clear options. */
      var showOpts = [
        ['submitted',  'My submitted only'],
        ['hearted',    'Hearted only'],
        ['favorited',  'Favorited only']
      ];
      showHtml = '<select id="hoh-yh-show" class="hoh-yh-filter-select">' + showOpts.map(function(o){
        return '<option value="' + o[0] + '"' + (o[0] === (s.show || 'submitted') ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
      }).join('') + '</select>';
    }
    /* Sort dropdown placement on Profile: pair it with the Show
       select so the two "what to view + how to sort" controls sit
       together. On other tabs the sort drops to the end as before. */
    var sortSelectHtml = '<select id="hoh-yh-sort" class="hoh-yh-filter-select">' + sortHtml + '</select>';
    var profileLayout = (currentView === 'characters');
    return '<div class="hoh-yh-filter-row">' +
      '<input type="text" id="hoh-yh-search" class="hoh-yh-search-input" placeholder="Search by name, race, class, story..." value="' + esc(s.search) + '" />' +
      showHtml +
      (profileLayout ? sortSelectHtml : '') +
      '<select id="hoh-yh-race" class="hoh-yh-filter-select">' + opts(races, s.race) + '</select>' +
      '<select id="hoh-yh-cls" class="hoh-yh-filter-select">' + opts(classes, s.cls) + '</select>' +
      (profileLayout ? '' : sortSelectHtml) +
    '</div>';
  }

  function wireFilterBar(){
    var s = FILTER_STATE[currentView];
    var search = document.getElementById('hoh-yh-search');
    var race = document.getElementById('hoh-yh-race');
    var cls = document.getElementById('hoh-yh-cls');
    var sort = document.getElementById('hoh-yh-sort');
    var show = document.getElementById('hoh-yh-show');
    if(search) search.addEventListener('input', function(){ s.search = this.value; renderGridContent(); });
    if(race) race.addEventListener('change', function(){ s.race = this.value; renderGridContent(); });
    if(cls) cls.addEventListener('change', function(){ s.cls = this.value; renderGridContent(); });
    if(sort) sort.addEventListener('change', function(){ s.sort = this.value; renderGridContent(); });
    if(show) show.addEventListener('change', function(){ s.show = this.value; renderGridContent(); });
  }

  /* Apply current filter state to the source rows and re-render the
     grid. Called after every filter input change. */
  /* R64: exposed so external surfaces (wallet-changed listener,
     ticket-applied listener) can ask the grid to repaint. Cheap —
     diff-based via hohSmoothGridUpdate. */
  window.hohYhRefreshGrid = function(){
    try { renderGridContent(); } catch(_){}
  };
  function renderGridContent(){
    var grid = document.getElementById('hoh-yh-grid');
    if(!grid) return;
    var rows = SOURCE_ROWS[currentView] || [];
    var s = FILTER_STATE[currentView] || {};
    /* R226: "Favorited only" now sources from your saved/favorited list (every hero you
       starred), not the submitted+hearted Profile pool. Favoriting a hero you did not make or
       heart never added it to that pool, so the filter came up empty (the reported bug). */
    if(currentView === 'characters' && s.show === 'favorited'){
      try {
        var _savedHall = (typeof window.hohHallGet === 'function') ? window.hohHallGet() : null;
        var _savedIds = {}; ((_savedHall && _savedHall.heroes) || []).forEach(function(h){ if(h && h.id != null) _savedIds[String(h.id)] = 1; });
        var _gpool = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
        rows = _gpool.filter(function(c){ return c && _savedIds[String(c.id)]; });
      } catch(_){}
    }
    var q = (s.search || '').trim().toLowerCase();
    var filtered = rows.filter(function(c){
      if(!c) return false;
      /* Profile-only "show" filter: narrow the combined feed to a
         single source. Default 'all' keeps everything. The Favorited
         option checks the local Hall data layer for the saved flag. */
      if(currentView === 'characters' && s.show && s.show !== 'all'){
        if(s.show === 'submitted' && c._kind !== 'submitted') return false;
        if(s.show === 'hearted'   && c._kind !== 'hearted')   return false;
        if(s.show === 'favorited'){
          if(typeof window.hohHallIsSaved !== 'function') return false;
          if(!window.hohHallIsSaved(c.id)) return false;
        }
      }
      if(s.race && c.race !== s.race) return false;
      if(s.cls && c.character_class !== s.cls) return false;
      if(q){
        var hay = (
          (c.name || '') + ' ' + (c.race || '') + ' ' + (c.character_class || '') +
          ' ' + (c.campaign || '') + ' ' + (c.player_name || '') + ' ' + (c.story || '')
        ).toLowerCase();
        if(hay.indexOf(q) === -1) return false;
      }
      return true;
    });
    /* Sort within source-kind groups so submitted always lands above
       hearted on the Profile tab. _kindRank: submitted=0, hearted=1.
       The sort comparator first orders by kind, then by the chosen
       sort field. */
    var primarySort;
    if(s.sort === 'most'){
      primarySort = function(a,b){ return (b.hearts||0) - (a.hearts||0); };
    } else if(s.sort === 'mine'){
      primarySort = function(a,b){ return (b._mine||0) - (a._mine||0); };
    } else if(s.sort === 'tickets'){
      /* R136-tickets: Most tickets — primary sort on raffle ticket
         count, falling back to hearts when tickets tie so heroes
         with the same ticket count keep a meaningful ordering
         instead of shuffling on every render. */
      primarySort = function(a,b){
        var dt = (b.tickets||0) - (a.tickets||0);
        if(dt !== 0) return dt;
        return (b.hearts||0) - (a.hearts||0);
      };
    } else if(s.sort === 'newest'){
      primarySort = function(a,b){
        var ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        var tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      };
    } else {
      primarySort = function(){ return 0; };
    }
    if(currentView === 'characters'){
      filtered.sort(function(a,b){
        var ar = (a._kind === 'hearted') ? 1 : 0;
        var br = (b._kind === 'hearted') ? 1 : 0;
        if(ar !== br) return ar - br;
        return primarySort(a,b);
      });
    } else {
      filtered.sort(primarySort);
    }
    if(!filtered.length){
      grid.innerHTML = '<div class="hoh-yh-empty">' + (currentView === 'hearted' ? 'No hearted heroes match those filters.' : 'No characters match those filters.') + '</div>';
      return;
    }
    /* Replace any non-card child (empty-state div, loading text) before
       handing off to the FLIP helper so it diffs cards-against-cards
       only. Otherwise the empty div would count as a leaver. */
    if(!grid.querySelector('.hoh-yh-card')) grid.innerHTML = '';
    /* R136-level: pass the active sort key into cardHtml so the
       chip vs. level-cluster decision is available at render time.
       FLIP grid path + the plain-render fallback both forward the
       same sortKey so behavior is identical across update modes.
       IMPORTANT: hohSmoothGridUpdate reuses existing card NODES when
       keys (char ids) match — it doesn't re-run buildFn for reused
       nodes. That means a sort change between 'mine' and any other
       value would leave the wrong inner HTML (pill vs cluster) on
       re-rendered cards. We track lastSortKey on the grid element
       and force a full innerHTML rewrite (skip FLIP) when the sort
       changed, so the pill/cluster swap actually paints. */
    var activeSortKey = s.sort || 'most';
    var prevSortKey = grid.getAttribute('data-yh-sort-key');
    grid.setAttribute('data-yh-sort-key', activeSortKey);
    var sortChanged = prevSortKey != null && prevSortKey !== activeSortKey;
    if(!sortChanged && typeof window.hohSmoothGridUpdate === 'function'){
      window.hohSmoothGridUpdate(grid, filtered, {
        key: function(c){ return c.id; },
        build: function(c){ return cardHtml(c, c._mine, activeSortKey); }
      });
    } else {
      grid.innerHTML = filtered.map(function(c){ return cardHtml(c, c._mine, activeSortKey); }).join('');
    }
  }

  /* Sub-view: Your Characters. Calls get_my_characters RPC and caches
     the result in SOURCE_ROWS so subsequent filter changes don't
     re-fetch. Filter rendering is in renderGridContent. */
  async function loadCharacters(token){
    /* R380 AUDIT FIX: this used to bail when #hoh-yh-grid was missing, and
       the new Profile has no such grid — which silently killed the hero
       rows, the heart bank, the stats, the session expiry check and the
       uncollected-own augmentation. The grid is OPTIONAL now: data always
       loads, grid writes only happen when a legacy grid exists. */
    var grid = document.getElementById('hoh-yh-grid');
    var sb = window.hohSupabaseClient;
    if(!sb){ if(grid) grid.innerHTML = '<div class="hoh-yh-empty">Database unavailable.</div>'; return; }
    try {
      var res = await sb.rpc('get_my_characters', {
        user_email: token.email, user_pin: token.pin
      });
      if(!hallAccountCurrent(token)) return;
      if(res && res.error){
        console.error('[YourHall.characters]', res.error);
        if(grid) grid.innerHTML = '<div class="hoh-yh-empty">Could not load. Try again in a moment.</div>';
        return;
      }
      var rows = ((res && Array.isArray(res.data)) ? res.data : []).filter(function(r){
        return submittedByAccount(r, token.email);
      }).map(function(r){ return Object.assign({}, r); });
      accountSubmittedRows = rows.slice();
      /* R166 (Layer 4): Defensive read-side union.
         The server RPC `get_my_characters` is the authoritative source
         for "this email's characters" — but if it ever has a subtle
         server-side filter beyond email match (e.g., player_name
         dependency, a missing index, an RLS predicate edge case), the
         user sees a partial list. Symptom reported by a daily user:
         "the majority of my characters aren't showing up in my hall…
         I can edit them but they don't show up as my created in my
         hall." The edit gate uses pure email match in the client, so
         it finds them; the RPC was missing some.
         Mitigation: scan the global pool for characters whose email
         matches the signed-in user's email and union them in if the
         RPC missed them. De-dup by id so a row never appears twice.
         If the RPC is healthy this loop adds zero rows and costs ~one
         pass through the global pool (already in memory). */
      try {
        var emailKey = String(token && token.email || '').trim().toLowerCase();
        if(emailKey && typeof window.hohGetAllCharacters === 'function'){
          var globalPool = window.hohGetAllCharacters() || [];
          var existingIds = {};
          for(var ri = 0; ri < rows.length; ri++){
            if(rows[ri] && rows[ri].id) existingIds[String(rows[ri].id)] = true;
          }
          var addedFromPool = 0;
          for(var gi = 0; gi < globalPool.length; gi++){
            var g = globalPool[gi];
            if(!g || !g.id) continue;
            if(existingIds[String(g.id)]) continue;
            if(!submittedByAccount(g, emailKey)) continue;
            /* Clone so we don't mutate the global pool entry when we
               decorate it with _mine / _kind below. */
            var cloned = Object.assign({}, g);
            rows.push(cloned);
            addedFromPool++;
          }
          if(addedFromPool > 0){
            try { console.info('[YourHall] Union recovered ' + addedFromPool + ' account-matched character(s).'); } catch(_){}
          }
        }
      } catch(_){}
      /* Update the phone-on-file flag based on returned rows. If any of
         the user's characters has a phone_number, the new-submission form
         can skip the SMS opt-in. */
      try {
        var hasPhone = rows.some(function(r){ return r && (r.phone_number || r.phone); });
        if(typeof window.hohYourHallSetPhoneFlag === 'function'){
          window.hohYourHallSetPhoneFlag(hasPhone);
        }
      } catch(_){}
      /* R166 (Layer 3): Cache the user's CANONICAL player_name so the
         toggle button can show "[Name]'s Hall" instead of "Your Hall".
         CRITICAL: sort by created_at ASCENDING (earliest first) and use
         the FIRST one. The previous version used DESCENDING (most
         recent), which created a self-reinforcing feedback loop with
         the form prefill: if a stale name ever got cached and silently
         written on a submission, loadCharacters would then re-confirm
         the stale name from the most-recent row, locking the user in.
         Using the earliest character anchors the canonical name to the
         user's FIRST choice — the loop can't form because the earliest
         row never changes. */
      try {
        var sorted = rows.slice().sort(function(a, b){
          var ta = a && a.created_at ? new Date(a.created_at).getTime() : Infinity;
          var tb = b && b.created_at ? new Date(b.created_at).getTime() : Infinity;
          return ta - tb;
        });
        for(var i = 0; i < sorted.length; i++){
          var pn = sorted[i] && sorted[i].player_name;
          if(pn && String(pn).trim()){
            if(typeof window.hohYourHallSetName === 'function') window.hohYourHallSetName(String(pn).trim());
            break;
          }
        }
      } catch(_){}
      if(!rows.length){
        /* Disambiguate: empty could mean session expired OR no chars yet. */
        var verify = await sb.rpc('verify_pin', { user_email: token.email, user_pin: token.pin });
        if(!hallAccountCurrent(token)) return;
        var n = (verify && typeof verify.data === 'number') ? verify.data : 0;
        if(!n){
          clearStoredToken();
          renderSignIn(token.email, 'Session expired. Sign in again.');
          return;
        }
        SOURCE_ROWS.characters = [];
        if(grid) grid.innerHTML = '<div class="hoh-yh-empty">No heroes here yet. Submit one to start your hall.</div>';
        return;
      }
      /* Decorate each row with _mine = the user's personal heart count
         on that character (read from the hoh-hearts localStorage map)
         and _kind = 'submitted' so the renderer can distinguish them
         from hearted rows below. */
      var heartMap = {};
      try {
        var raw = localStorage.getItem('hoh-hearts');
        if(raw) heartMap = JSON.parse(raw) || {};
        rows.forEach(function(r){
          if(r && r.id){
            r._mine = heartMap[r.id] || 0;
            r._kind = 'submitted';
          }
        });
      } catch(_){}
      /* ALSO load the user's hearted heroes (from localStorage hearts
         map) and append them after submitted. Most users only have 1-2
         submitted characters; the hearted feed makes Profile feel
         populated and gives them a place to see all their favorites
         from one screen. Hearted rows get _kind = 'hearted' so the
         renderer can hide ticket pills and the summary can exclude
         them from totals. */
      try {
        var pool = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
        var byId = {}; for(var i=0;i<pool.length;i++){ if(pool[i] && pool[i].id) byId[pool[i].id] = pool[i]; }
        var submittedIds = {}; rows.forEach(function(r){ if(r && r.id) submittedIds[r.id] = true; });
        var heartedRows = [];
        Object.keys(heartMap).forEach(function(id){
          if(!id || !((heartMap[id] || 0) > 0)) return;
          if(submittedIds[id]) return; /* don't duplicate user's own */
          var c = byId[id];
          if(!c) return;
          heartedRows.push(Object.assign({}, c, {
            _mine: heartMap[id] || 0,
            _kind: 'hearted'
          }));
        });
        /* Sort hearted by personal heart count desc so the user's
           strongest reactions land at the top of that section. */
        heartedRows.sort(function(a,b){ return (b._mine||0) - (a._mine||0); });
        rows = rows.concat(heartedRows);
      } catch(_){}
      SOURCE_ROWS.characters = rows;
      renderGridContent();
      if(typeof window.hohYourHallRefreshSummary === 'function') window.hohYourHallRefreshSummary();
      /* Re-evaluate the weekly archetype now that we have real
         character data (the first dashboard render may have run
         with an empty SOURCE_ROWS). */
      refreshArchetype();
    } catch(e){
      console.error('[YourHall.characters]', e);
      if(grid) grid.innerHTML = '<div class="hoh-yh-empty">Could not load. Try again in a moment.</div>';
    }
  }

  /* Sub-view: Heroes You've Hearted. Reads from the hoh-hearts
     localStorage map. No server call needed. The personal count chip
     shows how many hearts the user has given each character. */
  function loadWitnessed(){
    var grid = document.getElementById('hoh-yh-grid');
    if(!grid) return;
    var heartMap = {};
    try {
      var raw = localStorage.getItem('hoh-hearts');
      if(raw) heartMap = JSON.parse(raw) || {};
    } catch(e){}
    var ids = Object.keys(heartMap).filter(function(k){ return (heartMap[k]||0) > 0; });
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!ids.length){
      SOURCE_ROWS.hearted = [];
      grid.innerHTML = '<div class="hoh-yh-empty">No hearted heroes yet. Tap a heart on any card to start your collection.</div>';
      return;
    }
    /* Exclude characters owned by the signed-in user. Heroes You've
       Hearted is meant to surface OTHER players' heroes the user has
       lifted; their own characters live under the Your Characters tab
       and would feel redundant here. */
    var myEmail = '';
    try {
      var t = getStoredToken();
      if(t && t.email) myEmail = String(t.email).toLowerCase();
    } catch(e){}
    var rows = ids
      .map(function(id){
        var c = all.find(function(x){ return x.id === id; });
        if(!c) return null;
        if(myEmail && c.auth_email && String(c.auth_email).toLowerCase() === myEmail) return null;
        return Object.assign({}, c, { _mine: heartMap[id]||0 });
      })
      .filter(Boolean);
    SOURCE_ROWS.hearted = rows;
    renderGridContent();
  }

  function rarTier(c){
    try {
      if(typeof window.hohGetRarity === 'function') return window.hohGetRarity(c.hearts || 0).tier;
    } catch(e){}
    return 'common';
  }

  /* R148-Z22: Character XP / leveling system.
     ==========================================
     Two-source level model:
       1. Server-backed: c.level + c.xp on the characters row. The
          authoritative source — survives across devices, persisted
          via the new grant_character_xp RPC. New chars start at
          level 1 / xp 0.
       2. Legacy fallback: pre-migration chars + offline reads. The
          old "every 30 days alive = +1 level" formula still computes
          a baseline when c.level is missing or unset (0/undefined).
          The SQL migration backfills these at deploy time, so the
          fallback is just defense-in-depth.
     XP economy:
       - 1 heart = HOH_XP_PER_HEART = 10 XP.
       - Per-level XP cost climbs by rarity tier:
           Common  (lv 1-20):    200 XP / level
           Uncommon (lv 21-40):  600
           Rare    (lv 41-60):  1,600
           Epic    (lv 61-75):  4,000
           Legendary (lv 76-90):10,000
           Mythic  (lv 91-100): 30,000
       - Post-100 soft cap (MapleStory-style): cost = mythic × 100 ×
         2^floor((level-101)/10). Level 101 = 3,000,000 XP; doubles
         every 10 levels. Level 200 ≈ 3 billion XP — unreachable.
     Total to level 100 ≈ 558,000 XP ≈ 55,800 hearts. */
  var HOH_LEVEL_DAY_MS = 24 * 60 * 60 * 1000;
  var HOH_LEVEL_PERIOD_DAYS = 30;
  var HOH_LEGACY_LEVEL_MAX = 20;
  var HOH_LEVEL_SOFT_CAP = 100;    /* Mythic tier ends here; post-100 is unreachable territory */
  var HOH_XP_PER_HEART = 10;       /* 1 heart = 10 XP; 10 hearts = 100 XP per user spec */
  var HOH_LEVEL_BASE_XP = 200;     /* Common per-level cost */
  /* Tier multipliers track the SQL xp_for_level() function. Keep in
     sync if either changes. */
  var HOH_LEVEL_TIER_MULT = {
    common:    1,   // 200 XP/level
    uncommon:  3,   // 600
    rare:      8,   // 1,600
    epic:     20,   // 4,000
    legendary:50,   // 10,000
    mythic:  150    // 30,000
  };
  function hohCharAgeDays(c){
    if(!c || !c.created_at) return 0;
    var t = new Date(c.created_at).getTime();
    if(isNaN(t)) return 0;
    return Math.max(0, (Date.now() - t) / HOH_LEVEL_DAY_MS);
  }
  /* R148-Z22: Returns the level for a character. Prefers the server-
     persisted c.level; falls back to the age-based formula for chars
     whose row hasn't yet been backfilled (or pre-server-load reads). */
  function hohCharLevel(c){
    if(!c) return 1;
    var server = parseInt(c.level, 10);
    if(server && server >= 1) return server;
    var days = hohCharAgeDays(c);
    var lvl = Math.floor(days / HOH_LEVEL_PERIOD_DAYS) + 1;
    if(lvl < 1) lvl = 1;
    if(lvl > HOH_LEGACY_LEVEL_MAX) lvl = HOH_LEGACY_LEVEL_MAX;
    return lvl;
  }
  /* R148-Z22: Rarity tier for a level (top-heavy ladder per user). */
  function hohLevelTier(level){
    var l = level | 0;
    if(l <= 20)  return 'common';
    if(l <= 40)  return 'uncommon';
    if(l <= 60)  return 'rare';
    if(l <= 75)  return 'epic';
    if(l <= 90)  return 'legendary';
    return 'mythic';
  }
  /* R148-Z22: XP cost to advance FROM (level-1) TO level. Mirrors
     the SQL xp_for_level() helper exactly so client predictions match
     server resolutions. */
  function hohXpForLevel(level){
    var l = level | 0;
    if(l <= 1) return 0;
    if(l <= 100){
      var tier = hohLevelTier(l);
      return HOH_LEVEL_BASE_XP * HOH_LEVEL_TIER_MULT[tier];
    }
    /* Post-100 soft cap: mythic × 100 × 2^floor((level-101)/10). */
    var steps = Math.floor((l - 101) / 10);
    var postCapMult = 100 * Math.pow(2, steps);
    return HOH_LEVEL_BASE_XP * HOH_LEVEL_TIER_MULT.mythic * postCapMult;
  }
  /* R148-Z22: Character's current XP banked toward next level. */
  function hohCharXp(c){
    if(!c) return 0;
    var x = parseInt(c.xp, 10);
    return (x && x >= 0) ? x : 0;
  }
  /* R148-Z22: 0..1 progress toward the next level. Replaces the old
     age-cycle progress so the bar tracks XP-to-next instead of days-
     into-30-day-period. */
  function hohCharLevelProgress(c){
    if(!c) return 0;
    var level = hohCharLevel(c);
    var xp = hohCharXp(c);
    var threshold = hohXpForLevel(level + 1);
    if(threshold <= 0) return 1;
    return Math.max(0, Math.min(1, xp / threshold));
  }
  /* R148-Z22: Public exports so card-render + extras files can use
     the same level + XP math without duplicating the curve. */
  window.hohCharLevel = hohCharLevel;
  window.hohCharXp = hohCharXp;
  window.hohCharLevelProgress = hohCharLevelProgress;
  window.hohLevelTier = hohLevelTier;
  window.hohXpForLevel = hohXpForLevel;
  window.hohXpPerHeart = HOH_XP_PER_HEART;
  /* R148-Z22: Async heart-spend → XP grant. Hands off to the
     grant_character_xp RPC, then refreshes the character in
     SOURCE_ROWS so the next renderGridContent() pulls the new
     level + xp + bar progress. Heart deduction is client-side
     (matches the existing wallet model). Returns the RPC result
     so the caller can drive the bar fill animation / level-up
     celebration. */
  window.hohGrantCharacterXp = async function(charId, hearts){
    var h = parseInt(hearts, 10) || 0;
    if(h <= 0) return { ok: false, reason: 'no_hearts' };
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < h) return { ok: false, reason: 'insufficient', have: bank, need: h };
    var email = (typeof window.hohYourHallEmail === 'function')
      ? (window.hohYourHallEmail() || '').toLowerCase().trim() : '';
    if(!email) return { ok: false, reason: 'no_account' };
    /* R148-Z22d: BUG FIX — the bare `supabase` var lives inside the
       hoh-section-core.js IIFE so it's UNDEFINED in this scope. The
       previous typeof-undefined check was firing on EVERY spend,
       making hohGrantCharacterXp return 'rpc_unavailable' before
       the heart was ever deducted. That's why the wallet wasn't
       decrementing — RPC was never called, heart was never removed.
       Use the public window.hohSupabaseClient instead (same pattern
       as everything else in this file). */
    var sb = window.hohSupabaseClient;
    if(!sb || !sb.rpc){
      return { ok: false, reason: 'rpc_unavailable' };
    }
    var xpAmount = h * HOH_XP_PER_HEART;
    /* Deduct hearts BEFORE the RPC fires. The RPC is idempotent on
       success (returns the new persisted state), so over-deducting
       on a duplicate call doesn't compound — but we refund on
       explicit failure paths below. */
    try {
      if(typeof window.hohRemoveHeartsFromBank === 'function'){
        window.hohRemoveHeartsFromBank(h, 'spend_yourhall');
      }
    } catch(_){}
    try {
      var res = await sb.rpc('grant_character_xp', {
        p_user_email: email,
        p_character_id: charId,
        p_xp_amount: xpAmount
      });
      var data = res && res.data;
      if(res && res.error){
        /* Refund — RPC didn't apply. */
        if(typeof window.hohAddHeartsToBank === 'function'){
          try { window.hohAddHeartsToBank(h, 'earn_yourhall'); } catch(_){}
        }
        return { ok: false, reason: 'rpc_error', detail: res.error };
      }
      if(!data || data.ok !== true){
        if(typeof window.hohAddHeartsToBank === 'function'){
          try { window.hohAddHeartsToBank(h, 'earn_yourhall'); } catch(_){}
        }
        return { ok: false, reason: (data && data.error) || 'rpc_failed', detail: data };
      }
      /* Patch the character row in-place so the next render sees
         the new level/xp without waiting for a full reload. */
      try {
        var rows = (SOURCE_ROWS && SOURCE_ROWS.characters) || [];
        for(var i = 0; i < rows.length; i++){
          if(rows[i] && rows[i].id === charId){
            rows[i].level = data.level;
            rows[i].xp = data.xp;
            break;
          }
        }
      } catch(_){}
      return {
        ok: true,
        level: data.level,
        xp: data.xp,
        xp_to_next: data.xp_to_next,
        leveled_up: !!data.leveled_up,
        hearts_spent: h
      };
    } catch(e){
      /* Unknown error — refund. */
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(h, 'earn_yourhall'); } catch(_){}
      }
      return { ok: false, reason: 'exception', detail: String(e) };
    }
  };
  /* R136-quest: Quest-cooldown ledger — replaces the YMD-keyed
     harvest boolean with a per-character timestamp. The cooldown is
     a real 24-hour window (not a calendar-day reset), so the user
     sees a smooth countdown bar instead of a hard midnight flip.
     The character is "on a quest" for HOH_QUEST_COOLDOWN_MS after
     they tap Receive; the bar fills toward 100% as the cooldown
     elapses, then the Receive button reappears. */
  var HOH_QUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000;
  function hohQuestKey(charId){
    return 'hoh-quest-sent-' + charId;
  }
  function hohGetCharQuestSentAt(charId){
    if(!charId) return 0;
    try {
      var raw = localStorage.getItem(hohQuestKey(charId));
      if(!raw) return 0;
      var t = parseInt(raw, 10);
      return isNaN(t) ? 0 : t;
    } catch(_){ return 0; }
  }
  function hohIsCharOnQuest(charId){
    var sent = hohGetCharQuestSentAt(charId);
    if(!sent) return false;
    return (Date.now() - sent) < HOH_QUEST_COOLDOWN_MS;
  }
  function hohGetCharQuestProgress(charId){
    /* Returns 0..1 representing how much of the cooldown has
       elapsed. 1 = ready (cooldown done). 0 = just sent. */
    var sent = hohGetCharQuestSentAt(charId);
    if(!sent) return 1;
    var elapsed = Date.now() - sent;
    if(elapsed >= HOH_QUEST_COOLDOWN_MS) return 1;
    return Math.max(0, Math.min(1, elapsed / HOH_QUEST_COOLDOWN_MS));
  }
  /* R136-tick: format hours with one decimal place. We TRUNCATE (floor)
     to one decimal so the displayed value never exceeds the true
     remaining time — a 23.97 hr remaining shows as 23.9, not 24.0.
     The live ticker (hohYhStartQuestTicker) refreshes these strings
     every 30s so the number visibly counts down 23.9 → 23.8 → ... */
  function hohFormatQuestHoursOneDecimal(hoursF){
    var truncated = Math.floor(hoursF * 10) / 10;
    return truncated.toFixed(1);
  }
  /* Desktop full label keeps the granular "Xh Ym" format because the
     wider viewport has room for both units. The live ticker rewrites
     it every 30s so the minute counter visibly drops. Decimal-hour
     formatting is mobile-only (see hohGetCharQuestShortLabel). */
  function hohGetCharQuestRemainingLabel(charId){
    var sent = hohGetCharQuestSentAt(charId);
    if(!sent) return '';
    var remaining = HOH_QUEST_COOLDOWN_MS - (Date.now() - sent);
    if(remaining <= 0) return '';
    var hours = Math.floor(remaining / (60 * 60 * 1000));
    var mins = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    var secs = Math.floor((remaining % (60 * 1000)) / 1000);
    /* R148-Z22: Always include seconds — the user asked for the
       countdown to feel alive ("On a quest · 4h 18m 30s"). The 1s
       ticker below keeps the seconds counter moving. */
    if(hours >= 1) return hours + 'h ' + mins + 'm ' + secs + 's';
    if(mins >= 1) return mins + 'm ' + secs + 's';
    return secs + 's';
  }
  /* R136-mobile-text: Compact "Back in Xh" style for narrow viewports
     where the full "On a quest · 23h 59m" wraps or truncates. Single-
     unit so the text fits cleanly inside the card row without
     ellipsis.
     R136-tick: hours now show one decimal place (23.9, 23.8, ...) and
     update live every 30s via the ticker below. The decimal makes the
     wait feel alive — the user sees the countdown moving instead of
     a static integer. */
  function hohGetCharQuestShortLabel(charId){
    var sent = hohGetCharQuestSentAt(charId);
    if(!sent) return '';
    var remaining = HOH_QUEST_COOLDOWN_MS - (Date.now() - sent);
    if(remaining <= 0) return '';
    var hoursF = remaining / (60 * 60 * 1000);
    if(hoursF >= 1) return 'Back in ' + hohFormatQuestHoursOneDecimal(hoursF) + ' hrs';
    var mins = Math.floor(remaining / (60 * 1000));
    if(mins >= 2) return 'Back in ' + mins + ' min';
    if(mins >= 1) return 'Back in 1 min';
    var secs = Math.floor((remaining % (60 * 1000)) / 1000);
    return 'Back in ' + secs + 's';
  }
  /* R148-Z22: live updater for quest countdown labels. Ticks every
     1s now (was 30s) because the label includes seconds. Cheap — one
     querySelectorAll + a few textContent writes per second, only when
     quest-active rows exist. The cooldown bar role is gone (bar is XP
     now, not time-based), so this only rewrites the right-side
     status text in place. */
  var __yhQuestTickerStarted = false;
  function hohYhStartQuestTicker(){
    if(__yhQuestTickerStarted) return;
    __yhQuestTickerStarted = true;
    setInterval(function(){
      /* PERF-8 (2026-08-06): this ran an UNQUALIFIED attribute query across
         the whole document every second, for every visitor, hidden tabs
         included — and since R227 removed the per-hero countdown chip the
         matching nodes usually do not even exist. Sleep when hidden, scope
         the query to the personal hall, bail when the hall is not mounted. */
      if(document.hidden) return;
      var root = document.getElementById('hoh-yh-page');
      if(!root) return;
      var nodes = root.querySelectorAll('[data-yh-quest-id]');
      if(!nodes || !nodes.length) return;
      for(var i = 0; i < nodes.length; i++){
        var n = nodes[i];
        var charId = n.getAttribute('data-yh-quest-id');
        if(!charId) continue;
        var role = n.getAttribute('data-yh-quest-role');
        if(role === 'short'){
          var s = hohGetCharQuestShortLabel(charId);
          if(s) n.textContent = s;
        } else if(role === 'full'){
          var f = hohGetCharQuestRemainingLabel(charId);
          if(f) n.textContent = 'On a quest · ' + f;
        }
      }
    }, 1000);
  }
  try { hohYhStartQuestTicker(); } catch(_){}
  function hohMarkCharQuestSent(charId){
    if(!charId) return;
    try { localStorage.setItem(hohQuestKey(charId), String(Date.now())); } catch(_){}
  }
  /* R136-quest: Public quest handler — sends the character on a quest
     and credits the spoils to the wallet. Renamed from
     hohHarvestCharLevel to hohSendCharOnQuest to match the new
     theme: characters adventure in other people's campaigns and
     bring back rewards equal to their level. Hearts are the
     spoils today; future spoils could include other rewards
     without changing the surrounding contract.
     The legacy alias hohHarvestCharLevel is kept below pointing
     at this handler so any cached HTML still wired to the old
     function name continues to work after deploy. */
  window.hohSendCharOnQuest = function(charId, btnEl, evt){
    if(evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();
    if(evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    if(!charId) return;
    if(hohIsCharOnQuest(charId)){
      try { window.hohShowHeartToast && window.hohShowHeartToast('Already on a quest', 'They\'ll be back in ' + hohGetCharQuestRemainingLabel(charId) + '.'); } catch(_){}
      return;
    }
    /* Find the character row in our cached source rows so we can
       compute the level. Falls back to a lookup over hohGetAllCharacters
       in case the row isn't in SOURCE_ROWS yet (e.g. immediately after
       submission). */
    var c = null;
    try {
      var rows = (SOURCE_ROWS && SOURCE_ROWS.characters) || [];
      for(var i = 0; i < rows.length; i++){
        if(rows[i] && rows[i].id === charId){ c = rows[i]; break; }
      }
    } catch(_){}
    if(!c){
      try {
        var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
        for(var j = 0; j < all.length; j++){
          if(all[j] && all[j].id === charId){ c = all[j]; break; }
        }
      } catch(_){}
    }
    if(!c) return;
    var level = hohCharLevel(c);
    /* R145-D: Total grant = level + rarity bonus. The RECEIVE pill on
       each card already shows this total via hohYhTotalReceive, so the
       grant has to match what the pill promised. Bonus values:
       Common +0, Uncommon +2, Rare +3, Epic +4, Legendary +5, Mythic +7. */
    var rarityBonus = (typeof hohYhRarityBonus === 'function') ? hohYhRarityBonus(c) : 0;
    var totalGrant = level + rarityBonus;
    if(typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(totalGrant, 'earn_yourhall'); } catch(_){}
    }
    hohMarkCharQuestSent(charId);
    /* R136-quest-anim: Fire the button's brief tap response BEFORE
       the card repaint replaces the node. The animation runs on the
       original button element while the heart-fly sprites launch
       from the same coordinates. The repaint below swaps the card
       to the QUESTING state, but by then the button anim is already
       in flight and the sprites are mid-arc. */
    try {
      if(btnEl){
        btnEl.classList.remove('hoh-yh-quest-receive-firing');
        void btnEl.offsetWidth;
        btnEl.classList.add('hoh-yh-quest-receive-firing');
      }
    } catch(_){}
    /* Heart-fly visual from the button toward the wallet pill so the
       user sees their spoils land on their wallet. R136-fly-count:
       Sprite count == level (one heart per heart received). Soft cap
       at 14 so a level-20 hero doesn't drown the page with sprites,
       but minimum is the actual level so a level-1 hero shows ONE
       heart flying, not 4. */
    try {
      if(btnEl && typeof window.flyDiceTraderHeartsToPill === 'function'){
        /* R145-D: sprite count reflects the new total so the user
           sees ALL their hearts fly, not just the level portion. */
        window.flyDiceTraderHeartsToPill(btnEl, Math.max(1, Math.min(totalGrant, 14)));
      }
    } catch(_){}
    /* R145-D: Toast shows the total grant and credits both factors so
       the user understands why a Lv 2 Epic returned 6 hearts (2+4). */
    try {
      var toastSub = rarityBonus > 0
        ? 'Back from a level ' + level + ' quest, with a rarity bonus.'
        : 'Back from a level ' + level + ' quest.';
      window.hohShowHeartToast && window.hohShowHeartToast('Home from a quest', toastSub, true);
    } catch(_){}
    /* Repaint the card in place — preferred path, since it preserves
       grid scroll position. We swap just this card's outerHTML rather
       than re-rendering the whole grid.
       R136-quest-anim: Delay the swap by 280ms so the receive
       button's firing flash and the heart-fly launch read clearly
       before the card morphs to the QUESTING state. The fly sprites
       already cached their start coordinates synchronously, so they
       complete cleanly even after the swap. */
    setTimeout(function(){
      try {
        /* Character ids are server-issued UUIDs (alphanumerics + dashes)
           so no CSS-attribute escaping is required for the selector. */
        var card = document.querySelector('.hoh-yh-card[data-hero-id="' + charId + '"]');
        if(card){
          var sortKey = (FILTER_STATE && FILTER_STATE[currentView] && FILTER_STATE[currentView].sort) || 'most';
          var fresh = cardHtml(c, c._mine || 0, sortKey);
          var tmp = document.createElement('div');
          tmp.innerHTML = fresh;
          var newCard = tmp.firstChild;
          if(newCard && card.parentNode){ card.parentNode.replaceChild(newCard, card); }
        }
      } catch(_){}
    }, 280);
  };
  /* R136-quest: Back-compat alias. Older inline onclick handlers may
     still reference hohHarvestCharLevel; keep them functional by
     forwarding to the renamed quest handler. */
  window.hohHarvestCharLevel = window.hohSendCharOnQuest;

  /* ================================================================
     R437 (Jan) · THE HEART BANK IS GONE
     ----------------------------------------------------------------
     "remove the ability to claim the mass amounts of hearts for the
     heroes and colected cards adventure when they are away, remove
     that function entirely from the code and ux since thats no longer
     relevant."
     Deleted with it, and worth knowing about: this block started TWO
     setInterval timers at module load, unconditionally, on every page
     that loads this file — one every 15 seconds to repaint the bar and
     one every 2.2 seconds to spawn a decorative particle. Neither
     checked whether the bar was even on screen. Both are gone.
     Also gone: the claim_heart_bank and get_heart_bank_state RPC calls,
     the 7 day cooldown, the countdown label, the particle FX stylesheet
     it injected, and the 'hoh-heart-bank-claim-at' localStorage key.
     KEPT ON PURPOSE: hohYhRarityBonus, which lives further up this file and
     still feeds the per card display. hohYhTotalReceive sits beside it and is
     now callerless — left in place because it is exported on window and a
     cached page could still reach for it, but it is dead weight.
     The database functions are untouched. They simply stop being called.
     ================================================================ */
  /* R136-tooltip: Hover/tap tooltip on the Lvl pill. One shared
     element (lazy-created), positioned above the pill on hover or
     pinned on click. Mobile users tap to pin; outside-click closes.
     Stops click propagation so the parent card doesn't open. */
  var __lvlTip = null;
  var __lvlTipAnchor = null;
  var __lvlTipPinned = false;
  var __cfGlyphEl = null; /* R212: the + feed glyph that morphs to a > while the confirm is open */
  function __ensureLvlTip(){
    if(__lvlTip) return __lvlTip;
    __lvlTip = document.createElement('div');
    __lvlTip.className = 'hoh-yh-lvl-tooltip';
    __lvlTip.setAttribute('role', 'tooltip');
    __lvlTip.setAttribute('aria-hidden', 'true');
    __lvlTip.id = 'hoh-yh-lvl-tooltip';
    /* Don't let clicks inside the tooltip bubble to document and
       trigger the outside-click close handler. */
    __lvlTip.addEventListener('click', function(e){ e.stopPropagation(); });
    document.body.appendChild(__lvlTip);
    return __lvlTip;
  }
  function __findCharById(id){
    if(!id) return null;
    try {
      var rows = (SOURCE_ROWS && SOURCE_ROWS.characters) || [];
      for(var i = 0; i < rows.length; i++){
        if(rows[i] && rows[i].id === id) return rows[i];
      }
    } catch(_){}
    try {
      var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      for(var j = 0; j < all.length; j++){
        if(all[j] && all[j].id === id) return all[j];
      }
    } catch(_){}
    return null;
  }
  function __formatJoinedDate(d){
    if(!d) return '';
    try {
      var t = new Date(d);
      if(isNaN(t.getTime())) return '';
      return t.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch(_){ return ''; }
  }
  function __buildLvlTipHtml(c){
    /* R148-Z22: XP-system tooltip. Replaces the old age-based copy
       (days-till-next-level) with the new heart-spend mechanic:
       progress %, hearts to next level, and tier label. */
    var lvl = hohCharLevel(c);
    var tier = hohLevelTier(lvl);
    var tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
    var pct = Math.round(hohCharLevelProgress(c) * 100);
    var maxed = lvl >= 200; /* post-100 is scaled to be unreachable; we still let it level */
    var xpToNext = hohXpForLevel(lvl + 1);
    var xpCurrent = hohCharXp(c);
    var xpRemaining = Math.max(0, xpToNext - xpCurrent);
    var heartsToNext = Math.ceil(xpRemaining / HOH_XP_PER_HEART);
    var heartsToNextStr = heartsToNext >= 1000
      ? (Math.round(heartsToNext / 100) / 10) + 'k'
      : String(heartsToNext);
    var joined = __formatJoinedDate(c.created_at);
    var rows = [
      ['Level', 'Lvl ' + lvl + ' &middot; ' + tierLabel],
      ['Progress', pct + '% &middot; ' + xpCurrent + ' / ' + xpToNext + ' XP'],
      ['Next level', heartsToNextStr + ' hearts'],
      ['Joined', joined || '—']
    ];
    var rowsHtml = rows.map(function(r){
      return '<div class="hoh-yh-lvl-tt-row">' +
        '<span class="hoh-yh-lvl-tt-label">' + r[0] + '</span>' +
        '<span class="hoh-yh-lvl-tt-value">' + r[1] + '</span>' +
      '</div>';
    }).join('');
    var foot = (lvl >= HOH_LEVEL_SOFT_CAP)
      ? 'Past Mythic — each level costs vastly more.'
      : 'Tap the pill to spend 1 heart for ' + HOH_XP_PER_HEART + ' XP. Hold to stream.';
    return '<div class="hoh-yh-lvl-tt-bar" aria-hidden="true">' +
        '<span class="hoh-yh-lvl-tt-bar-fill" style="width:' + pct + '%"></span>' +
      '</div>' +
      '<div class="hoh-yh-lvl-tt-rows">' + rowsHtml + '</div>' +
      '<div class="hoh-yh-lvl-tt-foot">' + foot + '</div>';
  }
  function __positionLvlTip(anchor){
    if(!__lvlTip || !anchor) return;
    /* Make the tooltip measurable: render it but invisible so we can
       read its actual size before positioning. */
    var ar = anchor.getBoundingClientRect();
    var tr = __lvlTip.getBoundingClientRect();
    var sx = window.scrollX || window.pageXOffset || 0;
    var sy = window.scrollY || window.pageYOffset || 0;
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var pad = 8;
    var left = ar.left + ar.width / 2 - tr.width / 2;
    /* Try above; if not enough room, flip below. */
    var topAbove = ar.top - tr.height - pad;
    var topBelow = ar.bottom + pad;
    var top, below = false;
    if(topAbove >= pad){
      top = topAbove;
    } else if(topBelow + tr.height <= vh - pad){
      top = topBelow;
      below = true;
    } else {
      /* Edge case: tiny viewport. Pin to whichever edge is closer. */
      top = Math.max(pad, topAbove);
    }
    /* Clamp horizontally to viewport with padding. */
    left = Math.max(pad, Math.min(vw - tr.width - pad, left));
    __lvlTip.classList.toggle('hoh-yh-lvl-tooltip-below', !!below);
    __lvlTip.style.left = (left + sx) + 'px';
    __lvlTip.style.top = (top + sy) + 'px';
  }
  function __showLvlTip(charId, anchor, pin){
    var c = __findCharById(charId);
    if(!c || !anchor) return;
    var tip = __ensureLvlTip();
    tip.innerHTML = __buildLvlTipHtml(c);
    /* Show via class so CSS can transition opacity. Reset transform
       state in case a previous show left a half-faded element. */
    tip.classList.add('hoh-yh-lvl-tooltip-measuring');
    tip.classList.add('is-shown');
    tip.setAttribute('aria-hidden', 'false');
    /* Position after the browser has computed the tooltip's size with
       the new content. requestAnimationFrame guarantees layout has
       run. */
    requestAnimationFrame(function(){
      __positionLvlTip(anchor);
      tip.classList.remove('hoh-yh-lvl-tooltip-measuring');
    });
    __lvlTipAnchor = anchor;
    if(pin) __lvlTipPinned = true;
  }
  function __hideLvlTip(force){
    if(!__lvlTip) return;
    if(__lvlTipPinned && !force) return;
    __lvlTip.classList.remove('is-shown');
    __lvlTip.classList.remove('hoh-yh-lvl-confirm'); /* R206: drop morph styling so the next hover info renders plain */
    __lvlTip.setAttribute('aria-hidden', 'true');
    __lvlTipAnchor = null;
    __lvlTipPinned = false;
    /* R212: restore the feed glyph from > back to + when the confirm closes. */
    if(__cfGlyphEl){ try{ __cfGlyphEl.textContent='+'; }catch(_){} __cfGlyphEl=null; }
  }
  window.hohYhShowLevelTip = function(charId, anchor){
    /* Hover-only show: skip if already pinned to another anchor so we
       don't replace the user's pinned selection. */
    if(__lvlTipPinned) return;
    __showLvlTip(charId, anchor, false);
  };
  window.hohYhHideLevelTip = function(){
    __hideLvlTip(false);
  };
  window.hohYhToggleLevelTip = function(charId, anchor, evt){
    if(evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();
    if(evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    if(__lvlTipPinned && __lvlTipAnchor === anchor){
      __hideLvlTip(true);
    } else {
      __hideLvlTip(true);
      __showLvlTip(charId, anchor, true);
    }
  };
  /* Outside-click closes a pinned tooltip. Capture-phase so we beat
     any inner stopPropagation on cards. */
  document.addEventListener('click', function(e){
    if(!__lvlTipPinned || !__lvlTip) return;
    if(__lvlTip.contains(e.target)) return;
    if(__lvlTipAnchor && __lvlTipAnchor.contains(e.target)) return;
    __hideLvlTip(true);
  }, true);
  /* Reposition pinned tooltip on scroll/resize so it follows the
     pill if the page reflows. Throttled with rAF. */
  var __lvlTipReposScheduled = false;
  function __schedLvlTipRepos(){
    if(__lvlTipReposScheduled) return;
    __lvlTipReposScheduled = true;
    requestAnimationFrame(function(){
      __lvlTipReposScheduled = false;
      if(__lvlTip && __lvlTip.classList.contains('is-shown') && __lvlTipAnchor){
        __positionLvlTip(__lvlTipAnchor);
      }
    });
  }
  window.addEventListener('scroll', __schedLvlTipRepos, { passive: true });
  window.addEventListener('resize', __schedLvlTipRepos);

  /* ===================================================================
     R206: DOUBLE-TAP THE + FEED BUTTON -> "FINISH THIS LEVEL" CONFIRM
     ===================================================================
     Tapping + feeds 1 heart; holding streams. Pressing + twice quickly
     morphs the existing level tooltip into a confirm box offering to
     spend the hearts needed to finish the current level in one shot, so
     the user never has to hold to pour a whole level. The two taps still
     feed normally — we only OBSERVE pointerup, never touch the tap/hold
     path. If the bank can't cover a full level we offer to pour in all
     the hearts they have (partial progress). One confirm = at most one
     level (spend is capped at hearts-to-next). */
  function __yhHeartsToNext(c){
    var lvl = hohCharLevel(c);
    var rem = Math.max(0, hohXpForLevel(lvl + 1) - hohCharXp(c));
    return Math.max(1, Math.ceil(rem / HOH_XP_PER_HEART));
  }
  function __buildLvlConfirmHtml(c){
    var next = hohCharLevel(c) + 1;
    var need = __yhHeartsToNext(c);
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var heart = '<span class="hoh-yh-lvl-cf-heart">&#9829;&#xFE0E;</span>';
    if(bank <= 0){
      return '<div class="hoh-yh-lvl-cf-title">Reach Lvl ' + next + '</div>' +
        '<div class="hoh-yh-lvl-cf-sub">This level needs ' + need + ' ' + heart + ', but your bank is empty. Heart heroes in Explore to earn more.</div>' +
        '<button type="button" class="hoh-yh-lvl-cf-cancel" data-cf-cancel="1">Close</button>';
    }
    var full = bank >= need;
    var sub = full
      ? 'Spend ' + need + ' ' + heart + ' from your bank to finish this level instantly.'
      : 'You have ' + bank + ' of ' + need + ' ' + heart + ' needed. Pour all ' + bank + ' toward Lvl ' + next + '?';
    var goLabel = full
      ? 'Spend ' + need + ' ' + heart + ' &rarr; Lvl ' + next
      : 'Spend all ' + bank + ' ' + heart;
    return '<div class="hoh-yh-lvl-cf-title">Reach Lvl ' + next + '</div>' +
      '<div class="hoh-yh-lvl-cf-sub">' + sub + '</div>' +
      '<button type="button" class="hoh-yh-lvl-cf-go" data-cf-go="1">' + goLabel + '</button>' +
      '<button type="button" class="hoh-yh-lvl-cf-cancel" data-cf-cancel="1">Not now</button>';
  }
  function __showLvlConfirm(charId, anchor){
    var c = __findCharById(charId);
    if(!c || !anchor) return;
    var tip = __ensureLvlTip();
    tip.innerHTML = __buildLvlConfirmHtml(c);
    tip.classList.add('hoh-yh-lvl-confirm');
    tip.classList.add('hoh-yh-lvl-tooltip-measuring');
    tip.classList.add('is-shown');
    tip.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function(){
      __positionLvlTip(anchor);
      tip.classList.remove('hoh-yh-lvl-tooltip-measuring');
    });
    __lvlTipAnchor = anchor;
    __lvlTipPinned = true;
    /* R212: morph the + on the feed button into a > while the confirm is open. */
    try{ __cfGlyphEl = (anchor && anchor.querySelector) ? anchor.querySelector('.hoh-yh-quest-feed-glyph') : null; if(__cfGlyphEl) __cfGlyphEl.textContent='›'; }catch(_){}
    var go = tip.querySelector('[data-cf-go]');
    if(go){
      go.addEventListener('click', function(ev){
        ev.stopPropagation(); ev.preventDefault();
        if(go._busy) return; go._busy = true;
        go.classList.add('is-busy'); go.textContent = 'Leveling…';
        window.hohYhInstantLevel(charId, anchor);
      });
    }
    var cancel = tip.querySelector('[data-cf-cancel]');
    if(cancel){
      cancel.addEventListener('click', function(ev){ ev.stopPropagation(); ev.preventDefault(); __hideLvlTip(true); });
    }
  }
  /* Confirm handler: grant the hearts in ONE RPC, then reuse the existing
     level-up celebration (or a quiet bar bump on a partial pour).
     Recomputes the spend at click time so it never over-deducts if the
     bank changed while the box was open. */
  window.hohYhInstantLevel = async function(charId, anchor){
    var c = __findCharById(charId);
    if(!c){ __hideLvlTip(true); return; }
    var oldLevel = hohCharLevel(c);
    var need = __yhHeartsToNext(c);
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var spend = Math.min(need, bank);
    if(spend <= 0){
      __hideLvlTip(true);
      try { window.hohShowHeartToast && window.hohShowHeartToast('Not enough hearts', 'Heart heroes in Explore to earn more.'); } catch(_){}
      return;
    }
    if(typeof window.hohGrantCharacterXp !== 'function'){ __hideLvlTip(true); return; }
    var res = await window.hohGrantCharacterXp(charId, spend);
    __hideLvlTip(true);
    if(!res || !res.ok){
      try { window.hohShowHeartToast && window.hohShowHeartToast('Could not level up', 'Try again in a moment.'); } catch(_){}
      return;
    }
    var card = null;
    try {
      var pill = document.querySelector('.hoh-yh-quest-level[data-char-id="' + charId + '"]');
      card = pill ? pill.closest('.hoh-yh-card') : ((anchor && anchor.closest) ? anchor.closest('.hoh-yh-card') : null);
    } catch(_){ card = (anchor && anchor.closest) ? anchor.closest('.hoh-yh-card') : null; }
    if(!card) return;
    if(res.leveled_up && typeof window.hohYhCelebrateTicketLevelUp === 'function'){
      window.hohYhCelebrateTicketLevelUp(charId, card, { level: res.level, xp: res.xp, xp_to_next: res.xp_to_next, old_level: oldLevel });
    } else {
      try {
        var fill = card.querySelector('.hoh-yh-xp-bar-fill');
        if(fill){
          var pct = Math.max(0, Math.min(100, Math.round((res.xp / ((res.xp_to_next | 0) || 1)) * 100)));
          __yhSetBarPct(fill, pct, 'width 0.32s cubic-bezier(0.2, 0.8, 0.2, 1)');
        }
        window.hohShowHeartToast && window.hohShowHeartToast('Hearts poured', 'Spent ' + (res.hearts_spent || spend) + ' toward Lvl ' + (oldLevel + 1) + '.');
      } catch(_){}
    }
  };
  /* Double-tap detector on the + feed button. Capture-phase pointerup so
     the button's own stopPropagation (bubble) can't hide it from us. Two
     releases on the SAME feed button within 450ms open the confirm. We
     never preventDefault, so the taps still feed through the hold delegate. */
  var __yhLastFeedTap = { id: null, t: 0 };
  document.addEventListener('pointerup', function(e){
    try {
      var fb = (e.target && e.target.closest) ? e.target.closest('.hoh-yh-quest-feed') : null;
      if(!fb) return;
      var cid = fb.getAttribute('data-char-id');
      if(!cid) return;
      var now = Date.now();
      if(__yhLastFeedTap.id === cid && (now - __yhLastFeedTap.t) <= 450){
        __yhLastFeedTap.id = null; __yhLastFeedTap.t = 0;
        __showLvlConfirm(cid, fb);
      } else {
        __yhLastFeedTap.id = cid; __yhLastFeedTap.t = now;
      }
    } catch(_){}
  }, true);

  /* ===================================================================
     R148-Z22: HEART → XP SPEND HANDLER + HOLD-TO-STREAM DELEGATE
     ===================================================================
     The level pill is the primary heart-sink in the hall. The user
     spends 1 heart for 10 XP per tap. Holding the pill streams
     hearts continuously (like the main hall heart-stream). After
     each grant we animate the bar fill so even a single 10-XP
     award shows visible, satisfying progress. Level-ups trigger a
     sweep-to-100 + flash + reset celebration.

     The single-tap spend AND the hold-stream both route through the
     same async grant function. We coordinate via a document-level
     pointer delegate so a single press-and-hold drives the whole
     sequence — pointerdown fires the first spend, then a 280ms
     warmup arms the stream interval; pointerup/cancel cleans up.
     setPointerCapture keeps pointerup pinned to the pill even if
     the user drifts off mid-hold.
  */

  /* Visible feedback: pill "tap firing" — quick scale + flash glow
     after each successful spend. Applied + removed by the spend
     handler so each grant pops independently during a stream. */
  function __yhFireSpendPulse(btn){
    if(!btn) return;
    btn.classList.remove('hoh-yh-quest-level-firing');
    /* Force reflow so the re-add triggers a fresh animation cycle. */
    void btn.offsetWidth;
    btn.classList.add('hoh-yh-quest-level-firing');
  }

  /* Find the XP bar fill element that lives in the same card as the
     pill. We anchor by data-yh-xp-char (matches the pill's char-id)
     so we don't depend on DOM walking. */
  function __yhFindXpFill(charId){
    if(!charId) return null;
    return document.querySelector('[data-yh-xp-char="' + (window.CSS && CSS.escape ? CSS.escape(charId) : charId) + '"]');
  }

  /* Apply a new % width to the XP bar with a smooth transition.
     Caller is responsible for picking the transition timing/curve. */
  function __yhSetBarPct(fillEl, pct, transition){
    if(!fillEl) return;
    if(transition) fillEl.style.transition = transition;
    /* Clamp to [0, 100] so a buggy server response can't break the
       visual; the post-100 sweep is driven separately. */
    var v = Math.max(0, Math.min(100, pct));
    fillEl.style.width = v + '%';
  }

  /* Fire a brief shimmer pulse on the bar fill. At high levels (e.g.
     Mythic, where 1 heart = ~0.03% bar width), the width change is
     literally sub-pixel — the user wouldn't see anything happen. The
     pulse animation guarantees a visible response on every spend by
     flashing the bar's brightness + box-shadow regardless of width
     delta. ~280ms so a stream of 130ms-cadence spends overlap
     gracefully (each new spend resets the animation via reflow). */
  function __yhPulseBarFill(fillEl){
    if(!fillEl) return;
    fillEl.classList.remove('hoh-yh-xp-bar-fill-tick');
    /* Force reflow so re-add restarts the animation. */
    void fillEl.offsetWidth;
    fillEl.classList.add('hoh-yh-xp-bar-fill-tick');
    /* Remove the class after the animation so subsequent ticks can
       re-trigger cleanly. The reflow trick above also handles this
       but the explicit removeAfter keeps the DOM clean. */
    setTimeout(function(){
      if(fillEl) fillEl.classList.remove('hoh-yh-xp-bar-fill-tick');
    }, 300);
  }

  /* Update the LEVEL PILL'S tier class + text in place after a level-up
     so the user sees the new tier color immediately without a re-render.
     R148-Z22j: Always targets the LEVEL pill (the one carrying "Lvl N"
     text), NOT whatever the user clicked. If the click landed on the
     + feed button (which carries .hoh-yh-quest-feed), this used to
     overwrite the + glyph with "Lvl 13" — wrong. We now find the
     level pill via the .hoh-yh-quest-level:not(.hoh-yh-quest-feed)
     selector inside the same card.
     Tier classes get applied to BOTH the level pill (color change)
     AND the bar wrapper (gradient change). The clicked btn (which
     might be the + button) only gets the animation classes — never
     the textContent rewrite. */
  var __HOH_TIER_NAMES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
  function __yhFindLevelPill(clickedBtn){
    if(!clickedBtn) return null;
    var card = clickedBtn.closest('.hoh-yh-card');
    if(!card) return null;
    /* Prefer the non-feed pill (the "LVL N" one). Falls back to any
       .hoh-yh-quest-level if the markup ever changes. */
    return card.querySelector('.hoh-yh-quest-level:not(.hoh-yh-quest-feed)')
        || card.querySelector('.hoh-yh-quest-level');
  }
  function __yhSyncPillToLevel(clickedBtn, newLevel){
    var levelPill = __yhFindLevelPill(clickedBtn);
    if(!levelPill) return;
    levelPill.textContent = 'Lvl ' + newLevel;
    var newTier = (typeof window.hohLevelTier === 'function') ? window.hohLevelTier(newLevel) : 'common';
    /* Apply tier class to BOTH pills (level + feed) so they stay
       color-coordinated. */
    var card = clickedBtn.closest('.hoh-yh-card');
    if(card){
      var pills = card.querySelectorAll('.hoh-yh-quest-level');
      for(var p = 0; p < pills.length; p++){
        for(var i = 0; i < __HOH_TIER_NAMES.length; i++){
          pills[p].classList.remove('hoh-yh-quest-level-tier-' + __HOH_TIER_NAMES[i]);
        }
        pills[p].classList.add('hoh-yh-quest-level-tier-' + newTier);
      }
      /* Mirror onto the bar wrapper too so the bar's tier color tracks. */
      var bar = card.querySelector('.hoh-yh-xp-bar');
      if(bar){
        for(var j = 0; j < __HOH_TIER_NAMES.length; j++){
          bar.classList.remove('hoh-yh-xp-bar-tier-' + __HOH_TIER_NAMES[j]);
        }
        bar.classList.add('hoh-yh-xp-bar-tier-' + newTier);
      }
    }
  }

  /* Single-tap spend. The hot path: pointerdown calls this once
     immediately, then if the user keeps holding, the hold timer
     re-calls it on a cadence. Returns a promise that resolves to
     the RPC result (or {ok:false, reason:...} on failure). The
     animations run synchronously so they don't queue up if the
     user mashes.

     IMPORTANT: even spending 1 heart (10 XP) MUST produce a visible
     bar bump. We compute the new bar % from the RPC's xp_to_next
     return value so the bar tracks the true server state — no
     guesswork, no drift. */
  window.hohYhSpendHeartForXp = function(charId, btn, evt, opts){
    if(evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();
    if(evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    if(!charId || !btn) return Promise.resolve({ ok: false, reason: 'no_target' });
    var isStream = !!(opts && opts.isStream);
    /* Re-entrancy guard ONLY for single-tap mode — stream ticks need
       to fire in parallel since RPC latency (~100-300ms) exceeds the
       inter-tick cadence at peak (~50ms). The bank-deduction below
       reads CURRENT bank state per call, so parallel calls don't
       over-deduct. */
    if(!isStream && btn._hohXpSpending) return Promise.resolve({ ok: false, reason: 'in_flight' });
    if(!isStream) btn._hohXpSpending = true;
    /* Bail early if the wallet is empty — saves a round trip and gives
       the user a clean toast instead of an RPC roundtrip. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1){
      if(!isStream) btn._hohXpSpending = false;
      /* Throttle the toast — if you mash Enter we don't want 50
         "Not enough hearts" toasts. The hold-stream caller checks
         the bank itself, so this only fires on direct mis-tap. */
      if(!btn._hohXpEmptyToastedAt || (Date.now() - btn._hohXpEmptyToastedAt) > 1800){
        btn._hohXpEmptyToastedAt = Date.now();
        try { window.hohShowHeartToast && window.hohShowHeartToast('Not enough hearts', 'Heart some heroes first to earn more.'); } catch(_){}
      }
      return Promise.resolve({ ok: false, reason: 'insufficient' });
    }
    /* R148-Z22c+i: Heart sprite fires on EVERY spend (stream + single
       tap + keyboard). ALL XP spends use BLUE hearts ('xp' variant)
       because the user wants the visual to distinguish XP from
       heart-gifting consistently. Pink stays for actual heart-give
       on main hall cards.
       Jan, 2026-09-27: the blue hearts pop on the button and no longer
       fly out of the wallet pill; a fast stream draws small pops (see
       hohHeartBurst in hoh-wallet.js).
       R148-Z22d: Also pulse the wallet pill on every spend (re-add
       .hoh-tk-shed via reflow trick) so the user gets a visible
       "wallet reacted" cue even if their eye misses the count change
       — especially during fast streams where the count digit can
       update faster than the eye perceives motion. */
    try {
      if(typeof window.hohHeartBurst === 'function') window.hohHeartBurst(btn, { n: 1, tone: 'xp' });
      var walletPill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
      if(walletPill){
        /* Restart the shed animation on each spend via reflow. The
           class lifecycle: remove → force reflow → re-add. */
        walletPill.classList.remove('hoh-tk-shed');
        void walletPill.offsetWidth;
        walletPill.classList.add('hoh-tk-shed');
      }
    } catch(_){}
    /* Pulse the pill so it visibly reacts to the click even before
       the RPC completes. */
    __yhFireSpendPulse(btn);
    /* Fire the RPC. */
    if(typeof window.hohGrantCharacterXp !== 'function'){
      if(!isStream) btn._hohXpSpending = false;
      return Promise.resolve({ ok: false, reason: 'unavailable' });
    }
    var fill = __yhFindXpFill(charId);
    /* R148-Z22c: Apply the "feeding" glow on the bar wrapper. Each
       tick refreshes a 500ms auto-remove timer so the glow stays on
       through the stream and fades out shortly after the user
       releases. window.hohYhStreamStop (called from heart-hold's
       stopHold) clears it explicitly to avoid a 500ms tail. */
    if(fill && fill.parentElement){
      var barEl = fill.parentElement;
      barEl.classList.add('hoh-yh-xp-bar-feeding');
      if(barEl._hohFeedingTimeoutId) clearTimeout(barEl._hohFeedingTimeoutId);
      barEl._hohFeedingTimeoutId = setTimeout(function(){
        barEl.classList.remove('hoh-yh-xp-bar-feeding');
        barEl._hohFeedingTimeoutId = null;
      }, 500);
    }
    /* R148-Z22a: Cache the OLD level + xp + tier before the RPC
       fires. Lets us (1) detect tier-boundary crossings post-grant,
       and (2) optimistically nudge the bar forward INSTANTLY on
       click — no RPC-roundtrip wait. The grant function patches
       SOURCE_ROWS in-place so by the time .then() runs the row
       already holds the new level. */
    var oldLevel = 1;
    var oldXp = 0;
    var charRow = null;
    try {
      var rowsCache = (SOURCE_ROWS && SOURCE_ROWS.characters) || [];
      for(var iCache = 0; iCache < rowsCache.length; iCache++){
        if(rowsCache[iCache] && rowsCache[iCache].id === charId){
          charRow = rowsCache[iCache];
          oldLevel = parseInt(charRow.level, 10) || 1;
          oldXp = parseInt(charRow.xp, 10) || 0;
          break;
        }
      }
    } catch(_){}
    var oldTier = hohLevelTier(oldLevel);
    /* R148-Z22i: OPTIMISTIC ACCUMULATION. Previously oldXp was read
       from SOURCE_ROWS each call, so back-to-back clicks all bumped
       the bar to the SAME percentage (e.g., 5% every time) instead
       of accumulating. On RPC failure SOURCE_ROWS never got patched,
       which meant the bar visually "stuck" at the first-bump value.
       The user reported "EXP bar isn't moving as I spend hearts" —
       that's exactly this stuck-bump pattern.
       Fix: patch SOURCE_ROWS in-place OPTIMISTICALLY on every spend
       (assume success). Subsequent clicks read the accumulated value
       and bump further. The successful RPC response (in .then()
       below) overwrites with server-confirmed level/xp. On failure
       we revert. The bar now accumulates visibly across the whole
       stream — even if RPCs are slow or some fail, the visual
       progression matches the user's hearts spent.
       We also handle level-up locally: if optimistic xp crosses the
       threshold, predict the level bump so the bar resets visually
       in real time. */
    var xpToNextOld = hohXpForLevel(oldLevel + 1) || 1;
    var optimisticLevel = oldLevel;
    var optimisticXpAfter = oldXp + HOH_XP_PER_HEART;
    var optimisticLeveledUp = false;
    if(optimisticXpAfter >= xpToNextOld){
      /* Predict the level-up locally so the bar resets cleanly even
         before the server confirms. Server response will reconcile. */
      optimisticXpAfter = optimisticXpAfter - xpToNextOld;
      optimisticLevel = oldLevel + 1;
      optimisticLeveledUp = true;
    }
    /* Patch SOURCE_ROWS immediately so the next click reads the
       accumulated state. On RPC failure we revert below. */
    if(charRow){
      charRow.level = optimisticLevel;
      charRow.xp = optimisticXpAfter;
    }
    /* Bar bump uses the optimistic state. For level-up we bump to
       100 (the celebrate sequence below will paint the reset). For
       normal spends, bump to the accumulated percentage.
       R148-Z22k: SKIP the optimistic update entirely if a level-up
       celebrate sequence is currently animating this bar. Without
       this guard, fast-stream ticks during the 580ms celebrate
       window would clobber the sweep-to-100 / flash / snap-to-0
       phases — the bar would visually skip past the level-up
       animation. SOURCE_ROWS still gets patched so post-celebrate
       ticks resume from the correct state. */
    if(fill && !fill._yhCelebrating){
      var optimisticXpToNext = hohXpForLevel(optimisticLevel + 1) || 1;
      var optimisticPct = optimisticLeveledUp
        ? 100
        : Math.round((optimisticXpAfter / optimisticXpToNext) * 100);
      /* R148-Z22j: SMOOTH STREAM FILL. Stream transition is now 380ms
         linear (longer than every cadence stop on the heart-hold
         ramp: 320ms initial → 80ms mid → 50ms ultra). With a 380ms
         transition, each new tick INTERRUPTS the previous one mid-
         animation, so the browser smoothly interpolates from the
         current intermediate width to the new target. Result: the
         bar flows continuously instead of stepping in discrete chunks
         on every tick. For single taps we keep the snappier 140ms
         ease-out so a one-shot click still feels crisp.
         R148-Z22k: When this tick PREDICTS a level-up (optimisticLeveledUp),
         force the transition to a guaranteed-visible duration (260ms
         cubic-bezier) instead of the rapid stream linear. That way
         the user sees the bar's final climb to 100% even when
         heart-spam is firing 50ms apart — no more "skipped to 100"
         on close calls. */
      var transition;
      if(optimisticLeveledUp){
        transition = 'width 0.26s cubic-bezier(0.2, 0.8, 0.2, 1)';
      } else if(isStream){
        transition = 'width 0.38s linear';
      } else {
        transition = 'width 0.14s cubic-bezier(0.2, 0.8, 0.2, 1)';
      }
      __yhSetBarPct(fill, optimisticPct, transition);
    }
    return window.hohGrantCharacterXp(charId, 1).then(function(res){
      if(!isStream) btn._hohXpSpending = false;
      if(!res || !res.ok){
        /* R148-Z22i: Revert the OPTIMISTIC patch we applied above so
           the next click reads the TRUE pre-spend state. Without this
           the bar would "fake-fill" indefinitely while every server
           call refunds. */
        if(charRow){
          charRow.level = oldLevel;
          charRow.xp = oldXp;
        }
        if(fill){
          var revertPct = Math.round((oldXp / xpToNextOld) * 100);
          __yhSetBarPct(fill, revertPct, 'width 0.18s ease-out');
        }
        /* R148-Z22i: One-shot console log on the FIRST failure of a
           gesture so debugging is easy without spamming. The user
           specifically asked to "do research online" — the console
           details below tell them exactly what the server returned
           so they can diagnose deploy status / RLS / permissions. */
        if((btn._xpHoldFailCount || 0) === 0){
          try {
            console.warn('[hohYhSpendHeartForXp] RPC failed', {
              reason: res && res.reason,
              detail: res && res.detail,
              charId: charId
            });
          } catch(_){}
        }
        /* R148-Z22c: Track consecutive failures so the heart-hold
           loop can self-terminate (see top of fire() — checks
           btn._xpHoldFailCount >= 20 to kill the stream). Resets to
           0 on the next successful spend. */
        btn._xpHoldFailCount = (btn._xpHoldFailCount || 0) + 1;
        /* Failure: hearts were refunded inside hohGrantCharacterXp.
           Show the right toast for each failure mode. Throttle so
           a held button doesn't spam. */
        var nowMs = Date.now();
        if(!btn._hohXpFailToastedAt || (nowMs - btn._hohXpFailToastedAt) > 1800){
          btn._hohXpFailToastedAt = nowMs;
          var reason = res && res.reason;
          /* Detect "function not found" — most likely cause is the
             SQL migration not being deployed. Surface this clearly
             so the user knows what to fix. */
          var errMsg = '';
          try { errMsg = (res && res.detail && (res.detail.message || res.detail)) ? String(res.detail.message || res.detail) : ''; } catch(_){}
          var fnMissing = /function .* does not exist|not find the function|grant_character_xp/i.test(errMsg);
          if(reason === 'insufficient'){
            try { window.hohShowHeartToast && window.hohShowHeartToast('Not enough hearts', 'Heart some heroes first to earn more.'); } catch(_){}
          } else if(reason === 'no_account'){
            try { window.hohShowHeartToast && window.hohShowHeartToast('Sign in to level up', 'Your levels save across devices once you sign in.'); } catch(_){}
          } else if(reason === 'rpc_unavailable'){
            try { window.hohShowHeartToast && window.hohShowHeartToast('Offline', 'XP saves once you reconnect.'); } catch(_){}
          } else if(reason === 'not_owner'){
            try { window.hohShowHeartToast && window.hohShowHeartToast('Not yours', 'You can only level up heroes you submitted.'); } catch(_){}
          } else if(fnMissing || reason === 'rpc_error'){
            try { window.hohShowHeartToast && window.hohShowHeartToast('Server not ready', 'XP save function missing. Deploy R148-Z22 SQL.'); } catch(_){}
          } else {
            try { window.hohShowHeartToast && window.hohShowHeartToast('Save failed', 'Try again in a moment.'); } catch(_){}
          }
        }
        return res;
      }
      /* Success — reset the failure counter so a future failure
         doesn't dead-lock the pill due to stale fail count. */
      btn._xpHoldFailCount = 0;
      /* Success path. xp_to_next is the COST of the next level.
         res.xp is XP banked toward that next level after the grant.
         A leveled_up:true means xp_to_next is the cost of the NEW
         next level (already updated), and res.xp is the leftover. */
      var newLevel = res.level;
      var xpToNext = res.xp_to_next || 1;
      var targetPct = Math.max(0, Math.min(100, Math.round((res.xp / xpToNext) * 100)));
      var newTier = hohLevelTier(newLevel);
      var isTierUp = res.leveled_up && (newTier !== oldTier);
      /* R148-Z22j: Transition speed depends on mode. Stream mode
         uses a LONG linear transition (380ms) so successive ticks
         interrupt each other smoothly — the bar continuously
         interpolates instead of stepping in discrete chunks. Single-
         tap mode uses a snappier ease-out for crisp feedback. */
      var fillCurve = isStream
        ? 'width 0.38s linear'
        : 'width 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)';
      if(res.leveled_up){
        /* R148-Z22a: Cleaner level-up — single tight sequence.
           1. Sweep bar to 100 (160ms)
           2. Flash + pill swap (mid-flash hides the snap-reset)
           3. Snap to 0 + smooth fill to new target (200ms)
           Total ~620ms. Tier-up adds extra flash duration + a
           bigger glow burst on the bar AND a brighter pill flash.
           R148-Z22j: Level-up animation classes target the LEVEL
           PILL (the one with "LVL N"), not the clicked button. The
           clicked button (which may be the + feed button) gets only
           the spend-pulse, not the level-up bloom. */
        /* R148-Z22k: Sweep timing extended (160 → 240ms) so the bar's
           climb to 100% is unmistakably visible even when the user
           is heart-spamming at peak cadence. The previous 160ms
           sweep was easy to miss inside a fast stream. */
        /* R437: was also refreshing the heart bank total here. The bank is
           gone; reconciling the source row to the server level is still
           needed so the level ring and XP bar do not lag a reload behind. */
        try {
          if(charRow){ charRow.level = res.level; charRow.xp = res.xp; }
        } catch(_){}
        var sweepMs = 240;
        var flashHoldMs = isTierUp ? 360 : 240;
        var levelPill = __yhFindLevelPill(btn);
        if(fill){
          /* Mark the bar as celebrating so concurrent stream ticks
             skip their optimistic-bar update for the duration of
             the level-up animation (set above, cleared at the very
             end of the sequence). Without this, ticks fired during
             the celebrate window would clobber the sweep / flash /
             snap-to-0 / fill-to-target phases and the user would
             see the bar jump straight to the new target. */
          fill._yhCelebrating = true;
          __yhSetBarPct(fill, 100, 'width ' + (sweepMs / 1000) + 's cubic-bezier(0.2, 0.8, 0.2, 1)');
          fill.classList.add('hoh-yh-xp-bar-fill-celebrate');
          if(isTierUp) fill.classList.add('hoh-yh-xp-bar-fill-tierup');
        }
        if(levelPill){
          levelPill.classList.add('hoh-yh-quest-level-leveling');
          if(isTierUp) levelPill.classList.add('hoh-yh-quest-level-tierup');
        }
        setTimeout(function(){
          /* Update pill text + tier class so the user sees the new
             level number at the peak of the flash. The class swap is
             a hard step — the flash overlay masks the visual jump.
             __yhSyncPillToLevel finds the LEVEL pill (not the +)
             internally. */
          __yhSyncPillToLevel(btn, newLevel);
        }, sweepMs);
        setTimeout(function(){
          /* Toast — tier-up uses tier name for emphasis, regular
             level-up uses Lvl number. */
          try {
            if(isTierUp){
              var tierLabel = newTier.charAt(0).toUpperCase() + newTier.slice(1);
              window.hohShowHeartToast && window.hohShowHeartToast(
                tierLabel + ' tier reached!',
                'Lvl ' + newLevel + ' — XP costs ramp up from here.'
              );
            } else {
              window.hohShowHeartToast && window.hohShowHeartToast('Lvl ' + newLevel + '!', 'Your hero leveled up.');
            }
          } catch(_){}
        }, sweepMs + 60);
        setTimeout(function(){
          /* Hidden snap-reset behind the flash overlay, then animate
             forward to the new target. The flash bloom is at peak
             brightness here so the user's eye doesn't catch the
             instant width=0 frame. */
          if(fill){
            __yhSetBarPct(fill, 0, 'none');
            void fill.offsetWidth;
            __yhSetBarPct(fill, targetPct, 'width 0.20s cubic-bezier(0.2, 0.8, 0.2, 1)');
          }
        }, sweepMs + flashHoldMs);
        setTimeout(function(){
          if(fill){
            fill.classList.remove('hoh-yh-xp-bar-fill-celebrate');
            fill.classList.remove('hoh-yh-xp-bar-fill-tierup');
            /* R148-Z22k: Release the celebrating lock so concurrent
               stream ticks resume their optimistic bar updates. SOURCE_ROWS
               was kept in sync by the optimistic patch above, so the next
               tick reads correct state and the bar resumes from there. */
            fill._yhCelebrating = false;
          }
          /* R148-Z22j: Strip the level-up classes from the LEVEL PILL
             (not the clicked btn — those classes were never on the +
             button to begin with). */
          var lp = __yhFindLevelPill(btn);
          if(lp){
            lp.classList.remove('hoh-yh-quest-level-leveling');
            lp.classList.remove('hoh-yh-quest-level-tierup');
          }
        }, sweepMs + flashHoldMs + 280);
      } else {
        /* No level-up: smooth ease to the new %, plus a shimmer pulse
           so even sub-pixel width changes (Mythic-tier hearts) feel
           responsive. Stream mode uses a shorter transition so the
           bar visibly tracks each tick instead of catching up in
           bursts. */
        if(fill){
          __yhSetBarPct(fill, targetPct, fillCurve);
          __yhPulseBarFill(fill);
        }
      }
      return res;
    }).catch(function(e){
      if(!isStream) btn._hohXpSpending = false;
      /* R148-Z22i: Revert optimistic patch on exception same as
         failure path. */
      if(charRow){
        charRow.level = oldLevel;
        charRow.xp = oldXp;
      }
      if(fill){
        var revertPct2 = Math.round((oldXp / xpToNextOld) * 100);
        __yhSetBarPct(fill, revertPct2, 'width 0.18s ease-out');
      }
      btn._xpHoldFailCount = (btn._xpHoldFailCount || 0) + 1;
      try { console.warn('[hohYhSpendHeartForXp] RPC exception', e); } catch(_){}
      try { window.hohShowHeartToast && window.hohShowHeartToast('Save failed', 'Try again in a moment.'); } catch(_){}
      return { ok: false, reason: 'exception', detail: String(e) };
    });
  };

  /* R148-Z22c: Hold-to-stream is owned by hoh-heart-hold.js — the
     same delegate that drives the main-hall heart-spam. Adding
     .hoh-yh-quest-level[data-yh-spend] to that delegate's selector
     means the XP pill picks up the ramping cadence (320ms → 80ms
     over 4s, then 50ms ultra after 6s), frenzy escalation at 10 /
     30 / 60 ticks, auto-stop on wallet drain, and click-suppression
     — for free.
     The delegate calls window.hohYhSpendHeartForXp(charId, btn,
     null, { isStream: true, streamIdx }) on every tick; the
     keyboard / Enter activation path still calls the same function
     with no opts (single-tap mode).
     window.hohYhStreamStop(btn) below is called from stopHold so
     we can clean up the bar feeding glow + reset failure counter. */
  window.hohYhStreamStop = function(btn){
    if(!btn) return;
    /* Drop the feeding glow on the bar wrapper if it's the current
       active feeding target. */
    try {
      var charId = btn.getAttribute('data-char-id');
      if(charId){
        var fill = __yhFindXpFill(charId);
        if(fill && fill.parentElement){
          var bar = fill.parentElement;
          bar.classList.remove('hoh-yh-xp-bar-feeding');
          if(bar._hohFeedingTimeoutId){
            clearTimeout(bar._hohFeedingTimeoutId);
            bar._hohFeedingTimeoutId = null;
          }
        }
      }
    } catch(_){}
    /* Reset the consecutive-failure counter for a fresh next hold.
       Without this, three failures on one hold (e.g., RPC down)
       permanently dead-locks the pill. */
    btn._xpHoldFailCount = 0;
  };

  /* R148-Z22p: Ticket-driven level-up celebration. Called from
     hoh-tickets.js after level_up_character_by_ticket succeeds.
     Reuses the same sweep + flash + reset + refill animation
     pipeline as the XP-spend level-up, so the user sees a familiar
     visual that says "your hero leveled up" regardless of whether
     they got there via hearts or via spending a ticket. The card
     argument is the .hoh-yh-card the click came from. lvlData is
     the server response: { old_level, level, xp, xp_to_next }. */
  /* R152: Shared level-up pulse helper. Pass the card element + a flag
     for whether this is a tier-up. Both cases get a soft scale pulse
     and a rainbow glow bloom; a tier-up additionally keeps the gold
     gleam ring. This replaces the old R148-Z38 side-to-side shake,
     which read as an error state (a rejected-input jitter) rather
     than a reward. The class is removed after the animation completes
     so subsequent level-ups re-trigger cleanly. */
  function _yhTriggerCardPulse(cardEl, isTierUp){
    if(!cardEl || !cardEl.classList) return;
    var cls = isTierUp ? 'hoh-yh-card-tierup-pulse' : 'hoh-yh-card-levelup-pulse';
    /* Strip both classes first so a back-to-back level-up retriggers
       the keyframe even if the previous animation hasn't fully cleared. */
    cardEl.classList.remove('hoh-yh-card-levelup-pulse');
    cardEl.classList.remove('hoh-yh-card-tierup-pulse');
    /* Force reflow so the animation restarts on re-add. */
    void cardEl.offsetWidth;
    cardEl.classList.add(cls);
    /* dur sits just past the CSS animation length so the class is
       stripped only after the keyframe has fully played out. */
    var dur = isTierUp ? 960 : 680;
    setTimeout(function(){
      try { cardEl.classList.remove(cls); } catch(_){}
    }, dur);
  }
  window.hohYhCelebrateTicketLevelUp = function(charId, card, lvlData){
    if(!charId || !card || !lvlData) return;
    var fill = card.querySelector('.hoh-yh-xp-bar-fill');
    var levelPill = card.querySelector('.hoh-yh-quest-level:not(.hoh-yh-quest-feed)')
                 || card.querySelector('.hoh-yh-quest-level');
    var newLevel  = lvlData.level | 0;
    var xpToNext  = (lvlData.xp_to_next | 0) || 1;
    var newXp     = lvlData.xp | 0;
    var targetPct = Math.max(0, Math.min(100, Math.round((newXp / xpToNext) * 100)));
    var oldTier   = (typeof window.hohLevelTier === 'function')
                    ? window.hohLevelTier((lvlData.old_level | 0) || (newLevel - 1)) : 'common';
    var newTier   = (typeof window.hohLevelTier === 'function') ? window.hohLevelTier(newLevel) : 'common';
    var isTierUp  = oldTier !== newTier;
    /* R152: Pulse the whole character card on every level-up. A
       regular level-up gets a rainbow pulse; a tier-up gets the
       rainbow pulse plus the gold gleam ring. */
    _yhTriggerCardPulse(card, isTierUp);
    var sweepMs       = 240;
    var flashHoldMs   = isTierUp ? 360 : 240;

    if(fill){
      fill._yhCelebrating = true;
      __yhSetBarPct(fill, 100, 'width ' + (sweepMs / 1000) + 's cubic-bezier(0.2, 0.8, 0.2, 1)');
      fill.classList.add('hoh-yh-xp-bar-fill-celebrate');
      if(isTierUp) fill.classList.add('hoh-yh-xp-bar-fill-tierup');
    }
    if(levelPill){
      levelPill.classList.add('hoh-yh-quest-level-leveling');
      if(isTierUp) levelPill.classList.add('hoh-yh-quest-level-tierup');
    }
    setTimeout(function(){
      __yhSyncPillToLevel(levelPill || card.querySelector('.hoh-yh-quest-level'), newLevel);
    }, sweepMs);
    setTimeout(function(){
      try {
        if(isTierUp){
          var tierLabel = newTier.charAt(0).toUpperCase() + newTier.slice(1);
          window.hohShowHeartToast && window.hohShowHeartToast(
            tierLabel + ' tier reached!',
            'Lvl ' + newLevel + ' — ticket-blessed.'
          );
        } else {
          window.hohShowHeartToast && window.hohShowHeartToast(
            'Lvl ' + newLevel + '!',
            'Ticket leveled this hero up.'
          );
        }
      } catch(_){}
    }, sweepMs + 60);
    setTimeout(function(){
      if(fill){
        __yhSetBarPct(fill, 0, 'none');
        void fill.offsetWidth;
        __yhSetBarPct(fill, targetPct, 'width 0.20s cubic-bezier(0.2, 0.8, 0.2, 1)');
      }
    }, sweepMs + flashHoldMs);
    setTimeout(function(){
      if(fill){
        fill.classList.remove('hoh-yh-xp-bar-fill-celebrate');
        fill.classList.remove('hoh-yh-xp-bar-fill-tierup');
        fill._yhCelebrating = false;
      }
      if(levelPill){
        levelPill.classList.remove('hoh-yh-quest-level-leveling');
        levelPill.classList.remove('hoh-yh-quest-level-tierup');
      }
    }, sweepMs + flashHoldMs + 280);
  };

  /* R148-Z22p: Passive XP batch catch-up. Called once per Your Hall
     open to ensure every visible character bar reflects today's
     passive XP before the user sees it. Cached for 5 minutes so a
     rapid open/close/open doesn't pound the RPC. The RPC processes
     all char IDs server-side in one round-trip; we patch SOURCE_ROWS
     with each result's new level/xp so the next render uses fresh
     values. Failures are silent — passive XP catches up next time. */
  var __yhPassiveLastRunAt = 0;
  var __yhPassiveLastEmail = '';
  var __yhPassiveCacheMs = 5 * 60 * 1000; /* 5 minutes */
  window.hohYhCatchupPassiveXp = function(force){
    try {
      var email = (typeof window.hohYourHallEmail === 'function')
                  ? (window.hohYourHallEmail() || '').toLowerCase().trim() : '';
      if(!email) return;
      var now = Date.now();
      if(!force && __yhPassiveLastEmail === email && (now - __yhPassiveLastRunAt) < __yhPassiveCacheMs){
        return;
      }
      var sb = window.hohSupabaseClient;
      if(!sb || !sb.rpc) return;
      /* Gather char IDs from the cached source rows. Only the user's
         own submitted heroes get passive XP catch-up surfaced; their
         hearted-others list also progresses passively server-side
         but we don't need to fetch those visually here. */
      var rows = (typeof SOURCE_ROWS !== 'undefined' && SOURCE_ROWS && SOURCE_ROWS.characters) ? SOURCE_ROWS.characters : null;
      if(!rows || !rows.length) return;
      /* SOURCE_ROWS.characters can include hearted rows (other
         people's heroes the user has hearted) when the view is
         showing both their own submissions and their hearted list.
         _kind === 'hearted' tags those — filter them out so we only
         catch up on the user's OWN submitted heroes. Other people's
         heroes get their passive XP caught up when those users open
         their own Your Hall. */
      var ids = [];
      for(var i = 0; i < rows.length; i++){
        var r = rows[i];
        if(!r || !r.id) continue;
        if(r._kind === 'hearted') continue;
        ids.push(r.id);
      }
      if(!ids.length) return;
      __yhPassiveLastRunAt = now;
      __yhPassiveLastEmail = email;
      sb.rpc('catchup_character_passive_xp_batch', { p_character_ids: ids })
        .then(function(res){
          if(res && res.error){ try { console.warn('[hohYhCatchupPassiveXp] RPC error', res.error); } catch(_){} return; }
          var data = res && res.data;
          if(!data || data.ok !== true || !Array.isArray(data.results)) return;
          /* Patch each row with the post-catchup state. */
          var updates = data.results;
          for(var u = 0; u < updates.length; u++){
            var upd = updates[u];
            if(!upd || upd.ok !== true || !upd.id) continue;
            for(var j = 0; j < rows.length; j++){
              if(rows[j] && rows[j].id === upd.id){
                rows[j].level = upd.level;
                rows[j].xp    = upd.xp;
                break;
              }
            }
          }
          /* Re-render the dashboard if it's open so bars + level
             numbers reflect the freshly-applied passive XP. */
          try {
            if(typeof renderDashboard === 'function'
               && document.body && document.body.classList.contains('hoh-page-mode-yours')){
              renderDashboard();
            }
          } catch(_){}
        })
        .catch(function(e){ try { console.warn('[hohYhCatchupPassiveXp] threw', e); } catch(_){} });
    } catch(_){}
  };

  function cardHtml(c, mineCount, sortKey){
    var name = esc(c.name || 'Unnamed');
    /* R136-meta: drop the "· Lvl N" suffix from the race/class subtitle.
       The character's hall level is now expressed via the Lvl pill (which
       has a hover tooltip). The raw c.level field on the subtitle was
       redundant and visually noisy. */
    var meta = esc((c.race || '') + (c.character_class ? ' ' + c.character_class : ''));
    var firstLetter = esc((c.name || '?').charAt(0));
    var img = c.image_thumb_url || c.image_url;
    /* R265: honor the saved reframe here too. Your Hall cards previously
       shipped no object-position, so a hero the owner reframed on the main
       grid showed a default center-crop on their personal grid — one of the
       "not saved everywhere" surfaces. Read the shared offset so every grid
       agrees. */
    var yhPos = (typeof window.hohImgObjPos === 'function') ? window.hohImgObjPos(c.id, 'primary') : '50% 50%';
    var yhStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + yhPos + ';');
    var media = img
      ? '<img src="' + esc(img) + '" alt="' + name + '" loading="lazy" decoding="async" crossorigin="anonymous" data-letter="' + firstLetter + '" style="' + yhStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-yh-letter">' + firstLetter + '</div>';
    var tier = rarTier(c);
    /* R136-level: sort-aware bottom-row content.
       - When the user has sorted by 'mine' (Most yours), keep the
         "x# yours" pill visible — it's the metric they're looking at.
       - On every OTHER sort in the Profile / My Submitted view, hide
         the pill and surface the level + EXP bar + harvest button
         instead. The level system lives ONLY here (Profile +
         own-submitted rows + non-mine sort) so it stays a personal
         stewardship reward, not a public hall flex.
       Hearted rows in profile (c._kind === 'hearted') never get the
       level treatment — those characters belong to other submitters.
       Their pill behavior follows the original rule (hide when
       sort != 'mine'). */
    var isOwnSubmittedRow = c && c._kind !== 'hearted';
    var showLevelCluster = (currentView === 'characters'
      && isOwnSubmittedRow
      && sortKey && sortKey !== 'mine');
    var showMineChip = (sortKey === 'mine') && mineCount && mineCount > 0;
    var mineChip = showMineChip
      ? '<span class="hoh-yh-mine-chip">x' + mineCount + ' yours</span>'
      : '';
    var heartChip = '<span class="hoh-yh-hearts">&#9829;&#xFE0E; ' + (c.hearts||0) + '</span>';
    /* R136-quest: Two-state quest row — replaces the bolted-on level
       bar from R136-level. Designed to feel native to the card body
       (no heavy gradient strip, no border-top) and morphs cleanly
       between READY and ON-QUEST states.
       READY: small "Lvl N" chip on the left + a "Receive +N" gold
       pill button on the right. The user taps to send the hero on a
       quest and claim their spoils.
       ON-QUEST: no level number, no button — only "On a quest · 18h
       42m" italic text plus a thin progress bar that slowly fills
       toward 100% as the cooldown elapses. The bar shimmers gently
       so the surface feels alive while the user waits. */
    var levelClusterHtml = '';
    if(showLevelCluster){
      var lvl = hohCharLevel(c);
      var onQuest = hohIsCharOnQuest(c.id);
      /* R148-Z22: Cap class now tracks the XP-system soft cap (100)
         instead of the old legacy 20. Past level 100 we add a
         "maxed" hint visually but the spend mechanic still works
         (XP costs balloon dramatically — MapleStory soft cap). */
      var capCls = (lvl >= HOH_LEVEL_SOFT_CAP) ? ' hoh-yh-quest-row-maxed' : '';
      /* R148-Z22: Tier-colored pill. The CSS uses .hoh-yh-quest-level-tier-{tier}
         to paint the chip + glow based on rarity ladder (top-heavy:
         1-20 Common, 21-40 Uncommon, 41-60 Rare, 61-75 Epic, 76-90
         Legendary, 91+ Mythic). Mythic chars get the brand rainbow. */
      var lvlTier = hohLevelTier(lvl);
      var xpProg = Math.round(hohCharLevelProgress(c) * 100);
      /* R148-Z22: The Lvl pill is shared by both READY and QUESTING
         states. The XP bar replaces the old quest cooldown bar and
         sits UNDER the pill, always showing progress toward the next
         level. Pointer-down on the pill spends 1 heart for 10 XP
         (delegated handler — see hohYhPointer setup below). Hold to
         stream hearts continuously, like the main hall heart stream.
         The onclick is just event.stopPropagation so the card's
         detail modal doesn't open underneath. onkeydown handles
         keyboard accessibility (Enter / Space). */
      var pillHtml = '<span class="hoh-yh-quest-level hoh-yh-quest-level-tier-' + lvlTier + '" role="button" tabindex="0" ' +
        'data-char-id="' + esc(c.id) + '" ' +
        'data-yh-spend="1" ' +
        'aria-label="Level ' + lvl + '. Tap to spend 1 heart for XP. Hold to stream." ' +
        'onmouseenter="window.hohYhShowLevelTip&&hohYhShowLevelTip(\'' + esc(c.id) + '\',this)" ' +
        'onmouseleave="window.hohYhHideLevelTip&&hohYhHideLevelTip()" ' +
        'onfocus="window.hohYhShowLevelTip&&hohYhShowLevelTip(\'' + esc(c.id) + '\',this)" ' +
        'onblur="window.hohYhHideLevelTip&&hohYhHideLevelTip()" ' +
        'onclick="event.stopPropagation();event.preventDefault();" ' +
        'oncontextmenu="event.preventDefault();return false;" ' +
        'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();event.stopPropagation();window.hohYhSpendHeartForXp&&hohYhSpendHeartForXp(\'' + esc(c.id) + '\',this,event);}">Lvl ' + lvl + '</span>';
      /* R148-Z22d: Dedicated "+ feed" button right next to the pill.
         Per user request — a smaller, more obvious spend affordance
         than tapping the pill itself. The pill still works (same
         delegate selector), but the + button reads as "I am the
         action button" so first-time users know what to press.
         Note: this carries the SAME .hoh-yh-quest-level class +
         data-yh-spend attr so the heart-hold delegate picks it up
         with the same ramping cadence / frenzy escalation / blue
         heart-fly that the pill gets. The visual is a small
         circular chip with a + glyph; the spend logic is identical. */
      var feedBtnHtml = '<span class="hoh-yh-quest-level hoh-yh-quest-feed hoh-yh-quest-level-tier-' + lvlTier + '" role="button" tabindex="0" ' +
        'data-char-id="' + esc(c.id) + '" ' +
        'data-yh-spend="1" ' +
        'aria-label="Feed 1 heart of XP to this hero. Hold to stream." ' +
        'title="Feed XP — tap or hold" ' +
        'onclick="event.stopPropagation();event.preventDefault();" ' +
        'oncontextmenu="event.preventDefault();return false;" ' +
        'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();event.stopPropagation();window.hohYhSpendHeartForXp&&hohYhSpendHeartForXp(\'' + esc(c.id) + '\',this,event);}">' +
        '<span class="hoh-yh-quest-feed-glyph" aria-hidden="true">+</span>' +
      '</span>';
      /* R148-Z22: XP bar — always rendered, in both READY and QUESTING
         states. Replaces the old quest-cooldown bar. The 10 evenly-
         spaced tick marks (League of Legends HP-bar style) come from
         the .hoh-yh-xp-bar-ticks pseudo-element. data-yh-xp-char lets
         the spend handler find the right bar to animate without a
         full re-render. */
      var xpBarHtml = '<span class="hoh-yh-xp-bar hoh-yh-xp-bar-tier-' + lvlTier + '" aria-hidden="true">' +
        '<span class="hoh-yh-xp-bar-fill" data-yh-xp-char="' + esc(c.id) + '" style="width:' + xpProg + '%"></span>' +
        '<span class="hoh-yh-xp-bar-ticks" aria-hidden="true"></span>' +
      '</span>';
      /* R148-Z22a: Right-side slot is now action-only. The quest
         status text moved UP into the chips row (alongside hearts /
         tickets) per user request — keeps the bottom level cluster
         dedicated to the heart-sink mechanic. When questing we have
         no right-side action (the Receive button is suppressed
         while on cooldown), so the pill sits alone on the top row
         and the bar fills the row beneath. */
      /* R227: Per hero Receive removed. All hero hearts now pool into the
         single heart bank bar above the grid, claimed once a day per
         account and gated server side. The right slot stays empty so the
         pill and feed button sit alone on the top row. */
      var rightHtml = '';
      /* R148-Z22: Two-row cluster. Top row = pill + feed-btn (left)
         + action (right, READY only). Bottom row = XP bar full-width. */
      levelClusterHtml = '<span class="hoh-yh-quest-row hoh-yh-quest-row-stacked' + capCls + '">' +
        '<span class="hoh-yh-quest-row-top">' +
          pillHtml +
          feedBtnHtml +
          rightHtml +
        '</span>' +
        xpBarHtml +
      '</span>';
    }
    /* Ticket pill rules:
       - Hearted rows in Profile (c._kind==='hearted') HIDE ticket count
         since those tickets belong to another player and aren't the
         user's own raffle stake.
       - Submitted rows show their tickets pill.
       - Legacy 'hearted' view path (separate tab) hides too. */
    var hideTicket = (c._kind === 'hearted') || (currentView === 'hearted');
    var tk = (!hideTicket && c && typeof c.tickets === 'number' && c.tickets > 0)
      ? '<span class="hoh-tk hoh-tk-card" aria-label="' + c.tickets + ' raffle tickets">' +
          '<span class="hoh-tk-icon" aria-hidden="true"></span>' +
          '<span class="hoh-tk-count">' + c.tickets + '</span>' +
        '</span>'
      : '';
    /* R148-Z22a: Quest status chip now lives in the .hoh-yh-chips row
       alongside hearts/tickets (moved UP from the level cluster per
       user request). data-yh-quest-id + role attrs let the 1s ticker
       keep rewriting the seconds counter in place without re-rendering
       the surrounding card. Only rendered when the level cluster
       would also show (own-submitted rows on non-mine sort) AND the
       char is currently on a quest. */
    /* R227: The per hero on a quest countdown chip is gone. Heroes no
       longer go on individual cooldowns. The single daily heart bank
       claim replaces per hero claiming, so cards stay in the clean
       ready state showing level and XP only. */
    var questStatusChip = '';
    /* The labeled action belongs to every active card in this collection,
       including a hearted Hall hero the player also owns. The shared predicate
       reads the active-copy cache; submitted rows remain the startup fallback
       because their card is granted automatically. */
    var canReceiveTicket = false;
    try {
      canReceiveTicket = typeof window.hohCanApplyTicketToCharacter === 'function'
        ? !!window.hohCanApplyTicketToCharacter(c && c.id)
        : (!hideTicket && !!c && c._kind !== 'hearted');
    } catch(_){ canReceiveTicket = !hideTicket && !!c && c._kind !== 'hearted'; }
    var unspentNow = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    /* Star toggle (favorite) — only on Profile rows. Lets the user
       quickly add a hearted hero to their saved Favorites without
       leaving the page. Uses span+role=button so we don't nest a
       real button inside the card button. stopPropagation on click
       prevents the card from opening detail. */
    var savedNow = (typeof window.hohHallIsSaved === 'function') && window.hohHallIsSaved(c.id);
    var starHtml = '';
    if(currentView === 'characters'){
      starHtml = '<span role="button" tabindex="0" class="hoh-yh-card-star' + (savedNow ? ' hoh-yh-card-star-on' : '') + '" data-yh-star="' + esc(c.id) + '" aria-label="' + (savedNow ? 'Remove from favorites' : 'Add to favorites') + '" onclick="event.stopPropagation();window.hohYhCardToggleStar&&hohYhCardToggleStar(\'' + esc(c.id) + '\',this)">' +
        '<svg viewBox="0 0 24 24" fill="' + (savedNow ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' +
      '</span>';
    }
    /* R99: "Give ticket" button moved from the bottom chip row to a
       header row alongside the name + meta. Hides entirely when the
       user's wallet is at 0 unspent tickets, so the row collapses to
       just name/meta on those cards. */
    var giveTicketBtn = '';
    /* R793: no ticket is given from Your Hall in the live Hall. A hero's
       daily ticket is given on its own page in the main Hall. */
    var r793NoGive = (typeof window.hohR793Live === 'function') && window.hohR793Live();
    if(canReceiveTicket && unspentNow > 0 && !r793NoGive){
      giveTicketBtn =
        '<span role="button" tabindex="0" class="hoh-yh-card-give-ticket" data-yh-give="' + esc(c.id) + '" ' +
          'aria-label="Give 1 raffle ticket to this hero" ' +
          'onclick="event.stopPropagation();window.hohYhApplyTicketFromCard&&hohYhApplyTicketFromCard(\'' + esc(c.id) + '\',this)" ' +
          'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();event.stopPropagation();window.hohYhApplyTicketFromCard&&hohYhApplyTicketFromCard(\'' + esc(c.id) + '\',this);}">' +
          '<span class="hoh-yh-card-give-ticket-label">Give ticket</span>' +
        '</span>';
    }
    return '<button type="button" class="hoh-yh-card hoh-yh-rar-' + tier + (c._kind ? ' hoh-yh-card-kind-' + c._kind : '') + '" ' +
      'data-hero-id="' + esc(c.id) + '" ' +
      'onclick="hohOpenDetail(\'' + esc(c.id) + '\')" aria-label="Open ' + name + '">' +
      starHtml +
      '<span class="hoh-yh-media">' + media + '</span>' +
      '<span class="hoh-yh-body">' +
        '<span class="hoh-yh-body-head">' +
          '<span class="hoh-yh-body-head-text">' +
            '<span class="hoh-yh-name">' + name + '</span>' +
            '<span class="hoh-yh-meta">' + meta + '</span>' +
          '</span>' +
          questStatusChip +
        '</span>' +
        /* R148-Z22m: Give-ticket moved OUT of body-head (where it
           was competing with name + class + timer and forcing them
           to truncate to "E." / "W." on narrower cards). Now lives
           in the chips row, flush right via margin-left:auto in the
           CSS — semantically natural since it's a TICKET action
           sitting next to the ticket count. The chips row already
           has flex-wrap, so on very narrow mobile cards it'll fold
           to a second line rather than squish anything. */
        '<span class="hoh-yh-chips">' + heartChip + tk + mineChip + giveTicketBtn + '</span>' +
        levelClusterHtml +
      '</span>' +
    '</button>';
  }
  /* R64 / R165: Apply Ticket card-corner button handler.
     ==========================================================
     R165 rewrite: every click on Give Ticket now queues a SERIALIZED
     animation cycle for this character. The user can rapid-fire taps;
     each tap immediately:
       1. Decrements the wallet badge optimistically (responsive).
       2. Increments a per-character pending counter shown as "+N" on
          the button so the user sees their taps were registered.
       3. Joins the per-character queue.
     The queue then plays each ticket sequentially:
       ticket-fly animation (~700ms) →
       apply_pending_ticket RPC + level_up_character_by_ticket RPC →
       level-up celebration (~880ms) →
       short breath (160ms) →
       next ticket in queue.
     The Give Ticket button is visually locked (firing class) for the
     entire duration of the queue so the user understands one cycle
     is in flight. They can keep tapping to enqueue more — those land
     in the badge counter, not as overlapping animations.
     Previously the button unlocked after 350ms — fast clicks landed
     mid-animation and a 2nd level-up RPC fired while the first was
     still painting, producing overlapping bars/pills. The user also
     never saw the level-up animate for tickets 2+ because the bar
     was still in `_yhCelebrating=true` state from ticket 1. */
  var __yhTicketQueueMap = {};   /* heroId → { pending: int, running: bool, btnEl: el } */
  /* Tunables — chosen to fit the worst-case real timing:
       fly anim ~700ms (transform transition + cleanup)
       RPC apply  ~200-400ms
       RPC level  ~200-400ms
       celebration sweep 240 + flashHold 240-360 + clean 280 = 760-880ms
     CYCLE_MS is long enough that a normal cycle finishes before the
     next one starts; the small breath at the end keeps successive
     cycles from feeling like one continuous animation. */
  var __YH_TICKET_CYCLE_MS = 1700;
  var __YH_TICKET_BREATH_MS = 160;
  function __yhSetQueuedBadge(btnEl, count){
    if(!btnEl) return;
    try {
      var badge = btnEl.querySelector('.hoh-yh-card-give-ticket-queued');
      if(count > 0){
        if(!badge){
          badge = document.createElement('span');
          badge.className = 'hoh-yh-card-give-ticket-queued';
          badge.setAttribute('aria-hidden', 'true');
          btnEl.appendChild(badge);
        }
        badge.textContent = '+' + count;
        badge.style.opacity = '1';
      } else if(badge){
        badge.style.opacity = '0';
        setTimeout(function(){ try { if(badge.parentNode) badge.parentNode.removeChild(badge); } catch(_){} }, 220);
      }
    } catch(_){}
  }
  function __yhBumpCardCountForGive(btnEl){
    try {
      var card = btnEl && btnEl.closest && btnEl.closest('.hoh-yh-card');
      if(!card) return;
      /* Tier-upgrade-style sweep on the card frame. */
      card.classList.remove('hoh-yh-card-ticket-upgraded');
      void card.offsetWidth;
      card.classList.add('hoh-yh-card-ticket-upgraded');
      setTimeout(function(){
        try { card.classList.remove('hoh-yh-card-ticket-upgraded'); } catch(_){}
      }, 1500);
      /* Per-card ticket pill bump so the user sees the count tick. */
      var countEl = card.querySelector('.hoh-tk-card .hoh-tk-count');
      if(countEl){
        var current = parseInt(countEl.textContent || '0', 10) || 0;
        countEl.textContent = String(current + 1);
        var pill = card.querySelector('.hoh-tk-card');
        if(pill){
          pill.classList.remove('hoh-tk-card-bumped');
          void pill.offsetWidth;
          pill.classList.add('hoh-tk-card-bumped');
          setTimeout(function(){ try { pill.classList.remove('hoh-tk-card-bumped'); } catch(_){} }, 900);
        }
      }
    } catch(_){}
  }
  function __yhStripGiveTicketButtonsIfBroke(){
    try {
      var remaining = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
      if(remaining > 0) return;
      var lefts = document.querySelectorAll('.hoh-yh-card-give-ticket');
      for(var i = 0; i < lefts.length; i++){
        if(lefts[i].parentNode) lefts[i].parentNode.removeChild(lefts[i]);
      }
    } catch(_){}
  }
  function __yhTicketProcessNext(heroId){
    var q = __yhTicketQueueMap[heroId];
    if(!q) return;
    /* Re-locate the button each iteration — the grid may have
       repainted between taps (e.g., the parent re-rendered after
       the first ticket bumped c.tickets). */
    var btnEl = q.btnEl;
    if(!btnEl || !document.body.contains(btnEl)){
      btnEl = document.querySelector('.hoh-yh-card-give-ticket[data-yh-give="' + String(heroId).replace(/"/g, '\\"') + '"]');
      if(btnEl) q.btnEl = btnEl;
    }
    /* Queue drained — clean up and unlock. */
    if(q.pending <= 0){
      q.running = false;
      __yhSetQueuedBadge(btnEl, 0);
      if(btnEl){
        try {
          btnEl.removeAttribute('data-yh-applying');
          btnEl.classList.remove('hoh-yh-card-give-ticket-firing');
        } catch(_){}
      }
      /* If wallet hit 0 during this run, strip the buttons globally. */
      __yhStripGiveTicketButtonsIfBroke();
      return;
    }
    /* Sanity gate: wallet might have hit 0 mid-queue if other surfaces
       also spent tickets concurrently. Drop the rest of the queue. */
    var walletNow = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    if(walletNow <= 0){
      q.pending = 0;
      __yhSetQueuedBadge(btnEl, 0);
      q.running = false;
      __yhStripGiveTicketButtonsIfBroke();
      return;
    }
    q.pending -= 1;
    __yhSetQueuedBadge(btnEl, q.pending);
    /* Keep the firing class on for the duration of the cycle so the
       button reads as "busy" until the queue empties. */
    if(btnEl){
      try {
        btnEl.setAttribute('data-yh-applying', '1');
        btnEl.classList.add('hoh-yh-card-give-ticket-firing');
      } catch(_){}
    }
    var ok = window.hohApplyTicket(heroId, { targetEl: btnEl || null, confirmed: true });
    if(ok) __yhBumpCardCountForGive(btnEl);
    /* Schedule the next cycle. CYCLE_MS covers fly+RPC+level-up; the
       breath buffer separates successive level-ups so they don't blur
       into one. The setTimeout is the single source of truth for when
       the next cycle starts — robust to RPC slowness or failure. */
    setTimeout(function(){
      __yhTicketProcessNext(heroId);
    }, __YH_TICKET_CYCLE_MS + __YH_TICKET_BREATH_MS);
  }
  window.hohYhApplyTicketFromCard = function(heroId, btnEl, confirmed){
    if(!heroId) return;
    /* R793: retired in the live Hall (see the give button above). */
    if(typeof window.hohR793Live === 'function' && window.hohR793Live()) return;
    if(typeof window.hohApplyTicket !== 'function') return;
    /* Wallet pre-check: never queue more than the user has. */
    var have = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    if(have <= 0){
      try { window.hohShowHallToast && window.hohShowHallToast('No tickets to apply yet. Earn more first.'); } catch(_){}
      return;
    }
    /* R687: confirm at the deliberate queue tap, not later inside the timed
       worker. Each accepted prompt corresponds to exactly one queued ticket. */
    var heroName = 'this hero', heroRow = null;
    try {
      var pool = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      for(var hi=0; hi<pool.length; hi++){
        if(pool[hi] && String(pool[hi].id) === String(heroId)){
          heroRow = pool[hi]; heroName = String(pool[hi].name || heroName); break;
        }
      }
    } catch(_){}
    /* ⛔ R695 · THE DESIGNED PANEL REPLACES THE BROWSER WARNING, AND IT CANNOT
       BLOCK. window.confirm froze the thread and answered inline; hohAsk
       resolves a promise, so this re-enters itself with `confirmed` set once
       the player says yes. The R687 property that matters is preserved
       exactly: one accepted prompt still queues exactly one ticket, because
       the queue push below is only reached on the re-entry.
       hohAskGiveTicket lives in hoh-tickets.js, which is loaded before this
       file, and falls back to confirm() if it is ever missing. */
    if(!confirmed){
      try {
        /* R734 (Jan): own hero, no prompt; someone else's hero, the red warning. */
        var heroMine = (typeof window.hohTicketTargetIsMine === 'function') ? window.hohTicketTargetIsMine(heroId, heroRow) : null;
        window.hohAskGiveTicket(heroName, { mine: heroMine }).then(function(yes){
          if(yes) window.hohYhApplyTicketFromCard(heroId, btnEl, true);
        });
      } catch(_){
        var fb = false;
        try { fb = window.confirm('Give 1 raffle ticket to ' + heroName + '?'); } catch(_e){ fb = false; }
        if(fb) window.hohYhApplyTicketFromCard(heroId, btnEl, true);
      }
      return;
    }
    var q = __yhTicketQueueMap[heroId];
    if(!q){
      q = { pending: 0, running: false, btnEl: btnEl || null };
      __yhTicketQueueMap[heroId] = q;
    } else if(btnEl){
      q.btnEl = btnEl;
    }
    /* Count tickets already in flight or queued across the whole
       wallet — refuse to queue beyond available. The wallet decrements
       happen INSIDE hohApplyTicket as each cycle fires, so by the time
       the queue runs the wallet will match. We cap intake so an
       impatient user can't queue 50 taps with a 3-ticket wallet. */
    var totalInFlight = 0;
    try {
      for(var k in __yhTicketQueueMap){
        if(!Object.prototype.hasOwnProperty.call(__yhTicketQueueMap, k)) continue;
        var qq = __yhTicketQueueMap[k];
        if(qq && qq.running) totalInFlight += 1; /* currently animating */
        if(qq) totalInFlight += (qq.pending | 0);
      }
    } catch(_){}
    if(totalInFlight >= have){
      /* Soft toast — don't spam if they're mashing. */
      try {
        if(!window.__yhTicketQueueToastedAt || (Date.now() - window.__yhTicketQueueToastedAt) > 1800){
          window.__yhTicketQueueToastedAt = Date.now();
          window.hohShowHallToast && window.hohShowHallToast(have === 1 ? 'Only 1 ticket left — already queued.' : 'All ' + have + ' tickets are already queued.');
        }
      } catch(_){}
      return;
    }
    q.pending += 1;
    __yhSetQueuedBadge(btnEl, q.pending);
    if(!q.running){
      q.running = true;
      __yhTicketProcessNext(heroId);
    }
  };

  /* Toggle save state from a star icon on a Profile card. Updates the
     icon visual immediately, persists to the data layer, and fires a
     toast for clarity. */
  window.hohYhCardToggleStar = function(id, el){
    if(!id) return;
    var wasSaved = (typeof window.hohHallIsSaved === 'function') && window.hohHallIsSaved(id);
    if(wasSaved){
      if(typeof window.hohHallRemoveHero === 'function') window.hohHallRemoveHero(id);
      if(el){ el.classList.remove('hoh-yh-card-star-on'); el.setAttribute('aria-label', 'Add to favorites'); var svgOff = el.querySelector('svg'); if(svgOff) svgOff.setAttribute('fill','none'); }
      if(typeof window.hohShowHallToast === 'function') window.hohShowHallToast('Removed from favorites');
    } else {
      if(typeof window.hohHallForceSaveHero === 'function') window.hohHallForceSaveHero(id, 'star');
      if(el){ el.classList.add('hoh-yh-card-star-on'); el.setAttribute('aria-label', 'Remove from favorites'); var svgOn = el.querySelector('svg'); if(svgOn) svgOn.setAttribute('fill','currentColor'); }
      if(typeof window.hohShowHallToast === 'function'){
        window.hohShowHallToast('Saved to Your Favorites', { linkLabel:'View', linkClick: function(){
          try {
            document.body.classList.add('hoh-page-mode-yours');
            if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('cast');
            if(typeof window.hohOpenYourHall === 'function') window.hohOpenYourHall();
          } catch(_){}
        }});
      }
    }
  };

  /* If the user lands on the page already in Your Hall mode (e.g., a
     deep link or after a navigation), render the dashboard once the
     section script has booted. */
  document.addEventListener('hoh:characters-loaded', function(){
    if(document.body.classList.contains('hoh-page-mode-yours')){
      window.hohOpenYourHall();
    }
  });
  /* R64: Wallet changes (earn or spend) re-render the grid so Apply
     Ticket buttons appear/disappear in real time. Without this, a
     user who applied their last ticket would still see ghost buttons
     until they switched tabs and back. */
  document.addEventListener('hoh:unspent-tickets-changed', function(){
    if(typeof window.hohYhRefreshGrid === 'function'){
      try { window.hohYhRefreshGrid(); } catch(_){}
    }
  });
  document.addEventListener('hoh:ticket-applied', function(){
    if(typeof window.hohYhRefreshGrid === 'function'){
      try { window.hohYhRefreshGrid(); } catch(_){}
    }
  });
})();
